/**
 * main.ts — the ICT verification chart.
 *
 * THE ONE RULE THIS FILE MUST OBEY
 * --------------------------------
 * This file RENDERS. It never COMPUTES an ICT rule. No fractal width, no ATR
 * multiple, no fib ratio, no threshold of any kind may appear in this codebase.
 * Every number on screen arrives from the Python `ict/` module via /api.
 *
 * Why so strict: the app exists to answer "is the component implemented
 * correctly?". If the chart computed its own version of a rule, a bug in
 * production would still look perfect on screen, and a bug here would look like
 * a production bug. Either way the answer would be worthless. Geometry in,
 * pixels out.
 *
 * Plan: .hermes/plans/2026-07-26_152800-shared-ict-module-and-visual-verification-app.md
 */
import {
  LineStyle,
  type LogicalRangeChangeEventHandler,
  type MouseEventHandler,
  type Time,
  type UTCTimestamp,
} from 'lightweight-charts';
import {
  commitChartedAsset,
  filterAssets,
  moveHighlight,
  offerableFeeds,
  pickerStatus,
  type AssetMatch,
  type ChartedAssetResult,
} from './assetpicker';
import {
  barAt,
  drawingLabel,
  drawingsSummary,
  fibLabel,
  snapAnchor,
  type Drawing,
  type DrawingKind,
  type DrawingsPayload,
} from './drawings';
import {
  ALL_TIMEFRAMES,
  ChartPane,
  drawContextLevels,
  needsClockOnAxis,
  paneBarLimit,
  paneWindowRange,
  restorePaneOverlayState,
  structurePointDots,
  swingsDraw,
  type PaneOverlays,
  type StructurePoint,
} from './chartpane';
import { apiErrorMessage, paneErrorLine } from './apierror';
import {
  advanceHistory,
  armHistory,
  historyNote,
  initialHistoryState,
  mergeOlder,
  noteRange,
  shiftedRange,
  shouldLoadOlder,
  type HistoryState,
} from './history';
import { readBuildInfo, buildLabel, buildMismatch } from './buildinfo';
import {
  INSPECTOR_COLLAPSED_KEY,
  inspectorCollapsedValue,
  inspectorStartsCollapsed,
} from './inspector';
import { ensureFreshTokens, resetAuthRefresh } from './authrefresh';
import { actionCopy, buttonState, cancelNote, cancelOutcome, dialogNote, isNoConfirm,
         isNoDryRun, isOneClick, type CancelRowState, type DialogPhase } from './oneclick';
import {
  detectRollover,
  initialRolloverState,
  seriesKey,
  type RolloverState,
} from './barclose';
import { LIVE_TICK_MS, panesDue } from './livepoll';
import { OverlayAnswerStore, OverlayRefreshScheduler, type OverlayAnswerKey } from './overlayrefresh';
import {
  breakMarkers,
  breakSegments,
  breaksSummary,
  fvgBands,
  fvgSummary,
  orderBlockBands,
  orderBlockSummary,
  offScreenBands,
  setupDrawing,
  setupSummary,
  idmStatus,
  liquidityDrawing,
  liquiditySummary,
  openLegDrawing,
  priceText,
  rangeDrawing,
  rangesSummary,
  swingMarkers,
  swingZigzag,
  swingsSummary,
  zigzagLiveTip,
  type BreaksPayload,
  type BreakSegment,
  type Zigzag,
  type FvgPayload,
  type OrderBlockPayload,
  type SetupPayload,
  type LevelRay,
  type LiquidityPayload,
  type LiquidityDrawing,
  type RangeDrawing,
  type RangesPayload,
  type SwingsPayload,
  type ZoneBand,
  zoneViewRange,
  experimentDrawing,
  type ExperimentEvent,
  type ExperimentPayload,
} from './overlays';
import {
  RESEARCH,
  adjacent,
  appendPage,
  applyFilter,
  clearCurrent,
  emptyNav,
  filterEvents,
  filterFromControl,
  flagControls,
  formatLink,
  labelRange,
  linkSentence,
  navKey,
  navigatorHtml,
  openIndex,
  parseLink,
  summarize,
  type EventDetail,
  type EventIndex,
  type EventWindow,
  type ExperimentList,
  type JumpTarget,
  type NavState,
} from './navigator';
import { ltfEvidenceDrawing, type LtfEvidencePayload } from './ltfevidence';
import { sessionsDrawing, type SessionsPayload } from './sessions';
import { sessionLevelsDrawing, type SessionLevelsPayload } from './sessionlevels';
import { bpSessionsDrawing, type BpSessionsPayload } from './bpsessions';
import { bpImbalanceDrawing, type BpImbalancePayload } from './bpimbalance';
import { bpIdmDrawing, type BpIdmPayload } from './bpidm';
import { bpObDrawing, type BpObPayload } from './bpob';
import { STATIC_N_CHOICES, STATIC_N_DEFAULT, staticStructureDrawing, type StaticStructPayload } from './staticstructure';
import {
  applyError,
  applyReply,
  buildRequest,
  canSend,
  escapeHtml,
  healthNote,
  overlaysOn,
  renderMarkdown,
  toolSummary,
  type AgentHealth,
  type AgentReply,
  type ChartContext,
  type ChatMsg,
} from './aichat';
import {
  authConfig,
  authHeader,
  clearTokens,
  configured,
  isSignedIn,
  loadTokens,
  refreshTokens,
  beginTotpEnrolment,
  decodeClaims,
  totpEnabled,
  saveTokens,
  signIn,
  type Tokens,
} from './auth';
import { validate, type CredentialState } from './wallet';
import {
  renderAlertsPanel,
  renderSecurityPanel,
  renderWalletPanel,
  validateAlerts,
  validateTotpCode,
  whoLabel,
  type AlertState,
  type ProfileTab,
} from './profile';
import {
  TicketPrimitive,
  type TicketGeometry,
} from './ticketprimitive';
import {
  LiveLevelModel,
  commitSummary,
  dragCallbacks,
  mergeContextLevels,
  placedOrderId,
  renderReadback,
  stagedModifyRequest,
  validateStaged,
  withDragHandles,
  type PendingLeg,
} from './livelevels';
import {
  applyMovedLeg,
  currentSizingError,
  equityFrom,
  freshTicket,
  manageIntent,
  renderTicket,
  sizingFingerprint,
  ticketForSymbol,
  ticketFromSetup,
  ticketGeometry,
  ticketPrefs,
  type ReviewNav,
  type SizingRefusal,
  type TicketPreview,
  type TicketState,
} from './ticket';
import { coarsePointer } from './legdrag';
import { ARM_WINDOW_MS, isArmed, placeStep, ticketKey, type PlaceArm } from './placearm';
import {
  resolveCursor,
  reviewNavAt,
  setupKey,
  stepIndex,
  type SetupCursor,
} from './setupstate';
import {
  renderAdvice,
  ticketFromAdvice,
  type AdvicePayload,
} from './advicetab';
import {
  TABS,
  esc,
  fmtPx,
  levelsFor,
  renderModifyRun,
  renderSetups,
  renderTab,
  renderTradeRun,
  tabCounts,
  worstSeverity,
  type AccountHealth,
  type ModifyRun,
  type PanelLevel,
  type SetupsPayload,
  type Snapshot,
  type TabId,
  type TradeRun,
} from './tradepanel';
import { nextSort, type SortState } from './sort';
import { type AccountLevel } from './levelprimitive';
import { formatHeader, selectHeaderBar } from './ohlcheader';
import { precisionFor } from './pricescale';
import { weeklyLine, weeklyRefused, type WeeklyPayload } from './weeklyline';
import { initMobileTabs, MOBILE_QUERY } from './mobiletabs';
import {
  EXECUTION_PANE_ID,
  PANE_SPEC,
  effectivePaneCount,
  gridClass,
  loadPaneCount,
  panesFor,
  parsePaneCount,
  savePaneCount,
  type PaneSpec,
} from './panecount';

/**
 * `time` is epoch SECONDS on the wire (bars_cache stores it that way; asserted
 * in chartapp/api/tests/test_api.py). lightweight-charts wants its branded
 * `UTCTimestamp` for the same value, so the brand is applied at the API
 * boundary — once, here — rather than cast at each call site.
 */
type Candle = { time: UTCTimestamp; open: number; high: number; low: number; close: number };
type BarsMeta = Record<string, unknown> & {
  has_forming_bar?: boolean;
  closed_bars_count?: number;
  /** First timestamp of the served window — passed to the overlay routes so they match it. */
  window_first_time?: number | null;
  /** Last timestamp of the served window. The research source's overlay routes need both bounds
   *  (U5 experiment route requires `last_time`); the live sources ignore it (t_42cdbde6 U7). */
  window_last_time?: number | null;
  /** Decimals the venue quotes this asset's prices at (`price_precision.py`); the price axis' precision. */
  px_decimals?: number | null;
};
type BarsResponse = { meta: BarsMeta; candles: Candle[] };
type AssetsResponse = { asset: string; timeframes: string[] }[];

/** One source the backend can read bars from. `live` decides whether this app polls. */
type SourceInfo = {
  id: string;
  label: string;
  timeframes: string[];
  live: boolean;
  feed: string;
  caveat: string;
  assets?: number;
  reachable?: boolean;
  error?: string;
};

/**
 * The forming-bar payload from /api/live.
 *
 * `forming` is the still-open candle; `closed_bar` is the newest FINISHED one and
 * is always sent, so a rollover can be applied in a single round-trip (finalise
 * the old candle, start the new one) rather than waiting for a full reload to
 * notice a bar closed.
 */
type LivePayload = {
  asset: string;
  tf: string;
  source: string;
  live: boolean;
  forming: { time: number; open: number; high: number; low: number; close: number; volume: number } | null;
  closed_bar: { time: number; open: number; high: number; low: number; close: number; volume: number } | null;
  mark: number | null;
  server_time?: string;
};

type Health = {
  ok: boolean;
  bars_cache: { path: string; exists: boolean; assets: number };
  ict: { importable: boolean; version?: string; components: Record<string, boolean>; error: string | null };
  /** First 8 chars of the commit the BACKEND is running. Distinct from `ict.version`. */
  build?: string;
  /** `prod` / `dev` / `local`, as the backend sees itself. */
  env?: string;
};

// The overlay catalogue.
//
// THREE FIELDS, and collapsing any two of them has already caused a bug:
//   `module`    the ict/ module the geometry comes from. Gates the toggle on
//               /api/health's importability flags.
//   `component` the /api/overlays/{asset}/{tf}/{component} route segment.
//   `wired`     whether THIS app has that route yet.
//
// `module` and `component` were ONE field until 2026-07-27, which was fine only while
// every overlay's route happened to share its module's name. `breaks` broke that: it is
// served by `ict.mss` (BOS + CHoCH from one walk) under the route name `breaks`, and with
// the fields conflated the health check would have probed a nonexistent `ict.breaks` and
// disabled the checkbox permanently.
//
// `module` and `wired` are also distinct, for the reason recorded when that was conflated:
// ict/structure.py and ict/fvg.py appeared on disk (so /api/health reported them ready)
// while their overlay routes still 404'd, which enabled two toggles that could only
// produce a raw fetch error. A control is offered only when BOTH the module exists AND
// the route exists — flip `wired` in the same commit that adds the route.
//
// THERE WAS A FOURTH FIELD, `on`, AND IT WAS DEAD (deleted 2026-08-24, card t_74464b11).
// It was added on 2026-07-29 for `renderOverlayToggles`, which rendered the SIDEBAR
// checkboxes as `on && ready ? 'checked' : ''`. Those were deleted on 2026-07-31 when
// visibility became per-pane, and `on` was left behind: measured over all of `chartapp/`
// at deletion time, the only reads of a catalogue row were `o.id` (line ~956, building the
// per-pane checkboxes) and `o.module`/`o.wired` (in `wired()`), so no value of `on` could
// change a single pixel. `overlaydefaults.test.ts` was asserting `/on: true/` against this
// very literal, i.e. guarding a field with no consumer — the exact vacuity that would have
// made "invert the eight defaults" a green test over an unchanged app.
//
// THE ONE DEFAULT IS `chartpane.DEFAULT_OVERLAYS`, which `loadPaneState` and the `ChartPane`
// constructor both read. A second copy here is what let one of them be wrong for four weeks.
const OVERLAYS: {
  id: string; label: string; module: string; component: string; wired: boolean;
  /** Drawn on this source only; the pane checkbox is disabled elsewhere (t_42cdbde6 U7). */
  gate?: typeof RESEARCH;
}[] = [
  { id: 'swings', label: 'Swings / pivots', module: 'swings', component: 'swings', wired: true },
  // MARKET STRUCTURE, ON ITS OWN CONTROL (2026-08-10). Andrei, from his live chart: "why i don't
  // see these dots, what should be selected?" -- and the honest answer was "tick swings", which is
  // the bug. The structure dots were set inside the `on('swings')` block, so turning off the raw
  // pivots (~1,300 of them on a 15m pane, the clutter he has asked about three times) also turned
  // off the one overlay he wanted. They share the `swings` ROUTE because the server sends the
  // points on that payload -- one fetch, two gates -- so `component` names that route while the
  // control is separate. Default ON: it is the structure, and it was visible before this split.
  { id: 'market_structure', label: 'Market structure', module: 'swings', component: 'swings', wired: true },
  // ONE checkbox for BOTH kinds of break (2026-07-27). Andrei: "I believe we need to
  // rework our existing checkboxes ... probably, we need only one instead of two."
  //
  // It was two — "Structure + BOS/CHoCH" (ict.structure.detect_bos) and "Market
  // structure shift (MSS)" (ict.mss) — and they were not two features. BOS and CHoCH
  // differ ONLY by whether the break goes with or against the prevailing leg, so a
  // detector that does not track the leg cannot tell them apart: `detect_bos` labels
  // every event 'BOS' by its own docstring. The two also fired on different bars and
  // agreed on 19.2% / 14.5% of their events, so both boxes on meant two contradictory
  // sets of 'BOS' arrows. `ict.mss.detect_breaks` emits both kinds from the walk that
  // already tracked the leg to grade majors.
  { id: 'breaks', label: 'Structure breaks — BOS + CHoCH', module: 'mss', component: 'breaks', wired: true },
  // REMOVED 2026-07-28: `structure` — "Structure read — sidebar only (macro vs swing-seq
  // fork)". Andrei: *"consider removing ... as it does not show anything meaningful. if
  // something is implemented on the backend side to support it, it probably should be
  // remove too."* It was the only box here that drew nothing, and three measurements
  // agreed with him: the row it presented as `macro (prod)` is not reachable from
  // production at all (`indicator_setup/` and `exec_tools/` import `ict.structure` zero
  // times — the then-live classifier was `htf_analyzer.classify_structure`, on fractal pivots,
  // and swapping in alternating swings moved that label on 17.9% of 112 series); its
  // headline disagreement rate needed re-measuring on every cache refresh (43.6% ->
  // 47.0% -> 48.2%); and its `choch` count was structurally always 0. The route refuses
  // by name (`overlays.py::build`), the fork's evidence stays in `ict/structure.py`,
  // `docs/audits/DIRECTION_LAYER_AUDIT_2026-07-26.md` and `chartapp/README.md`.
  { id: 'fvg', label: 'Fair value gaps — zones + CE', module: 'fvg', component: 'fvg', wired: true },
  // Order blocks sit next to FVG because they are the other zone kind, but they are NOT the same
  // sort of object: a gap is pure geometry, a block is defined only relative to a structure break.
  // THE LABEL NAMES WHAT `ict/order_blocks.py` DRAWS (card `t_28a039b9`, bp_method.md §5 row 17): one
  // SWEEP candle that leaves an imbalance (the Dobrunia algorithm). It used to read "last opposing
  // candle before a break", which is the BP channel's REGULAR block («последняя медвежья свеча перед
  // ростом», 023 04:07) -- a different object the module never computes. orderblock.test.ts holds it.
  { id: 'order_blocks', label: 'Order blocks — sweep candle + imbalance (Dobrunia)', module: 'order_blocks', component: 'order_blocks', wired: true },
  { id: 'ranges', label: 'Dealing range + OTE (local + major)', module: 'ranges', component: 'ranges', wired: true },
  // liquidity last: ict/liquidity.py exists but has NO test suite, so its
  // equivalence to the four legacy pool implementations is unproven and it is
  // neither exported from ict/__init__.py nor given a route.
  { id: 'liquidity', label: 'Liquidity pools / sweeps', module: 'liquidity', component: 'liquidity', wired: true },
  // LAST, and it is the only COMPOSITE row here: every other overlay draws one detector's
  // output, this draws the conjunction (break + IDM + sweep + entry zone) that the method
  // actually trades. It therefore belongs at the bottom of the list rather than beside the
  // other zone overlays — the reader ticks the parts to understand it, and this to trade it.
  //
  // AND IT IS THE ONE ON BY DEFAULT since 2026-08-24 (card t_74464b11) — see
  // `chartpane.DEFAULT_OVERLAYS`, the only place that decision lives. Its module IS in every
  // deploy now (`ict.setup` imports; `/api/health` reports it), and a module that were absent
  // would grey the box out via the `wired()` conjunction rather than offer a dead toggle.
  // 'zone to wait at', not 'entry zone': the box is a price band to WAIT for a reaction at, and
  // the entry is its NEAR EDGE (the dashed line inside it), not the box. Same correction as the
  // band caption in `setupBands`.
  { id: 'setup', label: 'Trade setups — break + IDM + sweep + zone to wait at', module: 'setup', component: 'setup', wired: true },
  // AFTER `setup`, and it is the only row here that draws EVIDENCE rather than a finding. Card
  // `t_bf04f7ec`: closed-bar LTF facts for a manual review -- what each detector saw, and the bar
  // a STREAMING observer first had it. The label says NOT A SIGNAL because that is the one thing a
  // reader must not get wrong about this layer: it draws no entry, no stop, no target and no rank,
  // and it removes nothing from any other overlay.
  //
  // OFF BY DEFAULT (`chartpane.DEFAULT_OVERLAYS` is the only place that decision lives). A review
  // layer that arrives unasked competes with the finding layers for attention, and this one draws
  // one marker per fact rather than one box per setup.
  { id: 'ltf_evidence', label: 'LTF evidence — closed-bar ICT facts (NOT a signal)', module: 'ltf_evidence', component: 'ltf_evidence', wired: true },
  // Card `t_5b2017b5`: the full-course video's session boxes and Asia model (first Asia boundary
  // taken = M, opposite boundary = target, its touch = D), `ict.session_model`. 1m-1h only: the
  // route answers `unsupported_tf` on coarser bars. OFF BY DEFAULT, like every review layer.
  { id: 'sessions', label: 'Sessions — Asia/Frankfurt/London/NY + Asia model (M → target → D)', module: 'session_model', component: 'sessions', wired: true },
  // Card `t_2d80caad`: the same video's clock levels -- RTH 09:30-16:15, the Asia-open -> RTH-open
  // range, crypto 07-11 / 19-23 ET, PDH/PDL, day/week/RTH open gaps (`ict.session_levels`). The
  // RTH family needs <= 15m bars; the route names what a TF cannot resolve. OFF BY DEFAULT.
  { id: 'session_levels', label: 'Session levels — RTH, Asia→RTH range, crypto windows, PDH/PDL, gaps', module: 'session_levels', component: 'session_levels', wired: true },
  // Card `t_fb5e844d`: the full-course STATIC structure, `ict.static_structure` -- valid fractals,
  // HH / HL / LL / LH (seeds marked), body-close breaks, order-flow zones. Needs the `fractal n`
  // knob below (UNSPECIFIED BY AUTHOR, no default). OFF BY DEFAULT, like every review layer.
  { id: 'static_structure', label: 'Static structure — full-course fractals, HH/HL, break, order flow', module: 'static_structure', component: 'static_structure', wired: true },
  // Card `t_5ee634f0`: the BP channel's Asia-sweep objects, `ict.bp_session` -- pre-Frankfurt
  // 01-02 / pre-NY 07-08 ET boxes, the sweep 02-10 ET with its session tag, the Asia 50% line,
  // worked/partial/failed by 11:00, V3a and V2 teaching stops, the GBP 02:33 macro. 1m-1h only.
  // OFF BY DEFAULT, like every review layer.
  { id: 'bp_sessions', label: 'BP sessions — pre-FR/pre-NY, Asia sweep 02-10, 50%, W/P/F, V2/V3a stops', module: 'bp_session', component: 'bp_sessions', wired: true },
  // Card `t_28a039b9`: the BP channel's imbalance, `ict.bp_imbalance` -- consecutive gaps with
  // untouched wicks merged into one (022 05:49), and the part still unfilled after each bar
  // (022 06:08). The shared `fvg` layer is unchanged. OFF BY DEFAULT, like every review layer.
  { id: 'bp_imbalance', label: 'BP imbalance — merged runs + unfilled part (022)', module: 'bp_imbalance', component: 'bp_imbalance', wired: true },
  // Card `t_6f7db3f5`: the BP channel's 021 IDM, `ict.bp_idm` -- formed by a BODY close beyond
  // the leg extreme, level = the pullback extreme, broken by a BODY close (021 10:00-10:40). The
  // `setup` layer's IDM is a different rule and is unchanged. OFF BY DEFAULT, like every review layer.
  { id: 'bp_idm', label: 'BP IDM — body-close formation + body break (021)', module: 'bp_idm', component: 'bp_idm', wired: true },
  // Card `t_d894c573`: the BP channel's 023 order block, `ict.bp_ob` -- the candle before a BP
  // imbalance (regular / engulfed by colour, 023 05:55) and the breaker an imbalance pierced it into
  // (023 06:59). The `order_blocks` layer is a different rule and is unchanged. OFF BY DEFAULT.
  { id: 'bp_ob', label: 'BP OB — regular / engulfed / breaker (023)', module: 'bp_ob', component: 'bp_ob', wired: true },
  // Card `t_42cdbde6` U7: a research experiment's events (markers, entry/stop/TP boxes, and the
  // selected event's levels and windows), served by U5's `experiment` route. RESEARCH ONLY: the
  // route takes `source=research` and nothing else, so the checkbox is disabled on every other
  // source rather than drawing a 422. `module: 'bars'` because the route reads no `ict` component;
  // `bars` is in every health report, so `wired()` does not hide it. OFF BY DEFAULT.
  { id: 'experiment', label: 'Experiment events (research)', module: 'bars', component: 'experiment', wired: true, gate: RESEARCH },
];

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

const el = {
  chart: $('chart'),
  source: $<HTMLSelectElement>('source'),
  asset: $<HTMLSelectElement>('asset'),
  reload: $<HTMLButtonElement>('reload'),
  newtrade: $<HTMLButtonElement>('newtrade'),

  livetoggle: $<HTMLInputElement>('livetoggle'),
  status: $('status'),
  overlays: $('overlays'),
  meta: $('meta'),
  ictstatus: $('ictstatus'),
  buildinfo: $('buildinfo'),
  inspToggle: $<HTMLButtonElement>('insp-toggle'),
  livebox: $('livebox'),
  /** The order ticket's ONE mount, in the right column (2026-08-18, card t_ab054079). */
  tkMount: $('tk-mount'),
  // Trade panel.
  panel: $('tradepanel'),
  tpGrip: $('tp-grip'),
  tpTabs: $('tp-tabs'),
  tpSub: $('tp-sub'),
  tpBody: $('tp-body'),
  tpDialog: $<HTMLDialogElement>('tp-dialog'),
  tpDlgTitle: $('tp-dlg-title'),
  tpDlgInputs: $('tp-dlg-inputs'),
  tpDlgResult: $('tp-dlg-result'),
  tpDlgNote: $('tp-dlg-note'),
  assetInput: $<HTMLInputElement>('asset-input'),
  assetList: $('asset-list'),
  drawTools: $('drawtools'),
  drawMagnet: $<HTMLButtonElement>('draw-magnet'),
  drawDel: $<HTMLButtonElement>('draw-del'),
  tpApply: $<HTMLButtonElement>('tp-apply'),
  tpDryrun: $<HTMLButtonElement>('tp-dryrun'),
  tpConfirm: $<HTMLButtonElement>('tp-confirm'),
  tpCancelDlg: $<HTMLButtonElement>('tp-cancel-dlg'),
};

// NO `chart` / `candleSeries` MODULE ALIASES. There were two, assigned once in `initChart` from
// `activePane()`, and they were the fourth instance of one bug class in this file: code that reads
// as app-wide while acting on ONE pane. `drawWirePane(activePane())` (9d79980),
// `renderPaneZoneView` into the shared summary (d47a6e3), `pane.tf = sel.value` (96cc699), and then
// `resetChartLayout()` re-arming the price scale of the execution pane alone -- so switching BTC
// (64000) to SOL (73) left the 1W and 1D panes on BTC's price window with SOL's bars off screen.
//
// DELETED RATHER THAN LOOPED OVER, because a loop fixes one call site and leaves the next author the
// same trap. Naming a pane is now mandatory at all 13 former alias sites: the singular surfaces
// (ticket legs, the `__ictTicket` hooks) say `activePane()` and mean it, and anything
// that must act on every pane cannot be written by accident. Candles are still written only through
// `ChartPane.setBars()` / `.updateBar()` -- see `PaneSeriesReadonly`.
let assets: AssetsResponse = [];
let health: Health | null = null;
/** The experiment navigator (t_42cdbde6 U7). Every decision about it is in `navigator.ts`. */
let nav: NavState = emptyNav();
/** The execution pane's fully drawn event, for the navigator's detail block. */
let navDrawn: ExperimentEvent | null = null;
/** Bumped by every navigator jump: a load started for an earlier jump drops its answer. */
let jumpToken = 0;

// ─── AUTH (phase 1.3) ────────────────────────────────────────────────────────
// `acTokens` is read by getJSON/postJSON, so it is declared alongside the other
// module state rather than inside the auth section below — a `let` used before its
// declaration line is a TDZ error at runtime, not a compile error.
//
// NULL IS THE NORMAL CASE TODAY. `authHeader(null)` returns {}, so an unconfigured
// build (no VITE_COGNITO_* at build time) behaves exactly as the app does now. That
// is what keeps phase 1 additive: CloudFront + Cognito exist, the EC2 app still
// works, and nothing is gated until the API enforces the token in phase 3.
let acTokens: Tokens | null = null;

/**
 * The Authorization header for the NEXT call, refreshing the session first if it has expired.
 *
 * THE FIX for Andrei's wall of 401s (2026-08-03). `authHeader(acTokens)` was called directly at each
 * fetch site; it correctly refuses to send an expired token and returns `{}`, so one hour after page
 * load -- `idTokenValidity` is 1 hour -- every call went out unauthenticated and the API answered
 * 401 on every poll tick, forever. `refreshTokens` existed the whole time and was only ever called
 * once, from `acRestoreSession()` at boot.
 *
 * ONE FUNNEL, not a guard per call site: three helpers (`getJSON`, `postJSON`, `sendJSON`) send
 * authenticated requests, and a fourth added later would otherwise have to remember. The stampede
 * guard and the dead-latch live in `authrefresh.ts` where vitest can witness them.
 */
async function acAuthHeader(): Promise<Record<string, string>> {
  const before = acTokens;
  const fresh = await ensureFreshTokens(acTokens, {
    refresh: (t) => refreshTokens(t, authConfig()),
    onDead: () => {
      // ONCE, because the alternative is what the app did: 200 identical console errors and no
      // instruction. `clearTokens` stops a dead credential being presented on every later call.
      clearTokens();
      acTokens = null;
      setStatus('session expired - please sign in again', true);
      console.info('[auth] refresh token rejected - sign-in required');
    },
  });
  if (fresh !== before) {
    acTokens = fresh;
    if (fresh) {
      saveTokens(fresh);
      console.info('[auth] session refreshed before a request');
    }
  }
  return authHeader(acTokens);
}

/**
 * Restore this tab's session, if any, and report the state.
 *
 * Called first in `boot()` so the very first data call carries a token. Deliberately
 * NOT a login gate: the API does not enforce the token until phase 3, and blocking the
 * chart on a form the backend cannot yet honour would break a working tool.
 */
async function acRestoreSession(): Promise<void> {
  acTokens = loadTokens();
  const cfg = authConfig();

  // REFRESH BEFORE GIVING UP (2026-07-31). `loadTokens` returns null when the id token has expired
  // AND there is no refresh token; when a refresh token IS present it returns the (stale) blob. So
  // an expired-but-refreshable session reaches here, and previously nothing acted on it — the user
  // was sent to the login form with a valid 30-day credential sitting in storage. That is the
  // "session time is very short" Andrei reported.
  if (configured(cfg) && acTokens && !isSignedIn(acTokens)) {
    // THROUGH THE SAME FUNNEL as every request, not a second refresh path. This branch used to call
    // `refreshTokens` directly, which meant it neither shared the in-flight promise nor set the
    // dead-latch -- measured: a dead refresh token produced 6 REFRESH_TOKEN_AUTH calls in 12s,
    // because boot and the poll ticks were each refreshing independently. `acAuthHeader` owns the
    // decision now; the header it returns is discarded here because only the side effect matters.
    await acAuthHeader();
  }
  // Logged, not silent. Three distinct states an operator needs to tell apart:
  //  * this build has no pool compiled in     -> auth is not wired for this deploy
  //  * pool present, no session               -> sign-in needed (phase 3 will demand it)
  //  * signed in                              -> a token is attached to every call
  // Test hook, same rationale as `__ictDraw` / `__ictInspect`: a harness must be able to tell an
  // auth-wired build from an unwired one, because refresh CANNOT be exercised without a pool and a
  // spec that runs anyway fails on correct code (measured -- preflight's `plain build`).
  (window as unknown as { __ictAuth?: unknown }).__ictAuth = { configured: () => configured(cfg) };

  if (!configured(cfg)) {
    console.info('[auth] no Cognito pool in this build — API calls are unauthenticated');
  } else if (!isSignedIn(acTokens)) {
    console.info(`[auth] pool ${cfg.userPoolId} — not signed in`);
  } else {
    console.info(`[auth] signed in; a bearer token is attached to every API call`);
  }
}

/** Drop the session. Exposed on `window` so it is reachable before a UI exists. */
function acSignOut(): void {
  clearTokens();
  acTokens = null;
  console.info('[auth] signed out');
}
// Reachable from devtools in phase 1; phase 3 adds a real control. Declared rather than
// left as dead code so `clearTokens` has a caller and the sign-out path is exercised.
(window as unknown as { chartappSignOut: () => void }).chartappSignOut = acSignOut;

/**
 * Show the login gate and resolve once a session exists.
 *
 * GATES THE APP ONLY WHEN IT CAN. If no pool is compiled into the build, this resolves
 * immediately and the app behaves exactly as it did before phase 3 — which is what let
 * phases 1 and 2 ship additively. Once a pool IS present, every API call needs a bearer
 * token, so rendering the chart first would just show an empty page and a 401.
 */
function acRequireSession(): Promise<void> {
  const cfg = authConfig();
  if (!configured(cfg) || isSignedIn(acTokens)) return Promise.resolve();

  const el2 = {
    gate: document.getElementById('login') as HTMLElement,
    form: document.getElementById('login-card') as HTMLFormElement,
    user: document.getElementById('login-user') as HTMLInputElement,
    pass: document.getElementById('login-pass') as HTMLInputElement,
    newWrap: document.getElementById('login-new-wrap') as HTMLElement,
    newPass: document.getElementById('login-new') as HTMLInputElement,
    submit: document.getElementById('login-submit') as HTMLButtonElement,
    msg: document.getElementById('login-msg') as HTMLElement,
    sub: document.getElementById('login-sub') as HTMLElement,
  };
  if (!el2.gate || !el2.form) return Promise.resolve();

  el2.gate.hidden = false;
  el2.sub.textContent = `Sign in — ${cfg.userPoolId}`;
  el2.user.focus();

  return new Promise<void>((resolve) => {
    // Set when Cognito reports FORCE_CHANGE_PASSWORD, so the second submit completes the
    // challenge instead of starting a fresh sign-in.
    let pendingNewPassword: ((p: string) => Promise<Tokens>) | null = null;
    /** Set when Cognito issued a TOTP challenge; the second field then carries a 6-digit code. */
    let pendingTotp: ((code: string) => Promise<Tokens>) | null = null;

    const say = (text: string, cls: '' | 'err' | 'ok' = '') => {
      el2.msg.textContent = text;
      el2.msg.className = cls;
    };

    const finish = (tokens: Tokens) => {
      acTokens = tokens;
      saveTokens(tokens);
      // RE-ARM THE REFRESH. `ensureFreshTokens` latches after a rejected refresh token so a dead
      // 30-day credential cannot produce one Cognito call per request forever. Without this reset a
      // user who signs in again would keep that latch for the tab's life and their brand-new session
      // would refuse to refresh an hour later.
      resetAuthRefresh();
      say('signed in', 'ok');
      el2.gate.hidden = true;
      // Clear the fields rather than leaving a password in a DOM node for the tab's life.
      el2.pass.value = '';
      el2.newPass.value = '';
      resolve();
    };

    el2.form.addEventListener('submit', (ev) => {
      ev.preventDefault();
      el2.submit.disabled = true;

      // Second leg (a): verifying a TOTP code. Checked BEFORE the password branch — both use the
      // same field, and a user in the TOTP state has already passed the password.
      if (pendingTotp) {
        say('verifying…');
        pendingTotp(el2.newPass.value)
          .then(finish)
          .catch((err) => {
            say(String(err?.message ?? err), 'err');
            el2.submit.disabled = false;
          });
        return;
      }

      // Second leg (b): completing a forced password change.
      if (pendingNewPassword) {
        say('setting the new password…');
        pendingNewPassword(el2.newPass.value)
          .then(finish)
          .catch((err) => {
            say(String(err?.message ?? err), 'err');
            el2.submit.disabled = false;
          });
        return;
      }

      say('signing in…');
      void signIn(cfg, el2.user.value.trim(), el2.pass.value).then((res) => {
        if (res.kind === 'ok') {
          finish(res.tokens);
          return;
        }
        if (res.kind === 'newPasswordRequired') {
          // An admin-created account's first sign-in. Surfaced rather than swallowed: a
          // form that silently fails on a brand-new account is the worst first impression.
          pendingNewPassword = res.complete;
          el2.newWrap.hidden = false;
          el2.newPass.focus();
          say('this account needs a new password before first use', 'ok');
          el2.submit.textContent = 'Set password and sign in';
          el2.submit.disabled = false;
          return;
        }
        if (res.kind === 'totpRequired') {
          // The user has an enrolled authenticator. Reuse the SAME second-field mechanism as the
          // new-password path rather than adding a third input: one field that changes meaning is
          // easier to get right than two that are conditionally visible.
          pendingTotp = res.complete;
          el2.newWrap.hidden = false;
          el2.newPass.type = 'text';
          el2.newPass.value = '';
          el2.newPass.placeholder = '6-digit code';
          el2.newPass.autocomplete = 'one-time-code';
          el2.newPass.focus();
          say('enter the code from your authenticator app', 'ok');
          el2.submit.textContent = 'Verify and sign in';
          el2.submit.disabled = false;
          return;
        }
        if (res.kind === 'mfaSetupRequired') {
          // The pool REQUIRES MFA and this account has nothing enrolled. Stated plainly because the
          // user cannot resolve it themselves from here — the profile screen is behind sign-in.
          say(res.message, 'err');
          el2.submit.disabled = false;
          return;
        }
        say(res.message, 'err');
        el2.submit.disabled = false;
      });
    });
  });
}
let sources: SourceInfo[] = [];
/** Bar times in load order, so a clicked timestamp maps back to its bar index. */
let candleTimes: UTCTimestamp[] = [];
/** The loaded bars' OHLC, for the drawing MAGNET only.
 *
 *  Kept alongside `candleTimes` rather than read back out of the series, because
 *  `dataByIndex` returns the chart's own view of a bar and the magnet must snap to the
 *  data the API served. Replaced wholesale in `loadBars`, so it cannot outlive its series
 *  — snapping an anchor to a previous instrument's high is exactly the stale-geometry
 *  class this app deletes. */
let candleBars: { time: number; open: number; high: number; low: number; close: number }[] = [];
/**
 * How many of `candleTimes` are CLOSED bars.
 *
 * The chart DRAWS the forming candle but no detector may READ it, so anything quoting a bar NUMBER
 * must quote a closed one. Its consumers are the overlay-provenance strings — "computed at bar N"
 * in the live box, "recomputing at bar N" on rollover — which is how a stale overlay is told apart
 * from a chart that never updated.
 */
let closedBarCount = 0;

/** Live-poll handle. Cleared on every source/asset/tf change so a stale timer
 *  can never keep pushing one series' bars into another series' chart. */
let liveTimer: number | null = null;
/**
 * When each pane was last polled, so `panesDue` can decide per pane.
 *
 * REPLACES a flat `LIVE_POLL_MS = 5000` plus a round-robin cursor. The flat interval spent the same
 * request budget on a 1W pane as on the 15m one; see `livepoll.ts` for the numbers and the rule. The
 * TICK is still 5 s (`LIVE_TICK_MS`) — the 15m pane's cadence is unchanged, which is the point.
 */
const livePolledAt = new Map<string, number>();

/** Rollover bookkeeping for the overlay recompute — see `barclose.ts`. */
let rollover: RolloverState = initialRolloverState();
/**
 * WHEN the drawn overlay geometry was computed, so the answer on screen can be
 * dated. Andrei's rule: make it VISIBLE. The bug being fixed was invisible —
 * candles advanced while the geometry did not, and nothing said so — and a
 * self-healing overlay with no trace is only half a fix, because you still can't
 * tell a recompute from a chart that never updated.
 */
let overlayComputedAt: { bar: number; at: number; pending: boolean } | null = null;

function currentSource(): SourceInfo | undefined {
  return sources.find((s) => s.id === el.source.value);
}

function setStatus(msg: string, isError = false) {
  // ERRORS ONLY (2026-07-31). Andrei: "we don't need this text" — the bar read
  // "xyz:CRWV 15m — 4969 bars (last bar forming)", which every pane header already shows.
  //
  // The element STAYS because 10 of the 15 callers report real failures ("API unreachable",
  // "drawings: …", "sources unavailable"). Filtering here rather than deleting 5 call sites keeps
  // one place that decides what is worth interrupting the user for.
  el.status.textContent = isError ? msg : '';
  el.status.className = isError ? 'err' : '';
}

/**
 * THE ONE PLACE a request leaves this app.
 *
 * Adopted from the subagent's version of this fix, which was a better shape than mine: I had
 * `acAuthHeader()` awaited at five separate `fetch(` sites, and five is five chances for the sixth to
 * forget. Two of the original five already HAD forgotten -- `drawSave` and `drawDelete` sent no auth
 * header at all, and the HTTP API sets `defaultAuthorizer`, so those writes failed whenever the
 * session needed a refresh.
 *
 * With this, main.ts contains exactly one `fetch(` and reads `authHeader` in exactly one place, so
 * "did you remember the token" stops being a question anyone can get wrong.
 */
async function acFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const headers = await acAuthHeader();
  return fetch(url, { ...init, headers: { ...(init.headers ?? {}), ...headers } });
}

async function getJSON<T>(url: string): Promise<T> {
  // The Cognito header is attached HERE, centrally, for the same reason `?source=` is
  // (below): a per-call header is one someone forgets, and a forgotten one produces a
  // 401 that reads as a broken feature rather than a missing token. `authHeader`
  // returns {} when there is no usable session, so an unauthenticated build behaves
  // exactly as it does today — which is what keeps phase 1 additive.
  //
  // ONE RETRY ON A TRANSPORT FAILURE, and only on a transport failure. Andrei: "When opening
  // the app first time after the deployment, I always see the API unreachable: TypeError:
  // Failed to fetch error message. Hard browser page refresh helps."
  //
  // `TypeError: Failed to fetch` is not an HTTP status — it is `fetch` rejecting before any
  // response exists: DNS, TLS, a connection reset, or a Lambda cold start behind the HTTP API
  // that drops the first connection while the container boots. That is exactly the first request
  // after a deploy, which is why a manual reload "fixes" it: the second attempt hits a warm
  // container. The app then made it permanent by giving up on the first rejection
  // (`if (healthRes.status === 'rejected') { ...; return; }`) so the whole UI stayed dead until
  // he reloaded by hand.
  //
  // Retried at THIS seam and nowhere else, because every data call already funnels through here
  // — a retry in `boot` would fix the health check and leave all 15 other callers still failing
  // on a cold edge. An HTTP error is NOT retried: a 401/404/500 is an answer, and repeating it
  // would double every real error and hide a genuine fault behind a delay.
  try {
    return await getJSONOnce<T>(url);
  } catch (err) {
    if (!isTransportFailure(err)) throw err;
    await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
    return getJSONOnce<T>(url);
  }
}

/** Milliseconds before the single transport retry. One cold-start handshake, not a backoff ladder. */
const RETRY_DELAY_MS = 700;

/**
 * Did `fetch` fail before producing a response?
 *
 * `fetch` rejects with a `TypeError` for network-layer faults and resolves (with `r.ok === false`)
 * for every HTTP status, so the type IS the distinction — there is no status code to test. Kept as
 * a named predicate rather than inlined so the retry cannot silently widen to HTTP errors later.
 */
function isTransportFailure(err: unknown): boolean {
  return err instanceof TypeError;
}

async function getJSONOnce<T>(url: string): Promise<T> {
  const r = await acFetch(url);
  if (!r.ok) {
    const body = await r.json().catch(() => ({ detail: r.statusText }));
    throw new Error(apiErrorMessage(r.status, body, r.statusText));
  }
  return r.json() as Promise<T>;
}

/** `?source=` for every data call. Appended centrally so no fetch can silently
 *  read the OTHER feed — the two diverge by up to 1.38% on BTC 1D. */
function q(params: Record<string, string | number | boolean> = {}): string {
  const sp = new URLSearchParams({ source: el.source.value });
  for (const [k, v] of Object.entries(params)) sp.set(k, String(v));
  return sp.toString();
}


// ─────────────────────────────────────────────────────────── the pane registry
//
// ONE TO FOUR PANES, ONE ASSET. Andrei's layout was always 1W / 1D / 15m; since 2026-10-19
// (intent 261019-pane-layout) a header picker shows 1, 2, 3 or 4 of the four specs in
// `panecount.ts` (1W, 1D, 4h, 15m), default 3 = the old layout. Each pane has its own timeframe
// and its own overlay toggles, so each pane is an independent ICT read. The ASSET is global
// because that is what multi-timeframe analysis means — several views of one symbol.
//
// THE REGISTRY IS LIVE, NOT BUILT ONCE. `applyPaneCount` builds and tears panes down while the app
// runs, so `panes` holds exactly the BUILT panes and every loop over panes (live poll, context
// loads, levels, drawings) iterates it, never `PANE_SPEC`. `pane-ltf` is in every row of the table
// and is never torn down.
//
// NO "ACTIVE PANE" FOR THE MONEY PATH. `ticket.ts` contains zero references to timeframe: prices
// are prices, and an entry at 73.163 is the same order whether it was found on 1W or 15m. The
// ticket, positions and account panel therefore bind to the SYMBOL. `activePane()` exists only so
// that chart-GEOMETRY consumers (the ticket's draggable legs) have one well-defined
// surface to draw on, rather than drawing the same bracket on every pane.
const panes = new Map<string, ChartPane>();

/**
 * Is this pane object still the one the registry holds for its id?
 *
 * THE GUARD AFTER EVERY AWAIT ON A PANE'S DATA (2026-10-19, 261019-pane-layout FR-2.6 step 4). A
 * live decrease tears a pane down while its `/api/bars`, history page or `/api/live` answer may be
 * in flight; drawing it would write into a removed chart, and re-setting `paneHistory` /
 * `hoveredTime` would recreate the state teardown just deleted. An id test is not enough: a pane
 * rebuilt by a later increase reuses the id, and its epoch restarts from the stored value. Object
 * identity is the one thing a rebuild cannot fake.
 */
function paneAlive(pane: ChartPane): boolean {
  return panes.get(pane.id) === pane;
}

/** The execution pane's timeframe. Replaces the hidden `el.tf` mirror — see `initPanes`. */
function execTf(): string {
  return panes.get(EXECUTION_PANE_ID)?.tf ?? '';
}

function activePane(): ChartPane {
  const p = panes.get(EXECUTION_PANE_ID);
  if (!p) throw new Error('pane registry not initialised — initPanes() must run before this');
  return p;
}


/** localStorage key for the layout. Per-pane timeframe + overlay toggles.
 *
 *  BUMPED v1 -> v2 ON 2026-08-24 (card t_74464b11), and the bump IS the fix rather than
 *  bookkeeping. `savePaneState` writes on every checkbox click, so anyone who has opened this app
 *  has `panes.v1` holding the OLD overlay set — and `loadPaneState` restores it key-by-key, so
 *  changing `DEFAULT_OVERLAYS` alone would have changed nothing at all for the one person who
 *  asked for the change. That is the shape of a fix that ships and then "does not work".
 *
 *  A KEY BUMP RATHER THAN A `localStorage.removeItem`: the migration must be idempotent and must
 *  survive being run in a tab that still has the old code in another tab. Reading a different key
 *  is both by construction; a delete races.
 */
const PANE_STATE_KEY = 'panes.v2';

/** The key `PANE_STATE_KEY` replaced. Read ONCE, for the timeframes only — see `migrateV1`. */
const PANE_STATE_KEY_V1 = 'panes.v1';

type PaneState = { tf: string; overlays: PaneOverlays };

/**
 * Restore the saved layout, falling back to PANE_SPEC.
 *
 * THIS FUNCTION IS I/O ONLY. Every decision — which blob wins, that a v1 tf survives and a v1
 * overlay set does not, that an unrecognised tf falls back to the spec default because it becomes
 * a URL path segment (`/api/bars/{asset}/{tf}`) — is `chartpane.restorePaneOverlayState`, where
 * vitest can drive it without a DOM. See its docstring; duplicating any of it here would be the
 * second source of truth this card was filed to remove, not a convenience.
 */
function loadPaneState(): Record<string, PaneState> {
  const out: Record<string, PaneState> = {};
  const rawV2 = readBlob(PANE_STATE_KEY);
  const v2 = parseBlob(rawV2);
  // v1 IS ONLY READ WHEN v2 IS ABSENT, so a stale v1 blob can never resurrect the old overlay set
  // after the user has touched a checkbox. `null` rather than an empty object, so the decision
  // function is told "no blob" instead of "an empty one".
  const v1 = rawV2 === null ? parseBlob(readBlob(PANE_STATE_KEY_V1)) : null;
  for (const spec of PANE_SPEC) {
    const r = restorePaneOverlayState(spec.tf, v2?.[spec.id], v1?.[spec.id], rawV2 !== null);
    out[spec.id] = { tf: r.tf, overlays: r.overlays };
  }
  return out;
}

/** One localStorage read that cannot throw. Blocked storage (private mode) is "no blob". */
function readBlob(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** One JSON parse that cannot throw: corrupt state is a reset, not a crash — the defaults are
 *  always valid. `null` for anything that is not an object, so an array or a bare string cannot
 *  reach the decision function as a pane record. */
function parseBlob(raw: string | null): Record<string, Partial<PaneState>> | null {
  if (raw === null) return null;
  try {
    const v = JSON.parse(raw);
    return v && typeof v === 'object' && !Array.isArray(v)
      ? (v as Record<string, Partial<PaneState>>)
      : null;
  } catch {
    return null;
  }
}

/**
 * Save the BUILT panes' TF and toggles, keeping the saved entries of panes that are not built.
 *
 * MERGED, NOT REPLACED (2026-10-19, 261019-pane-layout FR-2.4). This wrote only `panes`, which was
 * every spec while the grid was fixed at three. With a picker, a pane beyond N is not in `panes`,
 * so a checkbox click at N=1 would have erased the 1W, 1D and 4h panes' saved TF and overlays and
 * they would come back as defaults at N=4. The base is `loadPaneState()`, i.e. every spec as the
 * app would restore it (v2, else the v1 migration, else the default), so an unbuilt pane keeps
 * exactly what it would have shown.
 */
function savePaneState(): void {
  const out: Record<string, PaneState> = loadPaneState();
  for (const [id, p] of panes) out[id] = { tf: p.tf, overlays: { ...p.overlays } };
  try {
    localStorage.setItem(PANE_STATE_KEY, JSON.stringify(out));
  } catch {
    // Storage full or blocked (private mode). The layout still works for this session.
  }
}

/** Which bar time the cursor is over, per pane; absent/null means not hovered. */
const hoveredTime = new Map<string, number | null>();

/** Per pane: the current week's XI/XII verdict, or absent when there is nothing to say.
 *
 *  NO EXPAND STATE AND NO BOX STATE any more (card t_70188040): the week box, the labelled level
 *  rays and the surviving/eliminated expand are all deleted, because ten of the twelve profiles
 *  they visualised measured worse than the calendar and Andrei asked for the clutter to go. What
 *  is left is one line of text, so this map is the whole feature's state. */
const weeklyState = new Map<string, WeeklyPayload>();

/**
 * Paint ONE pane's `O H L C  +495.00 (+0.79%)` row.
 *
 * The header is a pure function of `(pane.bars, hoveredTime, pane.lastBarForming)`, so every redraw
 * trigger is just another call to this: the crosshair handler, the end of a load (`setBars`) and
 * each live poll (`updateBar`). The decisions live in `ohlcheader.ts` where vitest can witness
 * them; this function only writes DOM.
 */
function renderHeader(pane: ChartPane): void {
  const slot = document.querySelector<HTMLElement>(`[data-ohlc="${pane.id}"]`);
  if (!slot) return;
  const f = formatHeader(selectHeaderBar(pane.bars, hoveredTime.get(pane.id) ?? null, pane.lastBarForming));
  // A GLYPH PLUS A WORD, not a colour or a dimmed field: `.pane-bar` is already the dimmest text in
  // the app (10px #868c9e) and this app has MEASURED that a low-contrast signal is simply not seen
  // (`overlays.ts:886-900` — two vision passes reported no boxes while 38 were drawn).
  const live = f.forming ? '<span class="pane-ohlc-live">\u25cf live</span> ' : '';
  const cls = f.sign === 'up' ? ' pane-ohlc-up' : f.sign === 'down' ? ' pane-ohlc-down' : '';
  slot.innerHTML = `<span class="pane-ohlc-values">${live}O ${f.o}  H ${f.h}  L ${f.l}  C ${f.c}`
    + `  <span class="pane-ohlc-chg${cls}">${f.change}</span></span>`;
}

/**
 * What `teardownPane` must undo for each built pane: its grid cell, and one release call per chart
 * subscription. lightweight-charts unsubscribes by HANDLER REFERENCE, so an inline arrow passed to
 * `subscribe…` could never be released; each handler is kept here instead (2026-10-19,
 * 261019-pane-layout FR-2.6 step 3).
 */
const paneWires = new Map<string, { cell: HTMLElement; release: Array<() => void> }>();

/**
 * Build ONE pane and its per-pane controls, and put its cell at its place in the grid.
 *
 * ONE CONSTRUCTION PATH (2026-10-19, 261019-pane-layout FR-2.3). This was the body of the loop in
 * `initPanes`; boot and a live increase (`applyPaneCount`) both call it now, so the 4th pane and a
 * pane rebuilt after a decrease get exactly the controls, subscriptions and listeners the boot panes
 * get. Cells are kept in `PANE_SPEC` (top-down) order: a rebuilt pane is inserted before the next
 * built one, so the DOM order, and the grid CSS keyed on it, never depend on build order.
 */
function buildPane(spec: PaneSpec, state: Record<string, PaneState>): ChartPane {
  const grid = el.chart;
  {
    const cell = document.createElement('div');
    cell.className = 'pane';
    cell.dataset.pane = spec.id;
    if (spec.id === EXECUTION_PANE_ID) cell.classList.add('pane-exec');

    const bar = document.createElement('div');
    bar.className = 'pane-bar';
    const sel = document.createElement('select');
    sel.className = 'pane-tf';
    sel.title = 'Timeframe for this pane';
    for (const tf of ALL_TIMEFRAMES) {
      const o = document.createElement('option');
      o.value = tf;
      o.textContent = tf;
      sel.appendChild(o);
    }
    sel.value = state[spec.id].tf;
    bar.appendChild(sel);

    // THE TIME-ZONE MARK, once per pane, right after the timeframe select it belongs to
    // (2026-08-25, card t_69d27c87). The axis is now spelled in America/New_York below 1D and in
    // the exchange's UTC calendar date at 1D and above; an axis that does not SAY which is the
    // same silent divergence the unlabelled UTC axis was, pointing the other way. The text and
    // the hover come from `axistime` through `pane.renderZoneTag()`, so the tag cannot claim a
    // zone the formatters are not using.
    const zone = document.createElement('span');
    zone.className = 'pane-tz';
    zone.dataset.tz = spec.id;
    bar.appendChild(zone);

    // Per-pane overlay toggles. ICT analysis is per timeframe: a dealing range on 1W is a
    // different claim from one on 15m, so sharing the toggles would make the panes decorative.
    // DERIVED FROM `OVERLAYS`, not a second hardcoded list. This WAS a hand-written array of five
    // pairs, and adding order blocks to the catalogue therefore produced zero checkboxes — the
    // overlay existed end to end (route, payload, bands) and was simply unreachable from the UI. A
    // Playwright assertion caught it: `order_blocks checkboxes: 0`.
    //
    // Short labels stay explicit because the catalogue's are sentences ("Order blocks — sweep
    // candle + imbalance (Dobrunia)") and a pane header has room for one word.
    const SHORT: Partial<Record<keyof PaneOverlays, string>> = {
      ranges: 'range', swings: 'swings', market_structure: 'structure', breaks: 'BOS',
      fvg: 'FVG', order_blocks: 'OB', liquidity: 'liq', setup: 'setup',
      // Card `t_fb5e844d`: 'static', so it cannot be read as `market_structure`'s 'structure'.
      static_structure: 'static',
      experiment: 'exp',
    };
    for (const o of OVERLAYS) {
      const key = o.id as keyof PaneOverlays;
      const label = SHORT[key] ?? key;
      const wrap = document.createElement('label');
      wrap.className = 'pane-ov';
      // The catalogue sentence as the hover title (card `t_28a039b9` §5 row 17): it was rendered
      // nowhere, so the OB definition it states could not be read off the screen at all.
      wrap.title = o.label;
      const cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.checked = state[spec.id].overlays[key];
      cb.dataset.ov = key;
      wrap.appendChild(cb);
      wrap.appendChild(document.createTextNode(label));
      bar.appendChild(wrap);
    }

    // A dedicated price row keeps crosshair updates from reflowing the controls or canvas.
    // Focus allows keyboard scrolling when the full price line exceeds a narrow pane.
    const ohlc = document.createElement('span');
    ohlc.className = 'pane-ohlc';
    ohlc.dataset.ohlc = spec.id;
    ohlc.tabIndex = 0;
    ohlc.setAttribute('aria-label', 'OHLC prices');
    bar.appendChild(ohlc);

    const canvas = document.createElement('div');
    canvas.className = 'pane-canvas';
    cell.appendChild(bar);
    cell.appendChild(canvas);
    // At its place in `PANE_SPEC` order: before the first BUILT pane that comes after it.
    const after = PANE_SPEC.slice(PANE_SPEC.findIndex((s) => s.id === spec.id) + 1)
      .map((s) => paneWires.get(s.id)?.cell)
      .find((c) => c !== undefined);
    grid.insertBefore(cell, after ?? null);
    const release: Array<() => void> = [];
    paneWires.set(spec.id, { cell, release });

    const pane = new ChartPane({
      id: spec.id,
      container: canvas,
      tf: state[spec.id].tf,
      overlays: state[spec.id].overlays,
    });
    panes.set(spec.id, pane);

    // The pane owns its tag from here on: `setTimeframe` re-renders it, so the header select's
    // change handler needs no second call.
    pane.zoneSlot = zone;
    pane.renderZoneTag();

    // EVERY pane's levels can be dragged, not just the execution pane's: Andrei asked for
    // the stop to be movable "on all panes". The callbacks are per pane but the MODEL is
    // shared, so whichever pane the gesture happens on, the price lands in one place and
    // every pane redraws from it (`liveLevels` subscription below).
    //
    // The handle names the position (`xyz:AMZN|sl`), so no id is passed here — a pane is
    // not bound to one market, it shows whichever one the panel is following, and binding
    // a pane to a stale coin is how a drag would land on the wrong position.
    //
    // Pixel -> price is done by THIS pane's own series inside its primitive. The panes
    // hold independent price windows (see the deleted-alias note above), so the same
    // pointer y is a different price on each; a shared scale would be wrong on three
    // panes out of four.
    // MOUSE-UP ASKS, IT DOES NOT WRITE: `tpCommitStaged` validates the dragged price
    // (zero network calls) and, if it can exist, opens the EXISTING confirm dialog for
    // `modify-sl` / `modify-tp`. The write still needs the explicit press.
    pane.levels.setCallbacks(dragCallbacks(liveLevels, undefined, tpCommitStaged));
    if (spec.id === EXECUTION_PANE_ID) {
      // Seed the hidden mirror from the RESTORED state, before the first loadBars reads it.
    }

    // PER PANE, and pane independence is structural rather than enforced: each `IChartApi` fires
    // only for its own canvas, so hovering pane 3 cannot move pane 1's header because pane 1's
    // chart never fires. `param.time` and not `param.seriesData`: the latter is keyed by
    // `ISeriesApi` (which `PaneSeriesReadonly` types away) and holds the chart's view of the data
    // rather than what the API served — the same reason `candleBars` exists for the magnet.
    const onCrosshair: MouseEventHandler<Time> = (param) => {
      hoveredTime.set(pane.id, param.time === undefined ? null : Number(param.time));
      renderHeader(pane);
    };
    pane.chart.subscribeCrosshairMove(onCrosshair);
    release.push(() => pane.chart.unsubscribeCrosshairMove(onCrosshair));
    renderHeader(pane);

    // SCROLL LEFT -> LOAD OLDER BARS FROM THE CACHE (2026-08-25, card t_dc8abf16).
    //
    // THE LIBRARY'S OWN PATH, not a hand-rolled window manager: lightweight-charts documents
    // `subscribeVisibleLogicalRangeChange` for exactly this ("lazy loading" in its own docs), and
    // `logicalRange.from` goes NEGATIVE as the user scrolls past the oldest bar — so "how close am I
    // to the left edge" is already computed by the chart and needs no measurement of our own.
    //
    // PER PANE, like every other subscription here: an `IChartApi` fires only for its own canvas, so
    // dragging the 15m pane cannot make the 1W pane page. That is the point — the three panes hold
    // three series with three left edges.
    //
    // THE DECISION IS NOT MADE HERE. Whether to ask is `shouldLoadOlder`, a pure function in
    // `history.ts` that vitest can drive; this callback only routes. A predicate written inline is
    // the shape gate 5 exists to catch.
    const onRange: LogicalRangeChangeEventHandler = (range) => {
      // A pane already torn down records nothing: `paneHistory` was deleted for it on purpose.
      if (!paneAlive(pane)) return;
      const state = historyOf(pane);
      const decided = shouldLoadOlder(range, state, pane.bars.length);
      // RECORD THE POSITION ON EVERY EVENT, including the ones that trigger nothing: the predicate
      // above asks whether the viewport MOVED LEFT, so the previous position is what makes the next
      // event answerable. Written AFTER the decision so this event is judged against where the
      // viewport WAS, not against itself.
      paneHistory.set(pane.id, noteRange(historyOf(pane), range));
      if (!decided) return;
      void loadOlderBars(pane);
    };
    pane.chart.timeScale().subscribeVisibleLogicalRangeChange(onRange);
    release.push(() => pane.chart.timeScale().unsubscribeVisibleLogicalRangeChange(onRange));

    // AND PAGING IS ARMED BY A HUMAN, not by the load finishing.
    //
    // MEASURED, three times (`process/reviews/t_dc8abf16/probe_boot_range_events.mjs`): a range
    // event carries no indication of who caused it, `resetChartLayout`'s own `fitContent` is an
    // 881-bar LEFTWARD move that satisfies both a proximity rule and a direction rule, and
    // lightweight-charts delivers range events ASYNCHRONOUSLY — so a flag set at the end of the load
    // path is still set before the app's own fit is reported. Every page load therefore fetched a
    // 48 KB history page nobody asked for, in an app whose data volume Andrei has objected to twice.
    //
    // A `wheel` or `pointerdown` on this pane's canvas is the one signal the app cannot generate for
    // itself, so it is what arms the pane. `resetHistory` disarms on every load, which is what keeps
    // a switch of asset or timeframe from inheriting the previous series' permission.
    //
    // PASSIVE listeners on the CONTAINER, in the CAPTURE phase: this must observe the gesture without
    // consuming it — the chart's own pan/zoom handlers and the drawing layer's pointer handling are
    // downstream, and a non-passive listener here could make the pane feel sluggish or swallow a drag.
    for (const ev of ['wheel', 'pointerdown'] as const) {
      pane.container.addEventListener(ev, () => {
        paneHistory.set(pane.id, armHistory(historyOf(pane)));
      }, { passive: true, capture: true });
    }

    sel.addEventListener('change', () => {
      // , not a field write: the axis clock (and anything else derived from tf)
      // must be re-applied on the chart, or a pane built at 1D keeps a date-only axis on 15m.
      pane.setTimeframe(sel.value);
      savePaneState();
      // Geometry from the OLD timeframe must not survive onto the new series: a 1W dealing-range
      // level painted over 15m bars is a price level that looks computed and belongs to a
      // different chart.
      repaintFvg.delete(pane.id);
      pane.clearOverlays();
      void loadPaneBars(pane);
    });

    bar.addEventListener('change', (ev) => {
      const cb = ev.target as HTMLInputElement;
      if (!cb.dataset.ov) return;
      pane.overlays[cb.dataset.ov as keyof PaneOverlays] = cb.checked;
      savePaneState();
      void refreshOverlays(pane);
    });
    return pane;
  }
}

/**
 * Tear ONE pane down completely (2026-10-19, 261019-pane-layout FR-2.6), in the required order:
 *
 *   1. out of the registry and out of every map or set keyed by pane id, so no loop, cache or
 *      selection can reach it again. Leaving `panes` is also what takes it out of the live poll:
 *      `liveTick` asks `panesDue` about the BUILT panes, and `loadContextPanes`, `tpDrawLevels` and
 *      `drawWire` iterate `panes`;
 *   2. its overlay epoch is BUMPED rather than deleted (`invalidateOverlays`, which also drops its
 *      FVG repaint and held answers), so a pass started before the teardown fails its epoch check
 *      even if the same id is rebuilt before that pass returns; `paneAlive` is the second guard;
 *   3. its chart subscriptions are released by handler reference. Its DOM listeners all sit on
 *      nodes inside its cell (container, select, bar), so they go with the cell; none sits on a node
 *      that outlives the pane;
 *   4. the cell leaves the grid, and `ChartPane.destroy()` (`chart.remove()`) runs LAST, so nothing
 *      above touches a disposed chart.
 *
 * The execution pane is refused: `activePane()` throws without it, and the panes table never asks
 * for its removal.
 */
function teardownPane(id: string): void {
  const pane = panes.get(id);
  if (!pane || id === EXECUTION_PANE_ID) return;
  invalidateOverlays(pane);
  panes.delete(id);
  for (const m of [hoveredTime, weeklyState, paneHistory, livePolledAt, drawWiredPanes]) m.delete(id);
  zoneViewPanes.delete(id);
  setupViewPanes.delete(id);
  if (drawSelPane === pane) drawSelPane = null;
  const wires = paneWires.get(id);
  paneWires.delete(id);
  for (const r of wires?.release ?? []) r();
  wires?.cell.remove();
  pane.destroy();
}

/** True once boot has started the first bar loads; until then a built pane is loaded by boot. */
let paneLoadsStarted = false;

/** The N currently built (the effective N: 1 on phone). */
let builtPaneCount = 0;

/**
 * Show N panes NOW, with no reload (2026-10-19, 261019-pane-layout FR-2.6).
 *
 * Panes not in `panesFor(n)` are torn down first, then the missing ones are built through the same
 * `buildPane` boot uses and inserted at their place, then the grid class is set (the CSS in
 * `index.html` keys the layout on it). Only the NEW panes are loaded, so a switch 3 -> 4 asks for
 * one series, not four. `pane-ltf` is in every row, so the order ticket, drawing layer and live poll
 * of the execution pane are never rebuilt.
 */
function applyPaneCount(n: number): void {
  const want = panesFor(n);
  const keep = new Set<string>(want.map((s) => s.id));
  for (const id of [...panes.keys()]) if (!keep.has(id)) teardownPane(id);
  const state = loadPaneState();
  const built = want.filter((s) => !panes.has(s.id)).map((s) => buildPane(s, state));
  const grid = el.chart;
  for (const c of [...grid.classList]) if (/^pane-n\d$/.test(c)) grid.classList.remove(c);
  grid.classList.add(gridClass(n));
  builtPaneCount = parsePaneCount(String(n));
  if (!paneLoadsStarted) return;
  for (const p of built) {
    if (drawWireDone) drawWirePane(p);
    void loadPaneBars(p);
  }
  // The context levels (entry / stop / target) on the new panes, and nothing to undo on the
  // removed ones: their primitives went with their charts.
  tpDrawLevels();
}

/** Build the panes for the saved N (1 on phone) and wire the picker. */
function initPanes() {
  const grid = el.chart;
  grid.innerHTML = '';
  grid.classList.add('pane-grid');
  const phone = window.matchMedia(MOBILE_QUERY);
  applyPaneCount(effectivePaneCount(loadPaneCount(localStorage), phone.matches));

  // THE PICKER (FR-2.1). Its value is the SAVED desktop N, not the built one, so the phone (which
  // hides it) never shows or saves its forced 1.
  const picker = document.getElementById('pane-count') as HTMLSelectElement | null;
  if (picker) {
    picker.value = String(loadPaneCount(localStorage));
    picker.addEventListener('change', () => {
      const n = parsePaneCount(picker.value);
      savePaneCount(localStorage, n);
      applyPaneCount(effectivePaneCount(n, phone.matches));
    });
  }
  // A WIDTH CROSSING 700 px re-applies the saved N without saving anything (FR-3.1): a phone turned
  // to landscape past 700 px, or a desktop window narrowed, gets the right layout, and the desktop N
  // survives a visit at phone width. The same query string as the CSS block (`MOBILE_QUERY`).
  phone.addEventListener('change', () => {
    const n = effectivePaneCount(loadPaneCount(localStorage), phone.matches);
    if (n !== builtPaneCount) applyPaneCount(n);
  });

  // ONE subscription, not one per pane: `tpDrawLevels` already writes every pane (the
  // execution pane directly, the rest through `drawContextLevels`), so a drag anywhere
  // repaints all of them in the same tick. N subscriptions would each redraw all of them.
  liveLevels.subscribe(() => tpDrawLevels());
}

function initChart() {
  // THE CHART ITSELF IS NOW A PANE. `ChartPane` owns createChart, both series, the volume scale
  // and the four overlay primitives — this function keeps only what is singular by nature and
  // binds it to the EXECUTION pane:
  //
  //   * the order ticket's draggable legs — one order, not three brackets on three charts
  //
  // THE CLICK-TO-INSPECT SUBSCRIPTION IS GONE (2026-08-18, card t_677775e0). It fed the
  // right-hand inspector panel, which was deleted for being width the charts wanted more; with no
  // sink, a click handler that fetched `/api/inspect` and threw the payload away is worse than
  // nothing. `/api/inspect` itself is untouched and still tested (`api/tests/test_inspect.py`).
  //
  // THE HAND-DRAWN LAYER IS NOT IN THAT LIST EITHER. It used to be, and that was the bug: a
  // module-level `DrawingPrimitive` attached here meant the 1W and 1D panes had no drawing layer
  // and no click subscription, so every drawing tool was dead on two of the three panes. Each
  // pane now owns its own layer (`pane.drawings`) and `drawWire()` subscribes to each pane's
  // chart — see the drawing section below.
  //
  // The 1W and 1D panes are otherwise read-only ICT context: their own timeframe, their own
  // overlays, no execution surface. That is why no "active pane" concept is needed for the money
  // path — see the pane registry above.
  const pane = activePane();

  // The order ticket's draggable legs. Same once-only reasoning, plus one more:
  // the primitive installs its pointer listeners in `attached()`, so re-attaching
  // would stack duplicate handlers and a single drag would move the level twice.
  pane.candleSeries.attachPrimitive(tkPrimitive);

  // Test hook for the ORDER TICKET: the drag path runs through lightweight-charts' own
  // hit-testing, which ignores
  // synthetic pointer events aimed at the canvas, so it cannot be driven from a
  // harness. This exposes the ticket's real state and the SAME functions the
  // pointer handlers call — not a parallel implementation — so a test through it
  // exercises production code.
  //
  // It also fixes a measurement trap found while verifying this feature: reading
  // the drag result off the form's `<input>` mid-drag returns a STALE value,
  // because the form is deliberately not re-rendered until the drop (rewriting
  // inputs on every pointermove would steal focus). The state, not the DOM, is
  // the thing to assert on.
  (window as unknown as { __ictTicket?: unknown }).__ictTicket = {
    state: () => JSON.parse(JSON.stringify(tkState)),
    preview: () => tkPreview,
    drag: (id: string, price: number) => {
      tkApplyDrag(id, price);
      tkDrawLines();
    },
    drop: (id: string, price: number) => {
      tkApplyDrag(id, price);
      return tkFetchPreview(true);
    },
    geometry: () => ticketGeometry(tkState, tkPreview),
    /** Panel-level probes. Same rationale as the ticket's: assert on state, not on
     *  a DOM read that can be stale. `coordFor` exposes the exact call the
     *  off-screen-level check relies on, so that check can be measured rather than
     *  assumed correct. */
    panel: () => ({
      selected: tpSelected,
      offscreen: [...tpOffscreenLevels],
      /** What `levelsFor` WOULD return for the active market. */
      levels: snapshot && tpActiveCoin() ? levelsFor(snapshot, tpActiveCoin() as string) : [],
      /** What is actually HANDED TO THE PRIMITIVE — the drawn truth. Distinct from
       *  `levels` on purpose: manage-mode suppression happens between the two, and a
       *  probe that recomputed would report the suppressed set as drawn. */
      drawn: [...tpDrawnLevels],
      coordFor: (p: number) => activePane().candleSeries.priceToCoordinate(p),
      showOnChart: (coin: string) => tpShowOnChart(coin),
    }),

    /** LIVE-POSITION LEVEL PROBES. Same rationale as the ticket's above: the drag runs
     *  through lightweight-charts' own hit testing, which ignores synthetic pointer
     *  events aimed at the canvas, so it cannot be driven from a harness.
     *
     *  `dragOn` calls the pane's REAL callbacks with a price this pane's OWN series
     *  derived from a pixel, so it exercises the production conversion rather than a
     *  parallel one — which is the thing a "did it use the right pane's scale?" check
     *  has to measure. */
    live: () => ({
      staged: Object.fromEntries(
        liveLevels.ids().map((id) => {
          const p = liveLevels.get(id);
          return [id, { sl: p?.pendingSl ?? null, tp: p?.pendingTp ?? null }];
        }),
      ),
      /** What each pane currently DRAWS, so cross-pane agreement is measurable. */
      drawn: Object.fromEntries(
        [...panes.values()].map((p) => [
          p.id,
          {
            tf: p.tf,
            priceOf: (leg: 'sl' | 'tp') => p.levels.priceOfLeg(leg),
            /** Every LABEL on this pane. Added for redteam D1: the defect was two panes
             *  drawing two DIFFERENT stop prices, both labelled `SL`, so the claim that a
             *  plan and a resting order are distinguishable has to be read off the pane
             *  rather than off the code that built the list. */
            labels: () => p.levels.drawnLabels(),
          },
        ]),
      ),
      /** Drag a leg on ONE pane, by pixel, through that pane's own price scale.
       *  `release` also fires mouse-up (`onDrop`), for a level off the visible range that
       *  a real pointer cannot press — see `LevelPrimitive.dragToCoordinate`. */
      dragOn: (paneId: string, leg: 'sl' | 'tp', y: number, release = false) =>
        panes.get(paneId)?.levels.dragToCoordinate(leg, y, release) ?? null,
      /** The INVERSE, on that same pane's own series: where does this price sit in
       *  canvas-local pixels? The browser E2E spec (`chartapp/e2e/legdrag.spec.ts`)
       *  needs it to aim a REAL pointer press at a level, and it must be the
       *  production conversion rather than a second one — otherwise the spec would
       *  press where its own arithmetic thinks the line is and prove nothing about
       *  where the app drew it. `null` also answers "is this price even on screen",
       *  which is the off-screen-level case that must not read as a missing stop. */
      coordOn: (paneId: string, price: number) => {
        const c = panes.get(paneId)?.candleSeries.priceToCoordinate(price as never);
        return typeof c === 'number' && Number.isFinite(c) ? c : null;
      },
      cancel: () => {
        for (const id of liveLevels.ids()) liveLevels.clearPending(id);
      },
    }),

    /** Chart VIEWPORT state, and the mutation-test lever for `resetChartLayout`.
     *
     *  `autoScale` is the load-bearing field — see that function. `breakAutoScale` sets
     *  the exact state a price-axis drag leaves behind, via the SAME library call the drag
     *  makes: the drag itself cannot be synthesized, as lightweight-charts hit-tests inside
     *  its own event pipeline and ignores synthetic PointerEvents. */
    viewport: () => ({
      autoScale: activePane().chart.priceScale('right').options().autoScale,
      logical: activePane().chart.timeScale().getVisibleLogicalRange(),
      bars: candleTimes.length,
    }),
    breakAutoScale: () =>
      activePane().chart.priceScale('right').applyOptions({ autoScale: false }),
  };

}

function renderMeta(meta: BarsMeta) {
  const rows: [string, unknown][] = [
    ['source', meta.source],
    ['symbol', meta.symbol],
    ['bars', meta.bars_count],
    ['earliest', meta.earliest_bar_utc],
    ['last closed', meta.last_closed_bar_utc],
    ['fetched', meta.fetched_at_utc],
  ];

  // Hyperliquid-only provenance. Each of these exists because it changes how the
  // chart should be READ, and none of it is inferable from the candles alone.
  if (meta.has_forming_bar !== undefined) {
    // Which candle is still moving. Without this the rightmost bar looks final.
    rows.push(['forming bar', meta.has_forming_bar ? String(meta.forming_bar_utc) : 'none']);
    rows.push(['closed bars', meta.closed_bars_count]);
  }
  if (meta.resampled_from) {
    // HL's native weekly is Thursday-anchored and its monthly is a rolling 30
    // days, so 1W/1M here are folded from daily bars. Saying so is the
    // difference between a comparable chart and a silently different one.
    rows.push(['resampled from', `${meta.resampled_from} → ${meta.resample_mode}`]);
  }
  if (meta.retention_days) rows.push(['HL retention', `${meta.retention_days}d`]);
  if (meta.first_real_volume_utc) {
    // ~900 pre-listing bars carry real prices with zero volume; a Volume Profile
    // across them sites its POC where nothing ever traded.
    rows.push(['volume valid from', meta.first_real_volume_utc]);
  }
  // The bars_cache defect, surfaced rather than smoothed over: all 111 cached
  // series stamp a still-forming bar as closed (measured 2026-07-26).
  if (meta.forming_suspected === true) {
    rows.push(['⚠ newest bar', 'captured BEFORE its close — stored as closed']);
  }

  el.meta.innerHTML = rows
    .map(([k, v]) => `<span>${k}</span><span>${v ?? '—'}</span>`)
    .join('');
  // The fingerprint is what analysis caches key staleness on; showing it lets a
  // chart-vs-htf_cache mismatch be caught by eye. Hyperliquid has none — it does
  // not feed those caches, and inventing one would imply it does.
  el.meta.innerHTML += `<span>fingerprint</span><span><code>${String(meta.input_fingerprint ?? '—')}</code></span>`;
}

/**
 * The document title as authored, captured BEFORE it is ever rewritten.
 *
 * Read once at module load rather than re-derived: `renderBuildInfo` runs on every health
 * refresh, and reading the CURRENT title to build the new one would concatenate the
 * prefix on itself ("dev · abc — dev · abc — ICT chart") after two calls.
 */
const BASE_TITLE = document.title;

/**
 * WHICH BUILD, WHICH ENVIRONMENT -- the top of the right pane.
 *
 * Blue/green means prod and dev are open in two tabs simultaneously, so this is the field
 * that has to be true before anything else on the page is worth reading. The values come
 * from `import.meta.env`, inlined by vite at build time from the pipeline's per-stage
 * `VITE_APP_BUILD` / `VITE_APP_ENV`.
 *
 * IT ALSO COMPARES ITSELF AGAINST THE BACKEND. A stale CloudFront index.html, or a half
 * finished `s3 sync`, serves a bundle from one commit against a Lambda from another -- a
 * state that is completely invisible unless the two stamps are put side by side, and the
 * cause of every "the deploy did nothing" report that could not be reproduced.
 */
function renderBuildInfo(h: Health | null) {
  const info = readBuildInfo(import.meta.env as never);
  const rows = [
    // DEV IS TINTED. `--warn` on the environment row, and only on dev: prod is the normal
    // case and colouring the normal case trains the colour to be ignored.
    `<span>env</span><span style="color:${info.appEnv === 'prod' ? 'var(--up)' : 'var(--warn)'}">${info.appEnv}</span>`,
    `<span>build</span><span><code>${info.build}</code></span>`,
  ];
  const stale = buildMismatch(info, h?.build);
  if (stale) {
    rows.push(
      `<span style="color:var(--warn)">⚠ api build</span>` +
        `<span style="color:var(--warn)"><code>${stale}</code> — this page is stale, reload</span>`,
    );
  }
  el.buildinfo.innerHTML = rows.join('');
  // AND THE TAB TITLE. The pane only helps when the tab is focused; with prod and dev open
  // side by side the tab strip is the one place both are visible simultaneously, which is
  // exactly when picking the wrong tab is a real risk.
  document.title = `${buildLabel(info)} — ${BASE_TITLE}`;
}

function renderIctStatus(h: Health) {
  if (!h.ict.importable) {
    el.ictstatus.innerHTML = `<span>status</span><span style="color:var(--warn)">not importable</span>`;
    el.ictstatus.innerHTML += `<span>reason</span><span><code>${h.ict.error ?? '—'}</code></span>`;
    return;
  }
  const comps = Object.entries(h.ict.components);
  el.ictstatus.innerHTML =
    `<span>version</span><span>${h.ict.version ?? 'unknown'}</span>` +
    comps
      .map(([name, ok]) =>
        `<span>${name}</span><span style="color:${ok ? 'var(--up)' : 'var(--muted)'}">${ok ? 'ready' : '—'}</span>`,
      )
      .join('');
}

function renderOverlayToggles(h: Health | null) {
  // THE SIDEBAR CHECKBOXES ARE GONE (2026-07-31). Andrei: "we don't need overlays controls in the
  // right side of the screen. Let's keep an inspector there only." (Quoted as said; he changed his
  // mind about the inspector on 2026-08-18 and it is gone too -- card t_677775e0. What survives in
  // this panel is what the paragraph below describes, none of which is the inspector.)
  //
  // Each pane now carries its own toggles (see `initPanes`), which is what makes the panes
  // independent ICT reads. Keeping a second, shared set would have been two controls for one fact
  // — and worse, the sidebar set wrote to `ov-*` DOM ids that nothing reads any more, so ticking
  // one would have looked broken rather than done nothing.
  //
  // What this function still renders is the MEASURED SUMMARY and the analysis-parameter selects
  // (inversion rule, ATR tolerance, min touches, mark-confirmation). Those are not overlays: they
  // change what the detector COMPUTES, so they legitimately apply to every pane at once.
  //
  // `h` is still taken because the summary reports module availability.
  el.overlays.innerHTML = '';

  // The MEASURED summary goes HERE — directly under the overlay checkboxes and
  // ABOVE the five control selects. It used to be appended last, which put the
  // alternation / doubled-bar counters (the numbers that decide whether the
  // algorithm does what it claims) below the fold at a normal window height. Same
  // lesson as moving the Inspector to the top: a diagnostic you have to scroll to
  // find is one you replace with a probe script.
  el.overlays.innerHTML += '<div class="kv" id="overlay-summary"></div>';

  // Shared controls. `inversion_rule` and the liquidity knobs are deliberately user
  // choices with NO default baked into the client: the retired production htf_analyzer ran
  // window=1 while every experiment uses 2/3/5, and the source implementations
  // disagree on what counts as an FVG inversion. A hidden default would conceal
  // exactly the discrepancies this app exists to expose.
  const wired = (id: string) => {
    const o = OVERLAYS.find((x) => x.id === id);
    return Boolean(o && h?.ict.importable && h.ict.components[o.module] && o.wired);
  };

  if (wired('swings') || wired('structure') || wired('ranges')) {
    // No swing-width or algorithm control: `fractal_n` and the fractal/alternating
    // selector were removed on 2026-07-26 when the pointer sequence became the single
    // swing definition (see overlays._pivots). `mark confirmation bars` stays because
    // the alternating confirm lag is VARIABLE, so the gap between where a swing
    // printed and where it became knowable is now worth seeing per swing.
    el.overlays.innerHTML += `
      <div class="overlay-sub">
        <input type="checkbox" id="show-confirm" />
        <label for="show-confirm">mark confirmation bars</label>
      </div>`;
  }
  if (wired('fvg')) {
    el.overlays.innerHTML += `
      <div class="overlay-sub">
        <label for="inv-rule">inversion</label>
        <select id="inv-rule">
          <option value="traded_through" selected>traded_through</option>
          <option value="ce_close">ce_close</option>
          <option value="close_beyond">close_beyond (ICT)</option>
        </select>
      </div>`;
  }
  // NO order_blocks controls. Andrei's 2026-08-02 spec has no tunables: a block is ONE candle, so the
  // `ob_reference` ambiguity that forced a dropdown is gone, and Step 4's forward-shift bound is
  // derived from the swing structure rather than configured.
  if (wired('liquidity')) {
    // Both knobs are exposed because both are UNCALIBRATED. The LuxAlgo
    // cross-check found production's 0.25xATR merges away ~a third of the
    // equal-highs an independent implementation sees (their EQH/EQL hit our
    // swings 75.7% but our clustered pools only 43.2%) — so the point is to
    // sweep it by eye, not to trust the default.
    el.overlays.innerHTML += `
      <div class="overlay-sub">
        <label for="tol-atr">tol xATR</label>
        <select id="tol-atr">
          <option value="0.10">0.10 — tighter than production</option>
          <option value="0.25" selected>0.25 — production (uncalibrated)</option>
          <option value="0.50">0.50</option>
          <option value="1.00">1.00 — merges aggressively</option>
        </select>
      </div>
      <div class="overlay-sub">
        <label for="min-touches">min touches</label>
        <select id="min-touches">
          <option value="1">1 — a lone old high is liquidity</option>
          <option value="2" selected>2 — a real equal-highs shelf</option>
          <option value="3">3</option>
        </select>
      </div>
      <div class="overlay-sub">
        <label for="max-dist-atr" title="A live setup further than this from spot is not drawn. Measured across BTC/ETH/SOL/AAVE on 1W/1D/15m (114 live setups): 20xATR keeps 54% and empties no timeframe, while 10xATR empties SOL 15m entirely. 0 draws every live setup regardless of distance.">setup reach</label>
        <select id="max-dist-atr">
          <option value="0">0 \u2014 no distance limit</option>
          <option value="10">10 \u2014 tight (can empty a timeframe)</option>
          <option value="20" selected>20 \u2014 production (uncalibrated)</option>
          <option value="30">30 \u2014 loose</option>
        </select>
      </div>`;
  }
  if (wired('static_structure')) {
    // PRE-SELECTED n = STATIC_N_DEFAULT = 1, a THREE-candle fractal (card `t_28a039b9`, bp_method.md
    // §5 row 1). The full course never says the count in words (kursy_full_course.md §2.2, card
    // `t_fb5e844d` left this blank), but the same author's channel lesson that course chapter is cut
    // from shows it: slide «Внутренняя Ликвидность / Трехсвечной фрактал» [F 015 95] over «заменю его
    // всем понятным фракталом» (015 01:23) and «только те фракталы, после образования которых цена
    // обновила High или Low телом» (015 01:39) -- the valid-fractal rule `ict.static_structure`
    // implements. The ROUTE still requires `n` (a caller names it); only this knob starts on 1.
    el.overlays.innerHTML += `
      <div class="overlay-sub">
        <label for="static-n" title="Bars each side of a fractal. 1 = the 3-candle fractal on his slide [F 015 95]; the overlay route requires it.">fractal n (1 = 3-candle, 015)</label>
        <select id="static-n">
          ${STATIC_N_CHOICES.map((v) => `<option value="${v}"${v === STATIC_N_DEFAULT ? ' selected' : ''}>${v}</option>`).join('')}
        </select>
      </div>`;
  }
}

/** Stop polling. Called before every load and on every switch: a timer left
 *  running would push BTC's live bar onto an ETH chart, or a 1m bar onto a 1D
 *  series, and lightweight-charts would accept it without complaint. */
function stopLive() {
  if (liveTimer !== null) {
    window.clearInterval(liveTimer);
    liveTimer = null;
  }
}

function renderLiveBox(d: LivePayload | null, note?: string) {
  if (note) {
    el.livebox.innerHTML = `<span>live</span><span class="muted">${note}</span>`;
    return;
  }
  if (!d || !d.forming) {
    el.livebox.innerHTML = '<span>live</span><span class="muted">—</span>';
    return;
  }
  const f = d.forming;
  // The mark must sit inside the forming bar's range. If it does not, the candle
  // being drawn is not the instrument being priced, and that has to be loud
  // rather than a discrepancy the eye has to catch.
  const markOk = d.mark === null || (d.mark >= f.low && d.mark <= f.high);
  const rows: [string, string][] = [
    ['bar open', new Date(f.time * 1000).toISOString().replace('T', ' ').slice(0, 16)],
    // `fmtPx`, the app's one magnitude-aware price formatter (card `t_ccfe3378` audit). A bare
    // `${f.open}` prints the float's own repr, which is fine for the venue's decimal strings but
    // is not the formatter every other price on this screen goes through.
    ['O / C', `${fmtPx(f.open)} / ${fmtPx(f.close)}`],
    ['H / L', `${fmtPx(f.high)} / ${fmtPx(f.low)}`],
    ['volume', f.volume.toFixed(2)],
    ['mark', fmtPx(d.mark)],
  ];
  // WHICH BAR the drawn overlays were computed from. Without this the chart
  // cannot distinguish "geometry is current" from "geometry is from page load" —
  // exactly the ambiguity that let a stale `ML?` sit on screen unnoticed.
  if (overlayComputedAt) {
    rows.push([
      'overlays',
      overlayComputedAt.pending
        ? `recomputing at bar ${overlayComputedAt.bar}…`
        : `computed at bar ${overlayComputedAt.bar}`,
    ]);
  }
  el.livebox.innerHTML =
    rows.map(([k, v]) => `<span>${k}</span><span>${v}</span>`).join('') +
    (markOk
      ? ''
      : `<span class="warn">⚠ mark</span><span class="warn">outside bar range</span>`);
}

/**
 * Poll the forming bar and repaint just that candle.
 *
 * `series.update()` rather than `setData()`: update mutates the last bar in place
 * (or appends if its time is new), so the viewport, scroll position and zoom are
 * preserved. A `setData()` here would re-send hundreds of bars every few seconds
 * and fight the user's pan.
 *
 * ROLLOVER is why `closed_bar` is applied first: when the bar closes, the server
 * returns the finalised old bar AND the new forming one. Applying the closed one
 * writes its true final values (a poll may have last seen it mid-tick), then the
 * new forming bar is appended. `closedBarCount` is incremented so the overlay-provenance
 * strings immediately count the just-closed bar.
 */
async function pollLive() {
  const asset = el.asset.value;
  const tf = execTf();
  const source = el.source.value;
  if (!asset || !tf) return;
  try {
    const d = await getJSON<LivePayload>(
      `/api/live/${encodeURIComponent(asset)}/${encodeURIComponent(tf)}?${q()}`,
    );
    if (source !== el.source.value || d.source !== source) return;
    if (!d.live) {
      // The FEED has no live bar (bars_cache is a static snapshot) — a permanent
      // property, so stopping the timer is correct.
      renderLiveBox(null, 'source has no live bar');
      stopLive();
      return;
    }
    if (!d.forming) {
      // A live feed with NOTHING forming is TRANSIENT, not terminal: right after a
      // bucket boundary HL's newest candle can already be closed, so `split_forming`
      // finds no forming bar for a moment. Measured on BTC 1m: 1 poll in 42 (2.4%)
      // lands in that window (.tmp/probe_forming_null.py).
      //
      // This used to call stopLive(), which killed the live poll PERMANENTLY at the
      // first minute boundary — the loop the bar-close overlay recompute rides on.
      // Keep polling and say so; the next poll (<=5s) has the new bar.
      renderLiveBox(null, 'between bars — waiting for the next candle');
      return;
    }
    // Guard against a response that arrived after the user switched away.
    if (d.asset !== el.asset.value || d.tf !== execTf()) return;
    const paint = (b: { time: number; open: number; high: number; low: number; close: number; volume: number }) => {
      const t = b.time as UTCTimestamp;
      // VIA THE PANE, same reason as the `setBars` call in `loadBars`: `ChartPane.updateBar` applies
      // the inside-candle tint and keeps `pane.bars` in step. A raw `candleSeries.update` here drew
      // the forming bar bright even once the loaded series was tinted correctly.
      activePane().updateBar({ time: b.time, open: b.open, high: b.high, low: b.low, close: b.close });
      if (!candleTimes.includes(t)) candleTimes.push(t);
    };

    // lightweight-charts' `update()` REFUSES a bar older than the series' last
    // one ("Cannot update oldest data") — found by driving the real UI, not by
    // reading the docs. In steady state `closed_bar` IS older than the drawn
    // forming bar and is already final on screen, so re-sending it is both an
    // error and pointless. It only needs applying on ROLLOVER, where its time
    // equals the bar we are currently drawing as forming: that is the moment the
    // live candle finalises and its true closing values must land.
    const lastDrawn = candleTimes.length ? candleTimes[candleTimes.length - 1] : undefined;
    if (d.closed_bar && (lastDrawn === undefined || d.closed_bar.time >= lastDrawn)) {
      paint(d.closed_bar);
    }

    const f = d.forming;
    paint(f);
    // A non-null `forming` means the bar just appended is the unclosed one.
    activePane().lastBarForming = true;
    renderHeader(activePane());
    // THE ZIGZAG'S DASHED TAIL FOLLOWS THE CANDLES, not the rollover below: see
    // `paintZigzagTip`. This is a recompute from bars already in memory, not a detector refresh.
    // (The week BOX used to be repainted here for the same reason; the box is deleted -- card
    // t_70188040 removed all weekly geometry, so there is nothing weekly left to keep in step.)
    paintZigzagTip(activePane());
    repaintFvg.get(activePane().id)?.();

    // Everything strictly before the forming bar is closed and therefore quotable
    // as a bar number. Derived from position rather than incremented, so a missed
    // or duplicated rollover cannot leave the count permanently skewed.
    const formingIdx = candleTimes.indexOf(f.time as UTCTimestamp);
    if (formingIdx >= 0) closedBarCount = formingIdx;

    // A BAR CLOSED -> the detectors' answer may have changed, so re-ASK them.
    // Overlay geometry used to be refreshed only on asset/tf switch, reload, or a
    // checkbox toggle, so a session left open across a close drew new candles
    // against old swings/majors/ranges (measured: the pivot set moves on 21.7%
    // of 1D closes and 31.7% of 15m closes — see barclose.ts).
    //
    // On the TRANSITION only, never on every 5s tick: refreshing each poll would
    // re-run ~7 detector endpoints over up to 5,000 bars every five seconds.
    const decision = detectRollover(rollover, d, seriesKey(el.asset.value, execTf()));
    rollover = decision.next;
    renderLiveBox(d);
    if (decision.rolledOver) {
      // VISIBLE, per Andrei's standing rule: an overlay that silently self-heals
      // leaves you unable to tell a recompute from a chart that never updated —
      // which is the failure being fixed. Say it before and after.
      overlayComputedAt = { bar: closedBarCount, at: Date.now(), pending: true };
      renderLiveBox(d);
      setStatus(`bar closed — recomputing overlays at bar ${closedBarCount}…`);
      await refreshOverlays();
      overlayComputedAt = { bar: closedBarCount, at: Date.now(), pending: false };
      setStatus(`overlays recomputed at bar ${closedBarCount} (${el.asset.value} ${execTf()})`);
      renderLiveBox(d);
    }
  } catch (err) {
    // A transient failure must not kill the poll loop or blank the chart — the
    // exchange being briefly unreachable is normal and the drawn bars stay valid.
    renderLiveBox(null, `error: ${err}`);
  }
}

/** Refresh ONE context pane's last candle (any pane that is not the execution one).
 *
 * WHY THIS EXISTS. `pollLive` only ever read `el.asset.value` + `execTf()`, so the 15m pane ticked
 * and the 1W/1D panes held their page-load candle forever. Measured 2026-08-01: the backend moved
 * 62436 -> 62403 over 33 s while both HTF panes sat unchanged — a chart showing a stale close beside
 * a live one, which is worse than showing neither.
 *
 * WHICH pane, and HOW OFTEN, is no longer decided here. It used to be a round-robin cursor over the
 * context panes on the 5 s tick, i.e. every pane polled every 10 s regardless of its timeframe — a
 * 1W candle re-fetched 6 times a minute to learn nothing (see `livepoll.ts` for Andrei's numbers).
 * `panesDue` now derives the cadence from the bar period, so this function just does the one pane it
 * is handed.
 *
 * ponytail: still N requests for N panes. If more panes are ever added, switch to a websocket —
 * polling does not scale past a handful of series.
 */
async function pollContextPane(pane: ChartPane): Promise<void> {
  const asset = el.asset.value;
  if (!asset) return;
  const tf = pane.tf;
  const source = el.source.value;

  try {
    const d = await getJSON<LivePayload>(
      `/api/live/${encodeURIComponent(asset)}/${encodeURIComponent(tf)}?${q()}`,
    );
    // The asset can change mid-flight; a response for the previous symbol must never be drawn.
    // Nor one for a pane torn down by the pane-count picker while it was in flight.
    if (!paneAlive(pane) || d.asset !== el.asset.value || pane.tf !== tf || source !== el.source.value || d.source !== source) return;
    if (!d.live || !d.forming) return;
    // `closed_bar` first, then forming — the same order and the same reason as `pollLive`: on
    // rollover the just-closed candle must land with its true values before the new one is drawn.
    if (d.closed_bar) pane.updateBar(d.closed_bar);
    pane.updateBar(d.forming);
    pane.lastBarForming = true;
    renderHeader(pane);
    // Same as `pollLive`: this loop mutates `pane.bars` every cadence, so the zigzag's tail is
    // re-aimed here rather than at the next rollover. See `paintZigzagTip`.
    paintZigzagTip(pane);
    repaintFvg.get(pane.id)?.();
  } catch {
    // Silent: a context pane's last candle is a nicety, and `pollLive` already surfaces feed errors
    // in the live box. Two components shouting the same outage is noise.
  }
}

/**
 * One tick of the live loop: poll every pane whose cadence says it is due.
 *
 * The TICK is `LIVE_TICK_MS` (5 s, the old flat interval) but a pane is only ASKED when
 * `livePollMs(tf)` has elapsed for it — so the 15m execution pane still refreshes every 5 s while the
 * 1W and 1D panes drop from every 10 s to every 60 s. A HIDDEN tab polls nothing at all
 * (`panesDue` returns []), which is where most of the saved requests come from: Chrome throttles a
 * background timer rather than stopping it, and each survivor still paid a Paris round trip.
 *
 * SEQUENTIAL, still, and for the original reason: each call costs ~780 ms of Lambda time (HAR H3), so
 * firing three at once would put three concurrent requests behind one tick. `await` in the loop keeps
 * the duty cycle the /api/live fix bought (69% -> 14%).
 *
 * The timestamp is recorded BEFORE the await, not after: keying off completion would let a slow
 * response stretch the interval by its own latency — exactly the transatlantic latency being worked
 * around — and turn a 60 s cadence into 61.5 s that drifts.
 */
async function liveTick(): Promise<void> {
  // The BUILT panes, not `PANE_SPEC` (2026-10-19, 261019-pane-layout FR-2.6): at N=1 or N=2 the
  // spec rows that are not built would otherwise each keep a `livePolledAt` cadence for nothing.
  const due = panesDue(
    [...panes.values()].map((p) => ({ id: p.id, tf: p.tf })),
    livePolledAt,
    Date.now(),
    document.visibilityState === 'hidden',
  );
  for (const target of due) {
    livePolledAt.set(target.id, Date.now());
    if (target.id === EXECUTION_PANE_ID) {
      await pollLive();
    } else {
      const pane = panes.get(target.id);
      if (pane) await pollContextPane(pane);
    }
  }
}

function startLive() {
  stopLive();
  const src = currentSource();
  // NO LIVE POLL ON A STATIC SOURCE, AND THEREFORE NONE ON AN ANCHORED PANE (t_42cdbde6 U7 PD-6).
  // The research source is `live: false`, and only it serves `around`, so this return is also what
  // keeps a poll from replacing an event-centred window with the latest bars.
  if (!src?.live) {
    renderLiveBox(null, 'bars_cache is a static snapshot');
    return;
  }
  if (!el.livetoggle.checked) {
    renderLiveBox(null, 'paused');
    return;
  }
  // Cleared so the first tick treats every pane as due, whatever the previous series left behind: a
  // timestamp carried across an asset change would make a pane wait up to 60 s to draw its FIRST
  // candle on the new symbol.
  livePolledAt.clear();
  void liveTick();
  liveTimer = window.setInterval(() => {
    // ONE clock for every pane rather than one interval each: one clock means no pane can outlive
    // `stopLive()`, which is how a paused chart ends up quietly still polling.
    void liveTick();
  }, LIVE_TICK_MS);
}

/**
 * Coming back to the tab polls IMMEDIATELY, rather than waiting out the interval.
 *
 * This is the other half of the visibility gate and it is not optional: without it, gating the poll
 * would mean a tab returned to after an hour showed an hour-old candle until the next tick fired. The
 * timestamps are cleared so every pane counts as due, so what the user sees on return is fresh —
 * which is what makes skipping the hidden polls safe rather than a staleness bug.
 *
 * Registered once, at module scope, on `document`. `startLive`/`stopLive` are called on every
 * asset/tf/source change and re-registering there would stack a listener per switch.
 */
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') return;
  if (liveTimer === null) return;
  livePolledAt.clear();
  void liveTick();
});

/** Reset ONE pane's viewport to its freshly-loaded series — BOTH axes.
 *
 *  `fitContent()` resets time only. lightweight-charts latches `autoScale: false` on the
 *  price scale the first time the user drags that axis, and the latch survives `setData()`:
 *  measured, BTC (15,443-126,297) → DOGE (0.049-0.486) drew DOGE's whole range on ONE pixel
 *  row (span 0px, was 207.6px), silently. Re-arm rather than compute a range, so the series
 *  — and the ticket's `autoscaleInfo` legs — keep deciding their own extent.
 *
 *  THE `autoscaleInfoProvider` GOES TOO, and that is the second half of Andrei's report
 *  ("when selecting new ticker, the pane scale sometimes does not change in such a way that I
 *  can see bars"). `showOffPaneZones` installs a provider pinning a FIXED price window — SOL's
 *  7.53-61.92, say — and a provider outranks autoscale, so re-arming alone would leave BTC's
 *  64000 candles clamped into a window computed for the previous asset. Clearing it here means
 *  the forced zone view is scoped to the series it was asked for, which is the only series it
 *  described.
 *
 *  PER PANE, and takes its pane as an argument. It used to read a module-level `chart` alias
 *  bound to the execution pane, so switching asset re-armed ONE of three panes and the 1W/1D
 *  panes kept the previous asset's price window — the reported bug. See the deleted-alias note
 *  at the top of this file: naming the pane is now mandatory.
 *
 *  THE TIME WINDOW IS ANDREI'S, NOT `fitContent()`'s (2026-08-10): 1W opens on 3 years, 1D on
 *  4 months, 15m on 1 week — see `paneWindowRange`, which now lives in `chartpane.ts` beside the
 *  other per-pane view facts. `fitContent()` showed everything fetched, which his screenshot
 *  measured at ~15 months of weekly candles.
 *
 *  ORDER MATTERS AND IT IS THE TRAP THIS FUNCTION EXISTS TO DOCUMENT. The price scale is
 *  re-armed BEFORE the time range is set, because lightweight-charts autoscales to the bars
 *  inside the VISIBLE time range: narrowing the range with the `autoScale: false` latch still
 *  on leaves the price window computed for the whole fetched history, which is a tighter,
 *  quieter version of the DOGE-on-one-pixel-row bug above. `setVisibleRange` on an autoscaling
 *  price scale triggers a fresh price fit for the bars it reveals; `fitContent()` is kept as
 *  the fallback for the series `paneWindowRange` declines to window.
 *  Numbers + mutation test: chartapp/README.md §"Switching asset resets BOTH axes". */
function resetChartLayout(pane: ChartPane) {
  if (zoneViewPanes.delete(pane.id)) {
    pane.candleSeries.applyOptions({ autoscaleInfoProvider: undefined });
  }
  pane.chart.priceScale('right').applyOptions({ autoScale: true });
  const win = paneWindowRange(pane.tf, pane.bars);
  if (!win) {
    pane.chart.timeScale().fitContent();
    return;
  }
  pane.chart.timeScale().setVisibleRange({
    from: win.from as unknown as Time,
    to: win.to as unknown as Time,
  });
}

// ───────────────────────────────────── "zones off screen — show them" (2026-08-03)
//
// Which panes are currently FORCED to show their off-pane zones. Per pane, keyed by id: the
// three panes hold different timeframes, so 1W's zones being off screen says nothing about
// 1D's, and a control that acted on `activePane()` regardless of which row was clicked is the
// exact bug shipped twice today (see `drawWirePane`).
const zoneViewPanes = new Set<string>();

/** What price window ONE pane is actually showing, read off its own scale.
 *
 *  The pane's TOP and BOTTOM pixel rows converted back to prices. There is no
 *  `priceScale().getVisibleRange()` in lightweight-charts v4, and `priceToCoordinate`
 *  EXTRAPOLATES off-screen instead of returning null (measured — see the TP off-screen check),
 *  so this is the only honest read of "what can the user see". Shared by the control and by the
 *  test hook so a spec asserts against the same number the control unions from. */
function paneVisibleRange(pane: ChartPane): { lo: number; hi: number } | null {
  const h = pane.container.getBoundingClientRect().height;
  if (h <= 0) return null;
  const top = pane.candleSeries.coordinateToPrice(0);
  const bot = pane.candleSeries.coordinateToPrice(h);
  if (top == null || bot == null) return null;
  return { lo: Math.min(top, bot), hi: Math.max(top, bot) };
}

/** The control appended to the `zones off screen` row. Styled as the existing `mini` button. */
function zoneViewButton(pane: ChartPane): string {
  const on = zoneViewPanes.has(pane.id);
  return ` <button class="mini${on ? ' on' : ''}" data-zoneview="${pane.id}" title="${
    on
      ? 'Return this pane to its default price scale (autoscale to the candles).'
      : 'Widen this pane\u2019s price scale until every off-screen zone is inside it.'
      + ' The zones ARE detected \u2014 they are simply far from spot, so the recent candles'
      + ' will compress into a band. Click again to go back.'
  }">${on ? 'default view' : 'show them'}</button>`;
}

/** Put (or remove) this pane's zone-view control in ITS OWN bar, so it is reachable regardless of
 *  which pane last wrote the shared overlay summary. */
function renderPaneZoneView(pane: ChartPane, offPane: number, total: number): void {
  const bar = document.querySelector(`[data-pane="${pane.id}"] .pane-bar`);
  if (!bar) return;
  bar.querySelector('.zoneview-slot')?.remove();
  const forced = zoneViewPanes.has(pane.id);
  if (offPane <= 0 && !forced) return;
  const slot = document.createElement('span');
  slot.className = 'zoneview-slot';
  slot.innerHTML = forced
    ? `<span class="muted">all ${total} zones in view</span>${zoneViewButton(pane)}`
    : `<span class="warn">${offPane} zone(s) off screen</span>${zoneViewButton(pane)}`;
  // Keep transient controls above the dedicated OHLC row.
  bar.insertBefore(slot, bar.querySelector('.pane-ohlc'));
}

// ───────────────────────────────────── "N more setups — show all" (2026-08-24, card t_0ad264de)
//
// Which panes are currently drawing EVERY setup rather than the nearest one per direction. PER
// PANE and keyed by id, for the same reason `zoneViewPanes` is: the three panes hold different
// timeframes, so 15m being an unreadable mat says nothing about 1W, and a control that acted on
// `activePane()` regardless of which row was clicked is a bug this file has shipped twice.
const setupViewPanes = new Set<string>();

/** The `show all` / `primary only` toggle, styled as the existing `mini` button. */
function setupViewButton(pane: ChartPane, withheld: number): string {
  const on = setupViewPanes.has(pane.id);
  return ` <button class="mini${on ? ' on' : ''}" data-setupview="${pane.id}" title="${
    on
      ? 'Draw only the nearest live setup per direction again.'
      : 'Draw every setup this timeframe carries \u2014 including the spent zones, which show'
        + ' where price has already reacted. The pane will get busy; click again to go back.'
  }">${on ? 'primary only' : `show all${withheld > 0 ? ` (+${withheld})` : ''}`}</button>`;
}

/** Put (or remove) this pane's setup-view control in ITS OWN bar.
 *
 *  ITS OWN BAR, not `#overlay-summary`, and that is not a style choice: the summary is ONE shared
 *  box that whichever pane refreshes LAST overwrites, so a summary-only control would be
 *  unreachable for exactly the pane the reader is looking at (measured for the zone-view control:
 *  candidates pane-htf 0, pane-mtf 0, pane-ltf 1).
 *
 *  THE ROW SURVIVES A SUCCESSFUL CLICK. In the forced state `withheld` is 0 by construction, so a
 *  `withheld <= 0` early return alone would delete the only way back the moment the click worked —
 *  the same trap the `zones in view` branch above exists to avoid. */
function renderPaneSetupView(pane: ChartPane, withheld: number): void {
  const bar = document.querySelector(`[data-pane="${pane.id}"] .pane-bar`);
  if (!bar) return;
  bar.querySelector('.setupview-slot')?.remove();
  const forced = setupViewPanes.has(pane.id);
  if (withheld <= 0 && !forced) return;
  const slot = document.createElement('span');
  slot.className = 'setupview-slot';
  slot.innerHTML = forced
    ? `<span class="muted">all setups drawn</span>${setupViewButton(pane, withheld)}`
    : `<span class="muted">${withheld} more setup band(s)</span>${setupViewButton(pane, withheld)}`;
  bar.insertBefore(slot, bar.querySelector('.pane-ohlc'));
}

/** Say WHY this pane draws no IDM, in ITS OWN bar -- or remove the line (card `t_0cdae4dc`).
 *
 *  The setup summary row says it too, but `#overlay-summary` is the one shared box the LAST pane
 *  to refresh overwrites (the reason `renderPaneSetupView` lives in the pane bar), so a 1D pane's
 *  "none" would be erased by 15m's refresh. The chart itself draws nothing: a level that does not
 *  exist has no line. */
function renderPaneIdmNone(pane: ChartPane, text: string | null): void {
  const bar = document.querySelector(`[data-pane="${pane.id}"] .pane-bar`);
  if (!bar) return;
  bar.querySelector('.idmnone-slot')?.remove();
  if (!text) return;
  const slot = document.createElement('span');
  slot.className = 'idmnone-slot muted';
  slot.textContent = text;
  bar.insertBefore(slot, bar.querySelector('.pane-ohlc'));
}

/** Flip ONE pane between the primary-only and the full setup view, then redraw THAT pane.
 *
 *  No scale surgery, unlike `showOffPaneZones`: the withheld bands are near spot by construction
 *  (the reach filter already dropped the far ones), so what was missing was the geometry, not the
 *  window. If they do land off screen the existing off-screen disclosure picks them up — two
 *  controls, two questions, neither reimplementing the other. */
function toggleSetupView(pane: ChartPane): void {
  if (!setupViewPanes.delete(pane.id)) setupViewPanes.add(pane.id);
  void refreshOverlays(pane);
}

/** One delegated listener for every pane's setup-view button.
 *
 *  On `document` for the same reason `wireZoneView` is: the pane bar's slot is rewritten on every
 *  refresh — including the refresh this very click triggers — so a handler bound to the button
 *  would be discarded by its own effect. */
function wireSetupView(): void {
  document.addEventListener('click', (ev) => {
    const id = (ev.target as HTMLElement | null)?.closest?.('[data-setupview]')
      ?.getAttribute('data-setupview');
    const pane = id ? panes.get(id) : null;
    if (pane) toggleSetupView(pane);
  });
}

/**
 * Write (or clear) THIS pane's overlay-failure line in ITS OWN bar.
 *
 * THE SINK FOR OVERLAY FAILURES since 2026-08-18 (card t_677775e0). They used to land in the
 * right-hand inspector panel — the surface Andrei asked to be rid of — and `#overlay-summary` is
 * not a replacement: it is ONE shared box that whichever pane refreshes LAST overwrites (measured:
 * candidates pane-htf 0, pane-mtf 0, pane-ltf 1), so a failure on the 1D pane was erased by 15m's
 * refresh milliseconds later. Same reasoning, same element, as `renderPaneZoneView` above.
 *
 * `#status` still gets the message too (see `load`), and the two are not redundant: the status line
 * is one global slot that the next failure overwrites, this one is per pane and survives.
 *
 * REMOVED, not blanked, when there is nothing to say — an empty `.pane-err` still costs the bar a
 * wrapped row, and a row that appears on success is indistinguishable from a stale failure.
 */
function renderPaneError(pane: ChartPane, failures: [string, string][]): void {
  const bar = document.querySelector(`[data-pane="${pane.id}"] .pane-bar`);
  if (!bar) return;
  bar.querySelector('.pane-err')?.remove();
  const line = paneErrorLine(failures);
  if (!line) return;
  const slot = document.createElement('span');
  slot.className = 'pane-err';
  slot.textContent = line;
  bar.insertBefore(slot, bar.firstChild);
}

/**
 * Widen ONE pane's price scale until every band its primitive holds is inside it.
 *
 * WHY NOT `autoScale: true` ALONE. Measured in a browser against this exact scenario: autoscale
 * fits the CANDLES, and it is the only thing the latch (`autoScale: false`, set by the user's
 * first price-axis drag and surviving `setData()` — see `resetChartLayout`) blocks. Clearing the
 * latch therefore returns the pane to the candles' extent, which is where the zone was invisible
 * to begin with whenever the zone lies outside it. A zone at 7.53 under a 50-350 window needs the
 * RANGE widened, not re-armed.
 *
 * DONE THROUGH `autoscaleInfoProvider` rather than a price-scale range setter because
 * lightweight-charts v4 has none: `IPriceScaleApi` exposes only `applyOptions`/`options`/`width`.
 * The provider is the library's own supported way to make the scale include prices the series
 * does not contain, and it is already the mechanism the order ticket's legs use
 * (`TicketPrimitive.autoscaleInfo`) — same primitive, one dimension over.
 *
 * A SECOND CLICK removes the provider and re-arms autoscale, i.e. the default view. That also
 * clears the drag latch, which is deliberate: the user who ends up here got here because the
 * latch stranded them.
 */
function showOffPaneZones(pane: ChartPane): void {
  if (zoneViewPanes.has(pane.id)) {
    zoneViewPanes.delete(pane.id);
    pane.candleSeries.applyOptions({ autoscaleInfoProvider: undefined });
    pane.chart.priceScale('right').applyOptions({ autoScale: true });
    void refreshOverlays(pane);
    return;
  }
  const bands = pane.range.bands;
  if (!bands.length) return;
  const visible = paneVisibleRange(pane)
    ?? { lo: Math.min(...bands.map((b) => b.lo)), hi: Math.max(...bands.map((b) => b.hi)) };
  const r = zoneViewRange(visible, bands);
  zoneViewPanes.add(pane.id);
  pane.candleSeries.applyOptions({
    autoscaleInfoProvider: () => ({ priceRange: { minValue: r.lo, maxValue: r.hi } }),
  });
  // The provider only gets asked while the scale is in AUTO mode, so the latch that stranded the
  // user would otherwise swallow the whole fix.
  pane.chart.priceScale('right').applyOptions({ autoScale: true });
  void refreshOverlays(pane);
}

/** One delegated listener for every pane's zone-view button.
 *
 *  On `document`, registered once: the overlay summary's innerHTML is rewritten on every refresh
 *  (and the container itself on every `renderOverlayControls`), so a handler bound to the button
 *  or its box would be discarded by the next repaint — which is precisely the repaint this
 *  control triggers. */
function wireZoneView(): void {
  document.addEventListener('click', (ev) => {
    const id = (ev.target as HTMLElement | null)?.closest?.('[data-zoneview]')
      ?.getAttribute('data-zoneview');
    const pane = id ? panes.get(id) : null;
    if (pane) showOffPaneZones(pane);
  });

  // Test hook, same rationale as `__ictDraw` / `__ictTicket`: a price-axis DRAG cannot be
  // synthesized (lightweight-charts hit-tests inside its own event pipeline), so the state the
  // drag leaves behind has to be set through the library's own call. PER PANE and keyed by pane
  // id, because "are this pane's zones on screen" is a per-pane question and the whole bug class
  // here is a control that answers it for the wrong pane. The click itself is NOT exposed: the
  // spec clicks the real button in the DOM, so it proves the wiring too.
  (window as unknown as { __ictZones?: unknown }).__ictZones = {
    /** Per pane: the visible price window, the bands the primitive holds, how many of those fall
     *  outside that window, and whether the scale is in auto mode. */
    state: () => Object.fromEntries([...panes.values()].map((p) => {
      const vis = paneVisibleRange(p);
      // `fromTime` IS PROJECTED, added 2026-08-26 for card `t_79d313f6`. Without it this hook could
      // not answer the one question the off-window clamp is about -- WHERE a band's left edge sits --
      // so a browser run reported the IDM's anchor as `null` and the clamp was unobservable from the
      // screen, which is exactly the "verified the artifact we edited, not the one that matters"
      // failure that card exists to correct. `lo`/`hi`/`label` alone describe a price and a word.
      // `toTime` added 2026-10-10 (card `t_5b2017b5`): a session box's width IS its window, so the
      // browser check has to read the right edge the primitive holds, not only the left.
      const bands = p.range.bands.map((b) => ({
        lo: b.lo, hi: b.hi, label: b.label, fromTime: b.fromTime, toTime: b.toTime ?? null,
      }));
      // THE CANDLES' OWN EXTENT, added 2026-08-05 for the ticker-switch scale spec. "Can Andrei see
      // bars" needs the bars, and `visible` alone cannot answer it -- a pane showing BTC's
      // 64000 window with SOL's 73 candles reports a perfectly healthy `visible`. Read off
      // `pane.bars` (the closed series actually rendered) rather than recomputed from anything.
      const highs = p.bars.map((b) => b.high);
      const lows = p.bars.map((b) => b.low);
      return [p.id, {
        visible: vis,
        candles: highs.length ? { lo: Math.min(...lows), hi: Math.max(...highs) } : null,
        bands,
        offScreen: vis === null ? bands.length
          : bands.filter((b) => b.hi < vis.lo || b.lo > vis.hi).length,
        autoScale: p.chart.priceScale('right').options().autoScale,
        forced: zoneViewPanes.has(p.id),
        // THE SERIES' OWN priceFormat, read back from the library, not from `pane.priceDecimals`
        // (card `t_ccfe3378`). A browser spec asking "does the PUMP axis carry 6 places" must
        // read what the series was told, or a `setPriceDecimals` that records the number and
        // forgets `applyOptions` passes.
        priceFormat: p.candleSeries.options().priceFormat,
        // The markers the SERIES holds (library read-back), card `t_5b2017b5`: the session M/D
        // check compares these against the payload, never against the builder's own list.
        markers: p.candleSeries.markers().map((m) => ({ time: m.time, text: m.text ?? '' })),
      }];
    })),
    /** The exact state a user's price-axis drag leaves behind, on ONE pane. */
    breakAutoScale: (paneId: string) =>
      panes.get(paneId)?.chart.priceScale('right').applyOptions({ autoScale: false }),
    /** Narrow the window to the last bars only -- what a user's ZOOM leaves behind, and the state in
     *  which an old zone is genuinely off screen. Needed because a pane's default range can already
     *  span every band (measured on BTC 1W: -60084 .. 190598), and a spec that runs in that state
     *  exercises nothing. */
    squeezeForTests: (paneId: string) => {
      const p = panes.get(paneId);
      if (!p) return;
      const bars = p.bars ?? [];
      const tail = bars.slice(-60);
      if (!tail.length) return;
      const lo = Math.min(...tail.map((b) => b.low));
      const hi = Math.max(...tail.map((b) => b.high));
      const pad = (hi - lo) * 0.05 || 1;
      p.candleSeries.applyOptions({
        autoscaleInfoProvider: () => ({ priceRange: { minValue: lo - pad, maxValue: hi + pad } }),
      });
      p.chart.priceScale('right').applyOptions({ autoScale: true });
    },
  };

  /**
   * PANE TEST HOOK — the TIME AXIS, read back through the library rather than recomputed.
   *
   * Its whole purpose is a property vitest structurally cannot reach (card t_69d27c87). The unit
   * tests prove `axisTickLabel` and `crosshairTimeLabel` are correct; they cannot prove
   * lightweight-charts is CALLING them. `tickMarkFormatter` is optional, is silently ignored if it
   * lands on the wrong options object, and a `null` return falls back to the library's own UTC
   * text -- so a perfectly correct formatter and a completely unlocalized axis look identical from
   * inside vitest.
   *
   * SO THE FORMATTERS ARE INVOKED THROUGH `chart.options()`, i.e. the exact function objects the
   * library holds and paints with. Not `axistime`'s exports, which is what a recomputation would
   * be and would pass with the hooks never installed. The tick TIMES are this pane's own bar
   * times, sampled evenly, because the library's internal mark list is a private field and reading
   * it would bind this hook to a minified name.
   *
   * READ-ONLY: it formats and reports. `getTime`-style state is untouched.
   */
  (window as unknown as { __ictPanes?: unknown }).__ictPanes = {
    state: (paneId: string) => {
      const p = panes.get(paneId);
      if (!p) return null;
      const opts = p.chart.options() as {
        timeScale?: { tickMarkFormatter?: (t: unknown, k: number, l: string) => string | null };
        localization?: { timeFormatter?: (t: unknown) => string };
      };
      const tickFmt = opts.timeScale?.tickMarkFormatter;
      const timeFmt = opts.localization?.timeFormatter;
      // Up to 12 evenly spaced bars: enough to cover a day boundary on any pane, few enough to
      // keep the report readable. `TickMarkType.Time` (3) is what an intraday axis asks for and
      // `DayOfMonth` (2) what a daily one does, so each pane is sampled at its own granularity.
      const kind = needsClockOnAxis(p.tf) ? 3 : 2;
      const step = Math.max(1, Math.floor(p.bars.length / 12));
      const ticks: { time: number; label: string | null }[] = [];
      for (let i = 0; i < p.bars.length && ticks.length < 12; i += step) {
        const t = p.bars[i].time;
        ticks.push({ time: t, label: tickFmt ? tickFmt(t, kind, 'en-US') : null });
      }
      const last = p.bars.length ? p.bars[p.bars.length - 1].time : null;
      return {
        tf: p.tf,
        bars: p.bars.length,
        firstTime: p.bars.length ? p.bars[0].time : null,
        lastTime: last,
        // NULL when the hook is absent, deliberately: that is the state this proof exists to
        // detect, and an empty string would read as "formatted to nothing".
        hasTickFormatter: typeof tickFmt === 'function',
        hasTimeFormatter: typeof timeFmt === 'function',
        ticks,
        crosshairSample: timeFmt && last !== null ? timeFmt(last) : null,
        zoneTag: p.zoneSlot?.textContent ?? null,
        zoneTitle: p.zoneSlot?.title ?? null,
        // The time range on screen (t_75c35d19: a label jump frames its whole window); null when
        // the scale has no range yet.
        visible: (() => {
          const r = p.chart.timeScale().getVisibleRange();
          return r ? { from: Number(r.from), to: Number(r.to) } : null;
        })(),
      };
    },
    /**
     * THE TEXT THE PRICE AXIS PAINTS for this pane's last close (card `t_ccfe3378`).
     *
     * `series.priceFormatter()` is the library's OWN formatter for this series -- the function it
     * uses for every right-axis label, the crosshair label and the last-price tag -- so this is the
     * user-visible string, not a recomputation. On the unfixed code it returned `0.00` for PUMP.
     * NULL when the pane has no bars. READ-ONLY.
     */
    formatLastClose: (paneId: string): string | null => {
      const p = panes.get(paneId);
      if (!p || !p.bars.length) return null;
      return p.candleSeries.priceFormatter().format(p.bars[p.bars.length - 1].close);
    },
    /** The number `formatLastClose` formatted, so a spec can assert the text lost no digit of it. */
    lastClose: (paneId: string): number | null => {
      const p = panes.get(paneId);
      return p && p.bars.length ? p.bars[p.bars.length - 1].close : null;
    },
    /**
     * THE SERIES A PANE ACTUALLY RENDERED, plus its history-paging state.
     *
     * Added 2026-08-25 (card t_dc8abf16) for `e2e/historypaging.spec.ts`. The pure functions in
     * `history.ts` prove the four paging DECISIONS; only a browser can prove the WIRING — that the
     * range subscription exists on the pane the user drags, that the splice reaches the RENDERED
     * array, and that the forming bar is still last afterwards.
     *
     * `pane.bars` AND NOT `dataByIndex`: this must report what the app believes it drew, which is
     * the array every consumer (the magnet, the OHLC header, `updateBar`) reads. The chart's own
     * view is a second answer, and if the two ever disagreed the bug would be invisible to a hook
     * that asked the chart.
     *
     * A DEEP COPY, so a spec cannot mutate the pane's series by holding onto it. READ-ONLY.
     */
    series: (paneId: string) => {
      const p = panes.get(paneId);
      if (!p) return null;
      const h = historyOf(p);
      return {
        tf: p.tf,
        bars: p.bars.map((b) => ({ ...b })),
        lastBarForming: p.lastBarForming,
        windowFirstTime: p.windowFirstTime,
        history: { ...h },
      };
    },
    /**
     * Put a pane's viewport AT its left edge, as a USER GESTURE would.
     *
     * TWO STEPS, and the first is not padding. Paging is armed by a real input event on the pane's
     * canvas (`HistoryState.armed`, and the measured boot trace behind it), because a range event
     * carries no indication of who caused it and the app's own `fitContent` is indistinguishable
     * from a drag. So this dispatches a `wheel` on the container — the same event a scroll produces
     * and the same listener production code installs — and then moves the range.
     *
     * WHY THE RANGE IS SET THROUGH THE LIBRARY RATHER THAN PANNED WITH POINTER EVENTS:
     * lightweight-charts' pan runs on internal hit-testing that ignores synthetic pointer events
     * (the limitation `__ictTicket` documents for the ticket's legs). `setVisibleLogicalRange` is the
     * library's PUBLIC way to produce the state a drag produces, and it fires the very
     * `subscribeVisibleLogicalRangeChange` handler under test. Nothing about the paging DECISION is
     * reimplemented here — both halves drive production code.
     *
     * A NEGATIVE `from`, deliberately: that is what the library reports once the viewport passes the
     * oldest bar, and it is the exact state a user who has scrolled to the cut edge is in.
     */
    scrollToLeftEdge: (paneId: string) => {
      const p = panes.get(paneId);
      if (!p) return false;
      p.container.dispatchEvent(new WheelEvent('wheel', { deltaY: -120, bubbles: true }));
      p.chart.timeScale().setVisibleLogicalRange({ from: -20, to: 130 });
      return true;
    },
  };
}


/**
 * The right panel's collapse toggle. One button, one behaviour, one remembered preference.
 *
 * ANDREI, 2026-08-05: "the panel from the right can be minimized too, right?" It could not, and on
 * a 3-pane grid the sidebar costs 300px of chart width for text he mostly is not reading.
 *
 * WHAT IS AND IS NOT HERE. This hides the panel's sections, it does not remove them, and there is
 * no settings screen. DEFAULT OPEN, persisted: the state that survives a reload is whichever one he
 * chose, and an absent preference means open. That decision lives in `inspector.ts` where vitest can
 * witness it, for the same reason `oneclick.ts` exists. (The module keeps its name; the per-bar
 * inspector it was named for was deleted on 2026-08-18 while the collapse preference it stores was
 * not, and renaming a localStorage KEY would silently forget every user's saved state.)
 *
 * NO CHART RESIZE CALL. The panes are built with `autoSize: true`, so lightweight-charts observes
 * each `.pane-canvas` and re-measures when the grid column changes -- the mechanism that already
 * covers a window resize and the trade panel's drag. Adding a manual `applyOptions({ width })` here
 * would be a second sizing path fighting the library's own, which is the trade panel's
 * `overflow: hidden` lesson one element over.
 *
 * NOT WITNESSED BY A TEST. This comment claimed `e2e/inspector.spec.ts` asserts the pane canvas is
 * genuinely WIDER after the click; that spec has never existed (9 specs in `chartapp/e2e/`, none of
 * them it). So "the canvas actually resizes" currently rests on the library's `autoSize` contract
 * and on manual use, not on a check. Corrected 2026-08-17 (t_b68b2caa).
 */
function wireInspectorToggle(): void {
  const apply = (collapsed: boolean) => {
    document.body.classList.toggle('insp-collapsed', collapsed);
    el.inspToggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
    // The label has to describe what the NEXT press does, or a collapsed panel offers a button
    // reading "hide panel" -- the state and the action pointing opposite ways.
    el.inspToggle.innerHTML = collapsed ? '\u00ab' : '\u00bb hide panel';
    el.inspToggle.title = collapsed
      ? 'Show the overlays panel'
      : 'Collapse this panel and give the width to the charts';
  };

  apply(inspectorStartsCollapsed(localStorage.getItem(INSPECTOR_COLLAPSED_KEY)));

  el.inspToggle.addEventListener('click', () => {
    const collapsed = !document.body.classList.contains('insp-collapsed');
    apply(collapsed);
    try {
      localStorage.setItem(INSPECTOR_COLLAPSED_KEY, inspectorCollapsedValue(collapsed));
    } catch {
      // Storage blocked (private mode). The toggle still works for this session -- matching
      // `savePaneState`, which made the same call.
    }
  });
}


/**
 * Fetch ONE pane's series, and return `null` if the answer arrived too late to be true.
 *
 * THE ROOT CAUSE OF "changing the ticker does not reload all three panes" (2026-08-03). Every
 * OTHER path that draws server data checks first that the response still describes the chart:
 * `pollLive` has `if (d.asset !== el.asset.value || d.tf !== execTf()) return`, `pollContextPanes`
 * has the same, and `refreshOverlaysOnce` has the per-pane epoch. The two BAR loads had no check at
 * all -- they applied whatever came back.
 *
 * So on two switches close together (or one switch onto a cold Lambda), the FIRST asset's bars can
 * land AFTER the second's. Reproduced in a browser (e2e/panereload.spec.ts): `setBars pane-htf
 * fetchedFor=2Z` at t+4445ms, 2.3s after AAVE's own bars had already been drawn AND painted, leaving
 * 2Z's candles under AAVE's geometry on two of the three panes.
 *
 * NOTE THE EPOCH GUARD IS SATISFIED in that trace and the overlays paint correctly. The geometry is
 * right and the CANDLES are stale, which is why it reads as "that pane did not reload" and why a
 * hard refresh is the only way out. The cancellation epoch cannot help here: it is bumped by the
 * asset change, so by the time the late response lands the epoch already matches again.
 *
 * NULL RATHER THAN A BOOLEAN OR A THROW: under `strict`, a caller cannot reach `data.candles`
 * without narrowing, so the un-checked shape this bug WAS does not type-check. That is why the
 * payload is returned through here instead of a guard being added at each of the two call sites --
 * the next bar-load path cannot forget.
 *
 * The staleness test covers all three things a URL-plus-response pair depends on: the global ASSET,
 * the global SOURCE (the two feeds diverge by up to 1.38% on BTC 1D), and the PANE's timeframe.
 */
async function fetchPaneBars(pane: ChartPane, tf: string): Promise<BarsResponse | null> {
  const asset = el.asset.value;
  const source = el.source.value;
  // ASK FOR WHAT THIS PANE'S WINDOW NEEDS. `paneBarLimit` returns a count only when the pane's own
  // opening window is longer than the server's 600-bar default -- today only 15m (10 days = 960 bars,
  // where 600 is 6.25 days). Every other pane sends no `limit` and gets the byte-identical response it
  // got before, so the 2026-07-31 payload cut is untouched where it was measured.
  const limit = paneBarLimit(tf);
  // AN ANCHORED WINDOW (t_42cdbde6 U7). A navigator jump sets `pane.around` to the event's time,
  // and the research source serves the window centred on it. Only research reads `around`, so it
  // is sent on no other source; a pane with no anchor sends exactly what it sent before.
  const around = source === RESEARCH ? pane.around : null;
  const token = jumpToken;
  const params: Record<string, number> = {};
  if (limit !== null) params.limit = limit;
  if (around !== null) params.around = around;
  const data = await getJSON<BarsResponse>(
    `/api/bars/${encodeURIComponent(asset)}/${encodeURIComponent(tf)}?${q(params)}`,
  );
  if (el.asset.value !== asset || el.source.value !== source || pane.tf !== tf) return null;
  // A pane torn down while the fetch was in flight (261019-pane-layout): its chart is disposed.
  if (!paneAlive(pane)) return null;
  // A later jump (or a manual change that cleared the anchor) owns this pane now.
  if ((source === RESEARCH ? pane.around : null) !== around || jumpToken !== token) return null;
  // WHICH SERIES THIS PANE IS ACTUALLY SHOWING, published on the pane element.
  //
  // A PERMANENT diagnostic, kept for the same reason as `overlayComputedAt`'s "computed at bar N":
  // before this, nothing on the page distinguished "this pane reloaded" from "this pane is still
  // holding the previous ticker's candles" -- which is precisely the ambiguity that let this bug
  // live. The pane HEADER shows the timeframe the user selected, not the one the drawn bars came
  // from, so it says nothing when a load is stale. One dataset write, and both the e2e gate and a
  // human in devtools can read the truth.
  const cell = document.querySelector(`[data-pane="${pane.id}"]`) as HTMLElement | null;
  if (cell) cell.dataset.series = seriesKey(asset, tf);
  return data;
}

/**
 * Fetch and render ONE pane's series.
 *
 * The per-pane twin of `loadBars`. The asset and source are global; the timeframe is the pane's.
 * Deliberately does NOT touch the shared status line or rollover state — those are
 * owned by the execution pane's load, and three panes writing the same status line would produce
 * whichever finished last rather than the one the user changed.
 */
async function loadPaneBars(pane: ChartPane): Promise<void> {
  const asset = el.asset.value;
  const tf = pane.tf;
  if (!asset || !tf) return;
  // CLEAR BEFORE LOADING, not only on failure (2026-07-31). Andrei clicked SOL and saw BTC's swing
  // markers and dealing-range levels still painted while SOL's bars loaded underneath. The timeframe
  // handler called `clearOverlays` but this function only did so in its catch, so an ASSET change
  // left the previous symbol's geometry on screen — price levels that look computed and belong to a
  // different market. Clearing here covers every caller instead of every call site remembering to.
  repaintFvg.delete(pane.id);
  pane.clearOverlays();
  // AND FORGET THIS PANE'S PAGING. A fresh series means a fresh left edge: an `exhausted` flag from
  // the previous asset or timeframe would make this one look hard-cut for a reason belonging to a
  // series that is gone, and the page ceiling is a per-series budget rather than a per-session one.
  resetHistory(pane);
  renderPaneHistory(pane);
  // AND FORGET ITS HELD OVERLAY ANSWERS. Not for correctness -- the answer key carries the asset and
  // the timeframe in its url, so an answer for the previous series can never match -- but because
  // this is where the series is REPLACED, and an unbounded map of answers for markets no longer on
  // screen is a leak in a tab left open all day. Same reason and same place as `resetHistory`.
  overlayAnswers.forget(pane.id);
  // A TIMEFRAME THE SOURCE DOES NOT SERVE IS NOT ASKED FOR (t_f169b3ae, 2026-10-16). The research
  // source serves 1m..1D, and `pane-htf` defaults to 1W, so every research load and every navigator
  // jump sent `/api/bars/<asset>/1W?source=research`, which answers 400 and logged a console error
  // each time (t_8589f7fe U7 probe). The pane is left empty and says why, which is what the 400 path
  // already drew minus the request; the pane keeps its timeframe, so a switch back to a feed that
  // has 1W draws it again.
  const served = currentSource()?.timeframes;
  if (served && !served.includes(tf)) {
    pane.setBars([]);
    pane.lastBarForming = false;
    renderHeader(pane);
    const slot = document.querySelector<HTMLElement>(`[data-ohlc="${pane.id}"]`);
    if (slot) slot.textContent = `${tf}: not served by the ${el.source.value} source`;
    return;
  }
  try {
    const data = await fetchPaneBars(pane, tf);
    // STALE: the asset, source or this pane's timeframe changed while the fetch was in flight, so a
    // newer load already owns this pane. Returning leaves the newer load's candles alone -- drawing
    // these would overwrite them with the previous market's, which is the reported bug.
    if (!data) return;
    pane.setBars(
      data.candles.map((c) => ({
        time: c.time as unknown as number,
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close,
      })),
    );
    // Record the window BEFORE the overlay refresh, or the first run asks for the full history.
    pane.windowFirstTime = data.meta.window_first_time ?? null;
    pane.windowLastTime = data.meta.window_last_time ?? null;
    // The asset's price precision, on THIS pane's series -- every load, because the series is
    // reused across assets (card `t_ccfe3378`; see `ChartPane.setPriceDecimals`).
    pane.setPriceDecimals(precisionFor(data.meta, data.candles.map((c) => c.close)));
    // The server's own boundary, not a wall-clock guess — see `ChartPane.lastBarForming`.
    pane.lastBarForming = data.meta.has_forming_bar === true;
    renderHeader(pane);
    // BOTH AXES, THIS PANE, same call the execution pane makes. This line was
    // `pane.chart.timeScale().fitContent()` -- time only -- while the price scale was re-armed
    // for the execution pane alone by `resetChartLayout`. So switching BTC (64000) to SOL (73)
    // left the 1W and 1D panes on BTC's price window and SOL's candles outside it: Andrei's
    // "the pane scale sometimes does not change in such a way that I can see bars". One
    // function, called by every pane's load, rather than two-thirds of the reset in each.
    resetChartLayout(pane);
    // THIS PANE's hand-drawn layer, for THIS PANE's timeframe. Every timeframe change routes
    // through here (the pane header's `<select>`), and `clearOverlays` above already emptied the
    // layer -- so without this refetch a pane switched 1W -> 15m would show no drawings at all
    // until a reload, and without the clear it would show 1W's on 15m bars.
    void loadDrawings(pane);
    await refreshOverlays(pane);
    // AND THE LEVELS AGAIN. `clearOverlays()` above emptied this pane's `levels` primitive --
    // correctly, the previous market's geometry must not survive -- so without this the context
    // panes would lose the entry/stop/target on every timeframe change and on every asset change
    // that keeps the ticket (loading a setup does exactly that). Redrawing from state rather than
    // caching what was drawn keeps one source of truth.
    //
    // `tpDrawLevels`, NOT `tkDrawLines`: this line was the latter, which redraws the TICKET's legs
    // only -- so a pane rebuilt while the ticket was empty came back with the live position's
    // entry/SL/TP missing, which is the very bug the context levels were added to fix. It
    // delegates to `tkDrawLines` whenever the ticket holds prices, so the ticket path is unchanged.
    tpDrawLevels();
  } catch (err) {
    // A torn-down pane has nothing to blank and no status to report: its chart is disposed, and
    // `setBars` on it would throw out of this catch (261019-pane-layout FR-2.6).
    if (!paneAlive(pane)) return;
    // Per-pane failure is per-pane: one dead timeframe must not blank the other two. The pane is
    // left EMPTY rather than showing the previous timeframe's bars, because stale candles under a
    // new timeframe label is a confidently wrong chart.
    pane.setBars([]);
    pane.lastBarForming = false;
    renderHeader(pane);
    repaintFvg.delete(pane.id);
    pane.clearOverlays();
    setStatus(`${asset} ${pane.tf} failed: ${err}`);
  }
}


/**
 * Load every CONTEXT pane (all but the execution one).
 *
 * The execution pane is loaded by `loadBars`, which additionally owns the shared status line,
 * rollover state and the trade panel's levels. Splitting them means an asset change
 * refreshes all three charts while only one writes the shared UI — three panes racing to set the
 * status line would show whichever finished last rather than what the user asked for.
 *
 * Concurrent, not sequential: three cold series is 2.2s serially versus one round trip in parallel,
 * and they share no mutable state. `allSettled` because one dead timeframe must not blank the
 * others — `loadPaneBars` already reports its own failure per pane.
 */
async function loadContextPanes(): Promise<void> {
  await Promise.allSettled(
    [...panes.values()].filter((p) => p.id !== EXECUTION_PANE_ID).map((p) => loadPaneBars(p)),
  );
}

async function loadBars() {
  const asset = el.asset.value;
  const tf = execTf();
  if (!asset || !tf) return;
  stopLive();
  // Any overlay run still in flight belongs to the PREVIOUS series (or to a bar
  // close on it). Retire it here — the single funnel every series change goes
  // through — so it cannot draw its geometry onto the series we are about to load.
  invalidateOverlays();
  // And drop what is ALREADY drawn. `invalidateOverlays` stops a pending run from painting; it does
  // not erase geometry already on the chart. On an asset change that left BTC's markers over SOL's
  // bars — the bug Andrei reported.
  activePane().clearOverlays();
  // Same reason as in `loadPaneBars`: a new series has a new left edge, so the paging state of the
  // one being replaced must not survive onto it.
  resetHistory(activePane());
  renderPaneHistory(activePane());
  rollover = initialRolloverState();
  setStatus(`loading ${asset} ${tf}…`);
  try {
    const data = await fetchPaneBars(activePane(), tf);
    // STALE, same contract and the same bug as in `loadPaneBars`: a late response for the previous
    // market must not overwrite the candles a newer load already drew. `setStatus` is left as the
    // newer load set it -- this run no longer owns the shared UI either.
    if (!data) return;
    // THROUGH THE PANE, not `candleSeries.setData` directly. `candleSeries` is an alias to the
    // EXECUTION pane's series (see `initPanes`), so a raw `setData` here bypassed `ChartPane.setBars`
    // — and with it the inside-candle tint. That is why Andrei still saw every bar bright green/red
    // after two attempts at the colours: the context panes were tinted, the pane he actually watches
    // was loaded by a different code path. Two load paths, one of them tinted.
    //
    // `setBars` also keeps `pane.bars` truthful, which `updateBar` reads to find a live bar's
    // predecessor. Setting the series without it left that list stale on every asset switch.
    activePane().setBars(
      data.candles.map((c) => ({
        time: c.time as unknown as number,
        open: c.open, high: c.high, low: c.low, close: c.close,
      })),
    );
    candleTimes = data.candles.map((c) => c.time);
    candleBars = data.candles.map((c) => ({
      time: c.time as unknown as number,
      open: c.open, high: c.high, low: c.low, close: c.close,
    }));
    // The forming bar is DRAWN but not readable by any detector, so the bar NUMBER quoted by
    // the overlay-provenance strings must count closed bars only. Falling back to the full
    // length is correct for bars_cache, which reports no forming bar.
    closedBarCount = Number(data.meta.closed_bars_count ?? data.candles.length);
    // Same window contract as loadPaneBars — the execution pane is a pane too. `closedBarCount` is
    // NOT reused for the header: it is this pane's alone, and the header exists on all three.
    activePane().lastBarForming = data.meta.has_forming_bar === true;
    renderHeader(activePane());
    activePane().windowFirstTime = data.meta.window_first_time ?? null;
    activePane().windowLastTime = data.meta.window_last_time ?? null;
    // Same precision contract as `loadPaneBars` -- the execution pane is a pane too.
    activePane().setPriceDecimals(precisionFor(data.meta, data.candles.map((c) => c.close)));
    resetChartLayout(activePane());
    renderMeta(data.meta);
    const liveNote = data.meta.has_forming_bar ? ' (last bar forming)' : '';
    setStatus(`${asset} ${tf} — ${data.candles.length} bars${liveNote}`);
    await refreshOverlays();
    // Date the initial geometry too, so the live box reads "computed at bar N"
    // from the first paint rather than only after the first rollover.
    overlayComputedAt = { bar: closedBarCount, at: Date.now(), pending: false };
    // The panel's own levels are attached to this series, so a series reload drops
    // them. Redraw from the snapshot we already have rather than re-fetching it.
    tpDrawLevels();
    tpRenderChrome();
    // The hand-drawn layer is keyed by (asset, tf) on the server, so it refetches with the series.
    // THIS pane only: the other two are loaded by `loadPaneBars`, which refetches their layers for
    // their own timeframes. Not awaited, so a slow drawings read cannot delay the candles, and the
    // layer is cleared first inside `loadDrawings` so a failure leaves it empty rather than showing
    // the previous instrument's marks.
    void loadDrawings(activePane());
    startLive();
  } catch (err) {
    setStatus(String(err), true);
  }
}

/** Sidebar key/value rows. A row whose key names a graded object renders in a colour that
 *  GROUPS it: "bos*"/"choch*" in the break colours, which those chart segments still carry,
 *  so for them the row does double as a legend. "major*" does NOT any more — the degree-2
 *  yellow left the pivots on 2026-08-23 (t_13260999, degree moved to the zigzag's vertex
 *  radius), so that tint groups the major rows and points at no mark on the chart.
 *
 *  A row with the EMPTY key is a NOTE: full-width prose spanning both grid columns, in a
 *  `<details>` COLLAPSED by default (see `#overlay-summary details.note`). Its value is
 *  `"<summary label>|<body>"`.
 *
 *  Both halves of that shape were forced by measurement, not preference. The API's own
 *  `labels_note` / `breaks_note` are five-sentence disclosures: in the `auto` value
 *  column they are an unreadable ribbon, which is part of why `breaks_note` was served,
 *  typed, asserted by a test and never printed at all (t_3b10b86d). Printing them OPEN
 *  then measured unreachable — the sidebar is 327px tall holding 1,926px of content, so
 *  the notes started 332px and 484px BELOW its visible bottom. Collapsed, each costs one
 *  line and is one click from being read. The summary states WHAT is hidden, never a bare
 *  "more", so a closed note is not a mystery.
 *
 *  A key ending in `*inert` renders warn-coloured, so a row measured non-informative
 *  cannot sit beside the honest read looking equally authoritative. */
const kv = (rows: [string, string][]) =>
  rows
    .map(([k, v]) => {
      if (k === '') {
        const cut = v.indexOf('|');
        const label = cut < 0 ? 'why' : v.slice(0, cut);
        const body = cut < 0 ? v : v.slice(cut + 1);
        return `<details class="note"><summary>${label}</summary><p>${body}</p></details>`;
      }
      const cls = k.startsWith('major')
        ? ' class="major"'
        : k.startsWith('bos')
          ? ' class="bos"'
          : k.startsWith('choch')
            ? ' class="choch"'
            : k.includes('inert')
              ? ' class="inert"'
              : '';
      return `<span${cls}>${k}</span><span${cls}>${v}</span>`;
    })
    .join('');

// `markerState`, `priceLines`, `redrawMarkers()` and the price-line helper MOVED TO ChartPane
// (2026-07-31). They were module globals while `redrawMarkers` wrote to the single global
// `candleSeries`, so with three panes every pane's overlay run merged into ONE list and painted it
// on ONE chart. That is the marker flood Andrei saw on 1D, and the reason the swings checkbox
// looked dead on 1W/1D: those panes computed their markers and a later pane's run clobbered them.

/** The user's hand-drawn marks MOVED TO ChartPane TOO (2026-08-03), for the same reason and one
 *  step further: `const drawPrimitive = new DrawingPrimitive()` lived here and `initChart` attached
 *  it to the EXECUTION pane's series only, so the 1W and 1D panes had no drawing layer and no click
 *  subscription -- every drawing tool was dead on two of three panes. There is now no module-level
 *  primitive to attach to the wrong series; see `pane.drawings` and the DRAWING LAYER section. */

function redrawPriceLines(
  pane: ChartPane,
  specs: { price: number; color: string; title: string }[],
) {
  // PER PANE (2026-07-31). `priceLines` was a module global and this removed lines from the single
  // global series, so one pane's refresh deleted another pane's FVG/liquidity lines.
  for (const l of pane.priceLines) pane.candleSeries.removePriceLine(l);
  pane.priceLines = specs.map((s) =>
    pane.candleSeries.createPriceLine({
      price: s.price,
      color: s.color,
      lineWidth: 1,
      lineStyle: LineStyle.Dashed,
      // TRADE-OFF, measured both ways. lightweight-charts only renders `title`
      // as part of the axis label, so titles and a clear right edge are mutually
      // exclusive: with axisLabelVisible=true, 12 CE levels + 5 range levels
      // stacked into a 14-box column that covered the most recent ~40 bars — the
      // ones you most want to see. Clear price action wins; the titles stay in
      // the payload and in the sidebar summary, which is where you read numbers.
      axisLabelVisible: false,
      title: s.title,
    }),
  );
}

/** Fetch + draw every enabled overlay. All geometry comes from the API.
 *
 *  SERIALISED, not re-entrant. This awaits up to ~7 sequential fetches while
 *  mutating shared draw state (`markerState`, the range/break primitives), so two
 *  concurrent runs — a bar-close recompute landing on top of a checkbox toggle —
 *  would interleave their writes and draw a blend of two answers.
 *  `overlayRefresh` (below) makes a second caller wait for the first instead of racing it;
 *  `overlayEpoch` makes a run that started before an asset/tf switch discard its own draw, the
 *  same guard `pollLive` applies to bars.
 */
/**
 * One refresh SCHEDULER for every pane, serialised per pane. Card t_10c01825.
 *
 * WAS a `Map<string, Promise<void>>` chain here, which serialised correctly and COALESCED NOTHING:
 * N requests to refresh one pane queued N full passes over the wire, each re-fetching URLs
 * byte-identical to the previous pass's. Measured (`process/reviews/t_10c01825/FINDINGS.md`): a 15m
 * checkbox toggled on then off 150 ms apart -- ending in the state it started in -- issued 7 overlay
 * calls for 4 distinct URLs, and two knob changes 200 ms apart issued 18 for 9. On 15m a layer costs
 * a median 2533 ms server-side, so each superseded pass is seconds of waste.
 *
 * The scheduler drops SUPERSEDED PASSES and caches NO RESPONSE, which is what keeps it clear of the
 * card's prohibition (and of t_420df4d4's week-box D1/D2): every pass that runs reads
 * `pane.windowFirstTime`, the knobs and the toggles fresh, so a level depending on the forming bar
 * stays as live as before. See `overlayrefresh.ts` for the property that makes dropping a pass safe.
 */
const overlayRefresh = new OverlayRefreshScheduler((paneId) => {
  const pane = panes.get(paneId);
  // A pane removed between the request and its pass has nothing to draw. Reachable since
  // 2026-10-19 (261019-pane-layout): the pane-count picker tears panes down live. Looking the id up
  // is cheaper than making the scheduler hold pane objects it would then keep alive.
  return pane === undefined ? Promise.resolve() : refreshOverlaysOnce(pane);
});

/**
 * Overlay answers already fetched for each pane's CURRENT closed-bar set.
 *
 * THE SECOND HALF OF t_10c01825, and the one that produces the saving. The scheduler above stops a
 * BURST costing several passes; this stops a pass that DOES run from re-asking for an answer that
 * cannot have changed. Both are needed: two knob changes 200 ms apart legitimately need two passes
 * (each must read state as of its own moment), and measured, the second pass's nine urls were
 * byte-identical to the first's.
 *
 * NOT A RESPONSE CACHE BY (asset, tf) -- the thing the card forbids. The key carries the pane's
 * window AND its last CLOSED bar, and `process/reviews/t_10c01825/prove_closed_bar_answers.py`
 * EXECUTES the reason that is sufficient: a forming bar 20% outside the series moved 0 of 7 overlay
 * payloads on BTC/15m and BTC/1D, while the same bar served as CLOSED moved 6 of 7. So a bar closing
 * invalidates everything, a splice invalidates everything, and the live candle is unaffected because
 * no detector ever read it.
 */
const overlayAnswers = new OverlayAnswerStore<unknown>();
/**
 * A cancellation epoch PER PANE.
 *
 * WHY NOT ONE GLOBAL COUNTER, which is what this was: `invalidateOverlays()` is called from
 * `loadBars`, i.e. the EXECUTION pane's load. With a single counter that bump cancelled in-flight
 * runs on the 1W and 1D panes too — so once the panes refresh in parallel, loading the execution pane
 * would silently discard the other two panes' overlays and leave them bare. The epoch's job is "this
 * pane moved to a different series, so retire work aimed at the old one"; that is a per-pane fact.
 *
 * An ASSET change still invalidates every built pane, because it calls `invalidateAllOverlays()`.
 * A torn-down pane's epoch is bumped, not deleted (`teardownPane`), so its in-flight pass is retired.
 */
const paneEpoch = new Map<string, number>();
// Full FVG candidates and sibling geometry are retained by each repaint callback.
// Live wick touches retire existing candidates before selection; only closed-bar
// detector refreshes confirm new candidates or close-based inversions.
const repaintFvg = new Map<string, () => void>();

/**
 * Scroll-left history paging state, PER PANE.
 *
 * Per pane because each pane holds its own series and therefore its own left edge — the same reason
 * `markers`, `drawState` and `windowFirstTime` are per pane in `ChartPane`. A module-global here
 * would repeat the measured clobber bug those fields were split to fix: one pane exhausting the
 * cache on 1D would silently stop the 15m pane, whose cache is 52 days deep, from paging at all.
 *
 * All four decisions this map feeds are pure functions in `history.ts`, tested by gate 5. See that
 * module's docstring for why: a splice living inside a `subscribeVisibleLogicalRangeChange`
 * callback is structurally unreachable by vitest, which is exactly the shape of the 2026-07-27
 * frontend-state bug that gate exists for.
 */
const paneHistory = new Map<string, HistoryState>();

function historyOf(pane: ChartPane): HistoryState {
  let s = paneHistory.get(pane.id);
  if (s === undefined) {
    s = initialHistoryState();
    paneHistory.set(pane.id, s);
  }
  return s;
}

/**
 * Forget this pane's paging, because its SERIES is about to change.
 *
 * Called from every bar-load path for the same reason `clearOverlays` is: a pane that had reached
 * the cache edge on BTC/1D must be free to page again on SOL/15m, and an `exhausted` flag carried
 * across an asset or timeframe change would make the new series look hard-cut for a reason that
 * belonged to the old one. It is also what makes the page CEILING a per-series budget rather than a
 * per-session one — reloading a pane starts over, which `historyNote` says out loud.
 */
function resetHistory(pane: ChartPane): void {
  paneHistory.set(pane.id, initialHistoryState());
}

/**
 * Write (or remove) THIS pane's one-line history-paging disclosure.
 *
 * Same sink and same contract as `renderPaneWeekly` / `renderPaneError`: a span in the pane's OWN
 * bar, REMOVED rather than blanked when there is nothing to say. Nothing is printed in the ordinary
 * case (more history available) — a row that is always there is read as chrome and stops being
 * information, which is the standard `.hermes.md` §11b settled on when the weekly profile's
 * permanent grey furniture was deleted.
 */
function renderPaneHistory(pane: ChartPane): void {
  const bar = document.querySelector(`[data-pane="${pane.id}"] .pane-bar`);
  if (!bar) return;
  bar.querySelector('.pane-hist')?.remove();
  const note = historyNote(historyOf(pane));
  if (note === null) return;
  const slot = document.createElement('span');
  slot.className = 'pane-hist';
  slot.textContent = note.text;
  slot.title = note.title;
  bar.insertBefore(slot, bar.querySelector('.pane-ohlc'));
}

/** One page of history for one pane. */
type HistoryPage = {
  candles: { time: number; open: number; high: number; low: number; close: number }[];
  has_more: boolean;
  next_before: number | null;
};

/**
 * Fetch ONE page of older bars and splice it onto the front of this pane's series.
 *
 * WHAT THIS MUST NOT BREAK, in the order the card names it:
 *
 *   * THE FORMING BAR. `mergeOlder` returns `existing` as its tail untouched and only ever prepends
 *     rows strictly older than `existing[0]`, so the live candle `pollLive`/`updateBar` writes at
 *     the right edge cannot be replaced or reordered by a history page. The server half is
 *     structural too: `/history` reads the bar cache, which stores CLOSED bars only.
 *   * ORDER AND DUPLICATES at the join — `mergeOlder`'s whole job, asserted in `history.test.ts`.
 *   * THE VIEWPORT. `setBars` is a `setData`, which keeps the visible LOGICAL range; prepending N
 *     bars renumbers every existing bar by +N, so without `shiftedRange` the chart jumps back a
 *     whole page the instant the data lands. The range is read BEFORE the splice and re-aimed
 *     after.
 *   * OVERLAYS DRAWN AGAINST THE OLD SERIES. Every detector row carries `idx`/`confirm_idx`, offsets
 *     into the bar array, and prepending bars shifts every one of them. So the geometry is CLEARED
 *     immediately and recomputed against the longer window — `.hermes.md` calls that recompute
 *     normal and expected, but geometry surviving the splice for even one frame is the WEEKBOX_REDTEAM
 *     D1 class (drawn lagging computed). `refreshOverlays` re-sends `first_time` from the pane's own
 *     new `windowFirstTime`, so the indices it gets back match the array it will be drawn on.
 *   * THE STALENESS CONTRACT. Asset, source and this pane's timeframe are re-checked after the
 *     await, exactly as `fetchPaneBars` does: a late page for the previous market must not be
 *     spliced onto the new one. The pane EPOCH is checked too, so a page in flight when the series
 *     was replaced is discarded rather than prepended to a series it does not belong to.
 */
async function loadOlderBars(pane: ChartPane): Promise<void> {
  const state = historyOf(pane);
  if (state.loading || state.exhausted) return;
  const bars = pane.bars;
  if (bars.length === 0) return;

  const asset = el.asset.value;
  const source = el.source.value;
  const tf = pane.tf;
  const epoch = epochOf(pane);
  // THE CURSOR IS THE FIRST BAR WE HOLD, and `before` is exclusive on the server, so the page joins
  // onto it with no gap and no duplicate. Taken from `pane.bars` — what was actually rendered —
  // rather than from a remembered cursor: a second copy of "where does my series start" is how the
  // two would drift, and `pane.bars` is the array the splice is about to happen on.
  const before = bars[0].time;

  paneHistory.set(pane.id, { ...state, loading: true });
  renderPaneHistory(pane);
  try {
    const page = await getJSON<HistoryPage>(
      `/api/bars/${encodeURIComponent(asset)}/${encodeURIComponent(tf)}/history?${q({ before })}`,
    );
    if (el.asset.value !== asset || el.source.value !== source || pane.tf !== tf) return;
    if (epochOf(pane) !== epoch || !paneAlive(pane)) return;

    // READ THE VIEWPORT BEFORE THE SPLICE. After `setBars` the logical indices mean something else.
    const range = pane.chart.timeScale().getVisibleLogicalRange();
    const merged = mergeOlder(pane.bars, page.candles);
    const added = merged.length - pane.bars.length;
    if (added > 0) {
      // CLEAR FIRST, DRAW LATER. Every overlay row is indexed into the bar array; prepending shifts
      // all of them, so geometry left on screen for even one frame is drawn against a series that no
      // longer exists at those indices.
      repaintFvg.delete(pane.id);
      pane.clearOverlays();
      pane.setBars(merged);
      // The pane's window moved LEFT, so the overlay routes must be asked for the new one or their
      // `idx` values are offset from the array they will be drawn on.
      pane.windowFirstTime = merged[0].time;
      const aimed = shiftedRange(range === null ? null : { from: range.from, to: range.to }, added);
      if (aimed !== null) pane.chart.timeScale().setVisibleLogicalRange(aimed);
      renderHeader(pane);
      // THE EXECUTION PANE HAS TWO MORE MIRRORS OF ITS SERIES, and both must grow with it. Neither
      // is decorative: `candleTimes` maps a clicked timestamp back to a bar INDEX, and `candleBars`
      // is what the drawing MAGNET snaps to. Left short, a click on a newly loaded bar would resolve
      // to index -1 and the magnet would refuse to snap anywhere left of the old first bar — i.e.
      // exactly the region this feature exists to make usable. Derived from `merged`, the one array
      // that was actually rendered, so there is no third spelling of the series to keep in step.
      if (pane.id === EXECUTION_PANE_ID) {
        candleTimes = merged.map((b) => b.time as UTCTimestamp);
        candleBars = merged.map((b) => ({
          time: b.time, open: b.open, high: b.high, low: b.low, close: b.close,
        }));
        // AND THE CLOSED-BAR COUNT, which is what the overlay-provenance strings quote ("computed
        // at bar N"). It is an index into the SAME array, so prepending N bars moves it by N.
        // Recomputed from position rather than incremented, exactly as `pollLive` does it: the
        // forming bar is the last element when there is one, so everything before it is closed.
        // Left stale, the next rollover would print a bar number a whole page short — a provenance
        // string that is confidently wrong is worse than none, which is the reason it exists.
        closedBarCount = merged.length - (pane.lastBarForming ? 1 : 0);
      }
    }
    paneHistory.set(pane.id, advanceHistory(historyOf(pane), {
      added,
      serverHasMore: page.has_more === true,
    }));
    renderPaneHistory(pane);
    if (added > 0) {
      await refreshOverlays(pane);
      // THE TWO THINGS `clearOverlays()` EMPTIES AND `refreshOverlays` DOES NOT WRITE BACK.
      // Measured rather than reasoned (redteam F1 on this card): `clearOverlays` empties six
      // primitives -- range, breaks, levels, drawings, priceLines, markers -- and
      // `refreshOverlaysOnce` writes back four. `pane.levels` (the LIVE POSITION's entry/SL/TP) and
      // `pane.drawings` (every hand-drawn line, rect and fib) are written by nobody on this path,
      // so one scroll left used to delete both.
      //
      // NEITHER COMES BACK ON ITS OWN, which is why this is not cosmetic. `loadDrawings` is
      // reachable from three call sites and none of them is a splice, so a drawing was gone until
      // the pane was reloaded. `tpDrawLevels` returns only via `liveLevels.subscribe` (a splice
      // emits no such event) or the 15s `tpLoad` poll, which is gated on `accountHealth.ok`, the
      // poll toggle and `tp-collapsed` -- so with the panel collapsed the live stop was absent
      // from the chart indefinitely, and a missing price line is indistinguishable from "no
      // order" (the failure class `tpDrawLevels`' own off-screen disclosure exists to prevent).
      //
      // SAME TWO CALLS, SAME ORDER AS `loadPaneBars`, which states the rule for each in its own
      // words at the point it makes them; this path took its `clearOverlays()` and not its
      // restores. `tpDrawLevels` covers every pane in one call -- it writes the execution pane's
      // `levels` directly and delegates 1W/1D to `tkDrawLines`.
      void loadDrawings(pane);
      tpDrawLevels();
    }
  } catch (err) {
    // A FAILED PAGE IS NOT AN EXHAUSTED PANE. The series already on screen is untouched and correct,
    // so this must stay retryable — marking it exhausted would turn one transport blip into a
    // permanently hard-cut chart. `loading` is cleared so the next scroll tries again; the failure
    // goes to the shared status line, which is where every other per-pane fetch failure reports.
    // A torn-down pane is not retried and must not get its paging entry back (`historyOf` re-creates).
    if (!paneAlive(pane)) return;
    paneHistory.set(pane.id, { ...historyOf(pane), loading: false });
    renderPaneHistory(pane);
    setStatus(`${asset} ${tf} history: ${err}`, true);
  }
}

function epochOf(pane: ChartPane): number {
  return paneEpoch.get(pane.id) ?? 0;
}

/** Retire in-flight overlay work for ONE pane (its series is about to change). */
function invalidateOverlays(pane: ChartPane = activePane()) {
  paneEpoch.set(pane.id, epochOf(pane) + 1);
  repaintFvg.delete(pane.id);
  // AND FORGET ITS HELD ANSWERS, in the same function, for the same reason the epoch is bumped: the
  // pane is moving to a DIFFERENT SERIES. The answer key would already refuse a match (the url
  // carries asset and timeframe, and both `first_time` and the last closed bar move), so this is the
  // second of two independent guards -- and it is the one that stops a switch away and back within
  // one bar from re-applying the first market's geometry.
  overlayAnswers.forget(pane.id);
}

/** Retire in-flight overlay work for EVERY pane — used on an asset or source change, which moves
 *  all three panes to a different market at once. */
function invalidateAllOverlays() {
  for (const p of panes.values()) invalidateOverlays(p);
}

/**
 * Write (or clear) THIS pane's ONE-LINE weekly-profile row, in ITS OWN pane bar.
 *
 * NO NEW PANE, NO TABLE, AND SINCE 2026-08-24 NO GEOMETRY AND NO EXPAND (cards t_fdebc065,
 * t_70188040). Andrei: "this indicator should not overwhelm my UI panes even more", of the
 * reference indicator's stats table: "I don't like this table, which I don't know how to use",
 * and finally "weekly profile нифига не работает и хочется его вообще удалить". Ten of the twelve
 * profiles were deleted as measured-worse-than-the-calendar, and with them the week box, the
 * labelled rays and the survivor/eliminated list. What is left is ONE line, and only when a
 * profile is LIVE.
 *
 * A ROW ONLY WHEN THERE IS SOMETHING TO SAY. A forming week (the usual state) and a 1W/1M pane
 * both render NOTHING now -- previously they printed permanent furniture ("week forming", "n/a on
 * 1W"), which is exactly the clutter this card removed. `weeklyLine` returning `null` is that
 * decision, made where vitest can witness it.
 *
 * It writes into the pane bar beside the OHLC header -- the same sink `renderPaneError` and
 * `renderPaneZoneView` use, and for the same reason: `#overlay-summary` is ONE shared box
 * whichever pane refreshes LAST overwrites, while the weekly read is per pane.
 */
function renderPaneWeekly(pane: ChartPane): void {
  const bar = document.querySelector(`[data-pane="${pane.id}"] .pane-bar`);
  if (!bar) return;
  bar.querySelector('.pane-wk')?.remove();
  const state = weeklyState.get(pane.id);
  if (!state) return;
  const line = weeklyLine(state);
  if (line === null) return;
  const slot = document.createElement('span');
  slot.className = 'pane-wk pane-wk-profile';
  // THE TEXT LIVES IN ITS OWN SPAN so the CSS can clip it without clipping anything beside it.
  // The measured defect: a 544px `nowrap` line inside a 500px pane bar pushed the control that
  // used to sit here to x=493-550, under the NEXT pane's canvas.
  const txt = document.createElement('span');
  txt.className = 'pane-wk-txt';
  txt.textContent = line.text;
  slot.appendChild(txt);
  // ON THE ROW, not on the clipped span: the hover text is where the provenance lives, and an
  // ellipsised span is exactly when it is needed.
  slot.title = line.title;
  bar.insertBefore(slot, bar.querySelector('.pane-ohlc'));
}

/**
 * Re-aim the zigzag's dashed tail at THIS PANE'S candles. Called after every candle write.
 *
 * THE SAME DEFECT AND FIX THE (now deleted) week box had, and it is the decision block's explicit
 * consequence for card t_b87831db: "the dashed segment's far endpoint tracks the LIVE candle,
 * never the detector refresh". `refreshOverlays` is the only writer of the zigzag itself and
 * the live loop calls it on a bar-close transition only, while `updateBar` moves the forming
 * bar's extremes every cadence — so the tail reached the last CLOSED extreme. The measured
 * magnitude lives in ONE place, `zigzagLiveTip`'s docstring, beside the probe that produced it.
 * See
 * `zigzagLiveTip` for why the payload cannot supply this (`_open_leg` is closed-bars-only by
 * audit HA-5) and for why moving a dashed, hollow-tipped, `?`-labelled line crosses no audit.
 *
 * `setZigzagTip` rather than a `setZigzag` rebuild, for the same reason: this
 * call site knows only the tail, and a whole-primitive write from here would erase the break
 * segments and every settled vertex.
 */
function paintZigzagTip(pane: ChartPane): void {
  const z = pane.breaks.zigzag;
  if (z) pane.breaks.setZigzagTip(zigzagLiveTip(z, pane.bars));
}

/** Fetch this pane's current-week verdict. A refused timeframe stores nothing and prints nothing.
 *
 *  HELD LIKE EVERY OTHER OVERLAY ANSWER (t_42cdbde6, 2026-10-14): `keyFor` is the pass's own
 *  (url, window, last closed bar) key, so a redraw that changed no bar re-reads nothing and a bar
 *  close still refetches. Before this, every `refreshOverlays` pass asked again -- the experiment
 *  e2e caught a navigator filter change making a `/api/weekly` request (NFR1.3, BR2.6: zero). */
async function loadWeekly(pane: ChartPane, keyFor: (url: string) => OverlayAnswerKey): Promise<void> {
  const asset = el.asset.value;
  if (!asset) return;
  weeklyState.delete(pane.id);
  // A refusal this pane can PREDICT is not fetched: see `weeklyRefused`. On 1W/1M one bar IS the
  // week, so `/api/weekly` answers 503 by design -- and asking for it showed up as a console error
  // on every load of those panes, which took `PostDeploySmokeDev` red against a healthy deploy.
  // Nothing is rendered either way now, so there is no sentence to buy with a round trip.
  if (weeklyRefused(pane.tf)) return;
  const url = `/api/weekly/${encodeURIComponent(asset)}/${encodeURIComponent(pane.tf)}?${q()}`;
  const key = keyFor(url);
  try {
    const held = overlayAnswers.get(pane.id, key) as WeeklyPayload | undefined;
    const answer = held ?? await getJSON<WeeklyPayload>(url);
    if (held === undefined) overlayAnswers.put(pane.id, key, answer);
    weeklyState.set(pane.id, answer);
  } catch {
    // A FAILED READ PRINTS NOTHING, and that is the deliberate choice for this feature only: the
    // row exists to say "a measured profile is live", so its absence already means "no such
    // claim". An error string here would put permanent furniture back on every pane whose read
    // failed -- and unlike a price or a stop, a missing weekly label costs nothing.
  }
}

async function refreshOverlays(pane: ChartPane = activePane()): Promise<void> {
  // SERIALISED PER PANE, NOT PER PROCESS (2026-07-31). I originally wrote one global chain and
  // justified it as needing to hold "ACROSS panes too". That reasoning was WRONG: the chain exists so
  // a slow run cannot paint over a newer run OF THE SAME SERIES, and each pane owns its own series,
  // markers and primitives. Two panes cannot overwrite each other's geometry, so ordering between
  // them buys nothing — it only made them queue.
  //
  // Measured cost of the mistake, live: 6776ms sequential vs 3782ms parallel for a 3-pane load.
  // The ordering guarantee is preserved exactly per pane.
  //
  // AND SINCE 2026-08-27 (card t_10c01825) IT COALESCES. The chain here was
  // `prev.then(() => refreshOverlaysOnce(pane))`, which queued a full pass per caller: a checkbox
  // toggled on then off issued 7 overlay calls for 4 distinct URLs. `overlayRefresh` gives every
  // caller that arrives during a pass ONE shared follow-up instead of one pass each. Nothing is
  // cached and no caller is dropped -- the returned promise still resolves only after a pass that
  // STARTED AFTER this call, which is what keeps the post-splice refresh in `loadOlderBars` real.
  return overlayRefresh.request(pane.id);
}

async function refreshOverlaysOnce(pane: ChartPane = activePane()) {
  const epoch = epochOf(pane);
  // The ASSET is global — all panes show one symbol, which is what ICT multi-timeframe analysis
  // means. The TIMEFRAME and the overlay toggles come from the PANE, so each pane is an
  // independent ICT read rather than three views of the same computation.
  const asset = el.asset.value;
  const tf = pane.tf;
  const on = (id: string) => pane.overlays[id as keyof PaneOverlays];
  const rule = (document.getElementById('inv-rule') as HTMLSelectElement | null)?.value ?? 'traded_through';
  const showConfirm = Boolean((document.getElementById('show-confirm') as HTMLInputElement | null)?.checked);
  const tolAtr = (document.getElementById('tol-atr') as HTMLSelectElement | null)?.value ?? '0.25';
  const minTouches = (document.getElementById('min-touches') as HTMLSelectElement | null)?.value ?? '2';
  // 20 xATR by default -- measured, see `zoneDistanceAtr`. Read the same way as the two above so all
  // three detector knobs behave identically and there is no second source for this number.
  const maxDistAtr = Number(
    (document.getElementById('max-dist-atr') as HTMLSelectElement | null)?.value ?? '20',
  );
  const base = `/api/overlays/${encodeURIComponent(asset)}/${encodeURIComponent(tf)}`;
  // The window this pane is actually showing. Without it the overlay routes return the full history
  // and every pivot's `idx` is offset from the trimmed bar array.
  // On the research source BOTH bounds go (t_42cdbde6 U7): an anchored window ends before the
  // series does, and with `first_time` alone the overlay would run to the end of the history.
  // Other sources ignore `last_time`, so they get exactly the query they always had.
  const research = el.source.value === RESEARCH;
  const winQ = (pane.windowFirstTime === null ? '' : `&first_time=${pane.windowFirstTime}`)
    + (research && pane.windowLastTime !== null ? `&last_time=${pane.windowLastTime}` : '');
  const summary = document.getElementById('overlay-summary');
  const rows: [string, string][] = [];
  const lines: { price: number; color: string; title: string }[] = [];
  // Ranges geometry is drawn by the primitive, not as full-width price lines. Empty
  // by default so an unchecked (or failed) ranges overlay clears the previous draw
  // rather than leaving a stale box on a new series.
  let rangeGeometry: RangeDrawing = { rays: [], bands: [] };
  // The open leg's ray, drawn through the SAME primitive as the range levels (it is
  // a level with a plate). Default empty so switching swings off — or a failed
  // fetch — erases it rather than stranding last series' provisional low.
  let openLegRays: LevelRay[] = [];
  // Same contract for the break segments: default empty, so switching the overlay off
  // (or a failed fetch) erases them instead of leaving last series' breaks behind.
  let breakGeometry: BreakSegment[] = [];
  // The LTF evidence layer's knowability segments, on the SAME primitive as the break segments and
  // kept SEPARATE from `breakGeometry` for the reason every geometry here is separate: unticking one
  // overlay must not erase the other's lines. Default empty, so switching this layer off -- or a
  // failed fetch -- erases last series' gaps rather than leaving them over new candles.
  let ltfGeometry: BreakSegment[] = [];
  // The swing zigzag, on the SAME primitive as the break segments. Default null for the same
  // reason as every other geometry here: unchecking swings — or a failed fetch — must erase
  // last series' polyline rather than leave it over new candles.
  let zigzagGeometry: Zigzag | null = null;
  // FVG zones, drawn as BOXES through the same band primitive as the OTE zone. Default
  // empty for the same reason as the others: unchecking the overlay — or a failed fetch
  // — must erase last series' gaps rather than leave them framing new candles.
  const fvg: { payload: FvgPayload | null } = { payload: null };
  // Separate from FVG geometry so unchecking one overlay cannot erase the other's boxes.
  let obGeometry: ZoneBand[] = [];
  // The experiment overlay's boxes, levels and windows (t_42cdbde6 U7). Default empty, like the rest.
  let experimentGeometry: ZoneBand[] = [];
  // Separate again, same reason: unticking `setup` must not erase the OB or FVG boxes.
  let setupGeometry: ZoneBand[] = [];
  // How many setup bands the FULL payload would have drawn that the primary-only default does
  // not (card t_0ad264de). Read from `setupDrawing`, never recomputed here: the count and the
  // geometry are one answer, and a second derivation is how "34 zone(s) off screen" came to be
  // printed over 29 painted zones. 0 both when nothing is withheld and when the setup overlay is
  // off, which is right — there is nothing to offer a click for in either state.
  let setupWithheld = 0;
  // WHY THIS PANE HAS NO IDM, or null when it draws one or the `setup` layer is off (card
  // `t_0cdae4dc`). Read from `idmStatus` on the same payload that drew the bands.
  let idmNone: string | null = null;
  // Liquidity pools, as BANDS through that same primitive (2026-07-28). Same
  // erase-on-toggle contract; a stale pool shelf on a new series is a level that does not
  // exist there.
  let liquidityGeometry: ZoneBand[] = [];
  // Session boxes + the Asia target (card `t_5b2017b5`). Separate, same erase-on-toggle contract.
  let sessionsGeometry: ZoneBand[] = [];
  // The clock levels (card `t_2d80caad`), same erase-on-toggle contract.
  let sessionLevelsGeometry: ZoneBand[] = [];
  // Static-structure levels + order-flow zones (card `t_fb5e844d`). Same erase-on-toggle contract.
  let structureGeometry: ZoneBand[] = [];
  // BP Asia-sweep boxes + lines (card `t_5ee634f0`). Same erase-on-toggle contract.
  let bpGeometry: ZoneBand[] = [];
  // BP imbalance staircases (card `t_28a039b9`). Same erase-on-toggle contract.
  let bpImbGeometry: ZoneBand[] = [];
  // BP 021 IDM levels (card `t_6f7db3f5`). Same erase-on-toggle contract.
  let bpIdmGeometry: ZoneBand[] = [];
  // BP 023 order block boxes (card `t_d894c573`). Same erase-on-toggle contract.
  let bpObGeometry: ZoneBand[] = [];
  // The liquidity payload AND the drawing that was handed to the series, carried past the
  // fetch so the sidebar row can be built AFTER `redrawMarkers` — see the `liquiditySummary`
  // call below and R4-6. The DRAWING travels too: rebuilding it there would produce fresh
  // marker objects and the identity match against the drawn list would find none of them.
  //
  // A one-field HOLDER rather than a bare `let`: tsc narrows a `let x: T | null = null` that
  // is only ever assigned inside a callback down to `never` at the read site (the assignment
  // is not on the control-flow path it can see), so `holder.rendered` — whose narrowing the
  // awaited `load` call resets — is what makes the deferred hand-off type-check.
  const liquidity: { rendered: { payload: LiquidityPayload; drawing: LiquidityDrawing } | null } =
    { rendered: null };

  pane.markers = { swings: [], liquidity: [], breaks: [], dots: [], ltf: [], sessions: [], structure: [], bp: [], bpImb: [], bpIdm: [], experiment: [] };

  // Each overlay is independent: one failing must not blank the others, and a
  // failure must be SHOWN — a silently-empty overlay is indistinguishable from
  // "the detector found nothing", the one answer this app must not fake.
  //
  // WHERE IT IS SHOWN CHANGED ON 2026-08-18 (card t_677775e0): the right-hand inspector panel is
  // gone, so a failure now goes to THIS PANE'S OWN BAR via `renderPaneError` below. `#status` and
  // the summary row are kept as well — three sinks with different lifetimes, and the pane bar is
  // the only one that is neither global nor overwritten by a sibling pane.
  const failures: [string, string][] = [];
  // THE ANSWER KEY FOR THIS PASS. `lastClosedTime` is the pane's last CLOSED bar, derived from
  // `lastBarForming` -- the server's own verdict (`meta.has_forming_bar` / `split_forming`), never a
  // wall-clock comparison, for the reason `ChartPane.lastBarForming` states. It is what makes reuse
  // safe: a bar closing moves this number, drops every held answer and refetches. See
  // `overlayrefresh.ts::OverlayAnswerKey` for the executed proof that the FORMING bar moves no
  // overlay answer, which is why it is deliberately not in the key.
  const closedBars = pane.lastBarForming ? pane.bars.slice(0, -1) : pane.bars;
  const lastClosedTime = closedBars.length ? closedBars[closedBars.length - 1].time : null;
  const answerKeyFor = (url: string) => ({
    url,
    firstTime: pane.windowFirstTime,
    lastClosedTime,
  });
  const load = async <T>(url: string, apply: (p: T) => void, label: string) => {
    try {
      // ALREADY FETCHED FOR THIS EXACT (window, last-closed-bar, url)? Then re-apply it instead of
      // asking again. `apply` still RUNS -- the geometry is redrawn on every pass, because a pass may
      // have been triggered by something that changed the DRAW without changing the ANSWER (a
      // setup-view flip, a zone-view rescale), and because `clearOverlays` may have emptied the
      // primitives since. Only the network call is skipped.
      const key = answerKeyFor(url);
      const held = overlayAnswers.get(pane.id, key) as T | undefined;
      if (held !== undefined) {
        apply(held);
        return;
      }
      const fresh = await getJSON<T>(url);
      overlayAnswers.put(pane.id, key, fresh);
      apply(fresh);
    } catch (err) {
      // `err.message`, NOT `${err}`. Stringifying an Error prepends "Error: ", which is the
      // second half of what Andrei saw: "setups: Error: 422 [object Object]".
      const why = err instanceof Error ? err.message : String(err);
      setStatus(`${label}: ${why}`, true);
      failures.push([label, why]);
      // THE REASON GOES IN THE ROW, not only the status bar. The one-line status bar is
      // overwritten by the next overlay that fails, so a row that merely pointed AT the status
      // bar pointed at a message already gone by the time you read it.
      rows.push([label, `ERROR ${why}`]);
    }
  };

  // ONE FETCH, TWO GATES -- `swingsDraw` decides, so the four combinations are testable without a
  // chart, a DOM and a backend. `swings` draws the raw pivots; `market_structure` draws the
  // structure dots. Both arrive on the SAME payload (the server places the points and sends them
  // beside the pivots), so the route is asked once if EITHER control is ticked and each control
  // decides only what it draws. That is the fix to Andrei's "why i don't see these dots": before
  // this the dots lived inside the `swings` branch, so clearing the pivot mat cleared the structure
  // with it.
  const draw = swingsDraw(pane.overlays);
  if (draw.fetch) {
    await load<SwingsPayload>(`${base}/swings?${q()}${winQ}`, (p) => {
      if (draw.pivots) {
        // SH/SL text stays ON even with breaks enabled. It used to be dropped because BOS
        // labels sat in the same slots and collided into runs like "BOSSH"; the break
        // markers now carry NO text at all (the word lives on the segment plate, which is
        // collision-tested), so there is nothing left for the swing labels to collide with.
        pane.markers.swings = swingMarkers(p, showConfirm);
        // THE ZIGZAG replaced the arrows and the MH/ML/SH/SL text (t_b87831db). Vertices and
        // the open leg's dashed tail are drawn by the break primitive, not the marker layer:
        // `SeriesMarker` cannot express hollow and quantises size away. See the ZIGZAG
        // DECISION block in overlays.ts.
        zigzagGeometry = swingZigzag(p);
        // The open leg's running extreme rides the RANGE ray primitive: its marker text
        // would be clipped at the right edge (measured: x=896 on a 910px canvas), and a
        // ray + plate is already the legible-at-the-edge mechanism on this chart. It is a
        // PIVOT fact (the leg that has not closed yet), so it belongs to this gate.
        openLegRays = openLegDrawing(p);
      }
      if (draw.dots) {
        // THE STRUCTURE POINTS, which are NOT these pivots. The detector places a point only
        // where price took an inducement, and the server sends the ones it found on this same
        // payload -- so the chart draws what arrived and derives nothing of its own. See
        // `docs/reports/structure-points-rule-2026-08-09.md`.
        //
        // The cast is because `SwingsPayload` does not name the field yet -- it is declared in
        // `overlays.ts`, which a concurrent change owns; `structurePointDots` tolerates the
        // field being absent, so an older API build draws no dots rather than throwing.
        pane.markers.dots = structurePointDots(p as { structure_points?: StructurePoint[] });
      }
      rows.push(...swingsSummary(p));
    }, 'swings');
  }
  if (on('breaks')) {
    // ONE fetch for BOS and CHoCH: they are one detector (see the OVERLAYS comment).
    // Markers point at the piercing swing exactly as the retired MSS overlay did —
    // Andrei asked to keep that — and the levels are now BOUNDED dashed segments
    // running from the swing that set them to the bar that broke them.
    await load<BreaksPayload>(`${base}/breaks?${q()}${winQ}`, (p) => {
      pane.markers.breaks = breakMarkers(p);
      breakGeometry = breakSegments(p);
      rows.push(...breaksSummary(p));
    }, 'breaks');
  }
  if (on('fvg')) {
    // Keep the full payload: the nearest pair can change on each price tick without
    // a closed bar or a new detector response. Selected IFVGs retain both phases.
    await load<FvgPayload>(`${base}/fvg?${q({ inversion_rule: rule })}${winQ}`, (p) => {
      fvg.payload = p;
    }, 'fvg');
  }
  if (on('order_blocks')) {
    // Same band primitive as FVG and the OTE zone. `orderBlockBands` drops mitigated blocks (206 of
    // 225 on BTC/1D) and starts each box at its CONFIRMING BREAK, so a zone never appears earlier
    // than the event that defines it.
    // `ob_reference` is REQUIRED by the route (422 without it): the primary source contradicts
    // itself about which candle of a run supplies the trigger, so a value must be chosen explicitly.
    // `extreme_open` is the deepest level price must cross — the conservative reading, and the one
    // the KB's bearish quotes state directly. Reuses the same select-driven pattern as `inv-rule`.
    await load<OrderBlockPayload>(
      `${base}/order_blocks?${q()}${winQ}`, (p) => {
      obGeometry = orderBlockBands(p);
      rows.push(...orderBlockSummary(p));
    }, 'order blocks');
  }
  if (on('setup')) {
    // The SAME band primitive as FVG, OB and the OTE zone — a composite overlay is still just
    // rectangles, and a fourth zone renderer would be three copies of the coordinate math.
    // `setupBands` draws live setups only by default, exactly as `orderBlockBands` does.
    //
    // 503 IS EXPECTED HERE while ict/setup.py is landing, and `load` surfaces it as a named
    // overlay error rather than an empty chart. That is the point of the route refusing: "no
    // setup right now" is a true and frequent market state, so an empty list would be
    // indistinguishable from a missing detector.
    await load<SetupPayload>(`${base}/setup?${q({ inversion_rule: rule })}${winQ}`, (p) => {
      // ONE FUNCTION DECIDES WHAT THE SETUP LAYER DRAWS (card t_0ad264de). By default only the
      // NEAREST live setup per direction, because on `xyz:XYZ100` 15m this layer alone drew ~14
      // boxes and ~18 captions -- `wait short OB` seven times over four distinct price levels.
      // The rest are one click away (`setupViewPanes` / `renderPaneSetupView` below), the same
      // shape as this pane's `zone(s) off screen -> show them` control.
      //
      // `setupDrawing` returns the BANDS AND the withheld count together, because the number the
      // control prints and the geometry on screen are the pair that must not drift -- the exact
      // defect behind "34 zone(s) off screen" over 29 painted zones.
      //
      // The live IDM is inside that return value rather than appended here: since card
      // `t_ec068c65` it is ONE level per pane, read from `p.live_idm` (the server decides it —
      // `chartapp/api/overlays.py::_live_idm`), and it is OUTSIDE the `withheld` arithmetic, so
      // the primary-only cut has nothing to do with it. `setupDrawing`'s own header in
      // `overlays.ts` states why in full — read it there rather than trusting a second copy here.
      // It remains a SEPARATE call inside `setupDrawing` (never folded into
      // `setupBands`) because every count assertion on that function's return value pins a
      // decision Andrei made himself; mixing a second kind of object in broke 15 of them.
      // `windowFirstTime` IS THE FOURTH ARGUMENT because the live IDM's anchor bar can be older
      // than the bars this pane loaded, and a band anchored before the series start draws NOTHING
      // (`RangePrimitive._at` -> null). `idmLevels` clamps it to the window's first bar instead --
      // card `t_79d313f6`; the number lives in `pane.windowFirstTime`, which is what `/api/bars`
      // itself reported, never a recomputation of it.
      // `pane.tf` names the IDM plate `IDM <tf>` (card `t_0cdae4dc`), the dot's own word.
      const d = setupDrawing(
        p, setupViewPanes.has(pane.id), maxDistAtr, pane.windowFirstTime, pane.tf,
      );
      setupGeometry = d.bands;
      setupWithheld = d.withheld;
      idmNone = idmStatus(p, pane.tf);
      rows.push(...setupSummary(p, pane.tf));
    }, 'setups');
  }
  if (on('ltf_evidence')) {
    // CLOSED-BAR EVIDENCE FOR A HUMAN REVIEW (card `t_bf04f7ec`) — never a signal. The layer draws
    // one marker per fact and a dashed segment for each fact whose KNOWABLE bar is later than the
    // bar it happened on: that gap is the look-ahead risk, made visible, and it is measurably not
    // the detector's own `confirm_idx` (the cached BTC/1D BOS at 243 confirms at 246 and appears in
    // no prefix until 247).
    //
    // The segments ride the SAME primitive as the break segments, so there is no fourth
    // coordinate-math implementation and the words land on collision-tested plates -- the labelling
    // lesson `breakMarkers` records in full.
    //
    // ONE CALL returns markers, segments AND the rows describing them, because the number the
    // sidebar prints and the geometry on screen are the pair that must not drift ("34 zone(s) off
    // screen" over 29 painted zones).
    await load<LtfEvidencePayload>(`${base}/ltf_evidence?${q()}${winQ}`, (p) => {
      const d = ltfEvidenceDrawing(p);
      pane.markers.ltf = d.markers;
      ltfGeometry = d.segments;
      rows.push(...d.rows);
    }, 'ltf evidence');
  }
  if (on('sessions')) {
    // CLOSED-BAR FACTS (card `t_5b2017b5`): a session still in progress has no box, because its
    // high and low are still moving. Boxes, target and M/D from ONE call, so they cannot drift.
    await load<SessionsPayload>(`${base}/sessions?${q()}${winQ}`, (p) => {
      const d = sessionsDrawing(p, pane.windowFirstTime);
      sessionsGeometry = d.bands;
      pane.markers.sessions = d.markers;
      rows.push(...d.rows);
    }, 'sessions');
  }
  if (on('session_levels')) {
    // CLOSED-BAR FACTS (card `t_2d80caad`): a window still open has no box; a PDH/PDL or a gap
    // whose period is still running comes back with `end_time` null and runs to the edge.
    await load<SessionLevelsPayload>(`${base}/session_levels?${q()}${winQ}`, (p) => {
      const d = sessionLevelsDrawing(p, pane.windowFirstTime);
      sessionLevelsGeometry = d.bands;
      rows.push(...d.rows);
    }, 'session levels');
  }
  if (on('bp_sessions')) {
    // CLOSED-BAR FACTS (card `t_5ee634f0`): a sweep still open has `end_time` null, so its 50%
    // line and stops run to the edge; partial / failed appear only at the 11:00 close.
    await load<BpSessionsPayload>(`${base}/bp_sessions?${q()}${winQ}`, (p) => {
      const d = bpSessionsDrawing(p, pane.windowFirstTime);
      bpGeometry = d.bands;
      pane.markers.bp = d.markers;
      rows.push(...d.rows);
    }, 'bp sessions');
  }
  if (on('bp_imbalance')) {
    // CLOSED-BAR FACTS (card `t_28a039b9`): an open imbalance has `end_time` null and runs to the
    // edge at its current unfilled part; a run's shorter prefix ends where the next gap formed.
    await load<BpImbalancePayload>(`${base}/bp_imbalance?${q()}${winQ}`, (p) => {
      const d = bpImbalanceDrawing(p, pane.windowFirstTime);
      bpImbGeometry = d.bands;
      pane.markers.bpImb = d.markers;
      rows.push(...d.rows);
    }, 'bp imbalance');
  }
  if (on('bp_idm')) {
    // CLOSED-BAR FACTS (card `t_6f7db3f5`): a row exists from its forming close on; a live level has
    // `end_time` null and runs to the edge; a body-broken one ends at the breaking bar.
    await load<BpIdmPayload>(`${base}/bp_idm?${q()}${winQ}`, (p) => {
      const d = bpIdmDrawing(p, pane.windowFirstTime);
      bpIdmGeometry = d.bands;
      pane.markers.bpIdm = d.markers;
      rows.push(...d.rows);
    }, 'bp idm');
  }
  if (on('bp_ob')) {
    // CLOSED-BAR FACTS (card `t_d894c573`): a zone exists from its `knowable_time` on; an open one
    // has `end_time` null and runs to the edge; an ended one stops at the bar that ended it.
    await load<BpObPayload>(`${base}/bp_ob?${q()}${winQ}`, (p) => {
      const d = bpObDrawing(p, pane.windowFirstTime);
      bpObGeometry = d.bands;
      rows.push(...d.rows);
    }, 'bp ob');
  }
  // THE EXPERIMENT LAYER (t_42cdbde6 U7). Research only: on any other source the checkbox is
  // disabled, and a stale tick from a saved layout draws nothing rather than a 422. The route needs
  // both window bounds, so a pane whose window has not loaded yet asks for nothing.
  // A holder, for the tsc narrowing reason `liquidity` below states.
  const expDrawn: { id: string | null; levels: number; selected: ExperimentEvent | null } =
    { id: null, levels: 0, selected: null };
  if (on('experiment') && research) {
    const exp = nav.exp;
    const first = pane.windowFirstTime;
    const last = pane.windowLastTime;
    if (exp === null) {
      rows.push(['experiment', 'pick an experiment in the navigator']);
    } else if (first !== null && last !== null) {
      // `selected` (card t_5b4bfe85): over the size cap the server thins every event but this one,
      // so the selected event keeps its levels on a 4h/1D pane. A FULL answer does not depend on
      // `selected`, so it is also held under the bare URL and reused whatever the selection: a
      // filter that clears the current event makes no request (BR2.6, e2e experiment.spec.ts).
      const bareUrl = `${base}/experiment?${q({ exp: exp, first_time: first, last_time: last })}`;
      const bare = overlayAnswers.get(pane.id, answerKeyFor(bareUrl)) as ExperimentPayload | undefined;
      const url = nav.current === null || (bare !== undefined && !bare.thin)
        ? bareUrl : `${bareUrl}&selected=${encodeURIComponent(nav.current)}`;
      await load<ExperimentPayload>(url, (p) => {
        if (!p.thin && url !== bareUrl) overlayAnswers.put(pane.id, answerKeyFor(bareUrl), p);
        const d = experimentDrawing(p, first, nav.current);
        experimentGeometry = d.bands;
        pane.markers.experiment = d.markers;
        rows.push(...d.rows);
        expDrawn.id = d.selectedId;
        expDrawn.levels = d.selectedLevels;
        expDrawn.selected = d.selected;
      }, 'experiment');
    }
  }
  if (on('static_structure')) {
    // The knob starts on STATIC_N_DEFAULT (1, the 3-candle fractal of [F 015 95], card `t_28a039b9`).
    // An empty value can still reach here (no knob rendered), and then nothing is fetched and the row
    // says why -- an empty pane would read as "the method found nothing".
    const n = (document.getElementById('static-n') as HTMLSelectElement | null)?.value ?? '';
    if (!n) {
      rows.push(['static structure', 'choose a fractal n']);
    } else {
      // `n: n`, not the `{ n }` shorthand: test_frontend_sends_required_params.py reads `key:` pairs.
      await load<StaticStructPayload>(`${base}/static_structure?${q({ n: n })}${winQ}`, (p) => {
        const d = staticStructureDrawing(p, pane.windowFirstTime);
        structureGeometry = d.bands;
        pane.markers.structure = d.markers;
        rows.push(...d.rows);
      }, 'static structure');
    }
  }
  if (on('ranges')) {
    await load<RangesPayload>(`${base}/ranges?${q()}${winQ}`, (p) => {
      // BOTH degrees come back in one payload — the local SH-SL box and the major MH-ML
      // box (Andrei, 2026-07-27). They are not a refinement of each other: measured over
      // the cache they pick a different (hi, lo) pair on 107 of 107 usable series.
      rangeGeometry = rangeDrawing(p);
      rows.push(...rangesSummary(p));
    }, 'ranges');
  }
  if (on('liquidity')) {
    // tol_atr and min_touches are REQUIRED by the route on purpose: 0.25xATR is
    // production's uncalibrated value and the LuxAlgo cross-check showed it merges
    // away ~a third of the equal-highs an independent implementation sees.
    //
    // ZONES, not lines (Andrei, 2026-07-28: the overlay "does not work"). A pool has a
    // real lo–hi extent the payload always served — median 0.970% of price, p90 18.266%
    // — and the old draw collapsed it to one scalar with no label at all. Bands + markers
    // now come from ONE call so a sweep arrow cannot outlive the pool it points at.
    await load<LiquidityPayload>(
      `${base}/liquidity?${q({ tol_atr: tolAtr, min_touches: minTouches })}${winQ}`,
      (p) => {
        const d = liquidityDrawing(p);
        liquidityGeometry = d.bands;
        pane.markers.liquidity = d.markers;
        // THE ROW IS NOT BUILT HERE. `sweeps N of M marked` has to be counted from what
        // `redrawMarkers` actually handed the series, and that call has not happened yet
        // (R4-6, high): computing it now is precisely how the sidebar came to say
        // `3 of 3` while one marker was drawn. The payload AND this drawing are carried to
        // the deferred call below — the drawing, because rebuilding it there would make
        // fresh marker objects and the identity match would find none of them.
        liquidity.rendered = { payload: p, drawing: d };
      }, 'liquidity');
  }

  // THE CURRENT WEEK'S PROFILE, fetched with the overlays and rendered into this pane's own
  // bar. Unconditional and not behind an overlay checkbox: it is ONE line of text in existing
  // chrome, and requirement 3's "current week's detected profile" is exactly the thing that
  // must be visible without being asked for. Awaited with the rest so its rays reach the same
  // `range.setData` call as everything else.
  await loadWeekly(pane, answerKeyFor);

  // The user may have switched asset/tf while those ~7 fetches were in flight.
  // Drawing now would put this series' geometry on another series' candles —
  // the same failure `pollLive` guards against for bars. Discard instead. Likewise for a pane torn
  // down by the pane-count picker meanwhile (261019-pane-layout): its chart is disposed.
  if (epoch !== epochOf(pane) || !paneAlive(pane)) return;

  // PANE DIAGNOSTICS for the experiment layer, written on every current pass so one that no longer
  // draws the event erases them: which event this pane drew in full, and how many of its level
  // lines it was given. The execution pane's drawn event also feeds the navigator's detail block.
  const expCell = document.querySelector(`[data-pane="${pane.id}"]`) as HTMLElement | null;
  if (expCell) {
    if (expDrawn.id === null) delete expCell.dataset.expEvent;
    else expCell.dataset.expEvent = expDrawn.id;
    expCell.dataset.expLevels = String(expDrawn.levels);
  }
  if (pane.id === EXECUTION_PANE_ID && research) {
    navDrawn = expDrawn.selected;
    renderNavigator();
  }

  // WHAT THE SERIES WAS ACTUALLY GIVEN. `insideWindow` runs inside this call, so any row
  // counted from a builder's output alone is counted on the wrong side of it (R4-6, high).
  const drawnMarkers = pane.redrawMarkers();
  // ...AND THE LIQUIDITY ROW IS BUILT FROM IT, here rather than in the fetch handler. The
  // sidebar said `sweeps 3 of 3 marked` on a pane that drew one, because the number came
  // from `liquidityDrawing` before the window filter had run. It now reads the drawn list.
  if (liquidity.rendered) {
    rows.push(...liquiditySummary(liquidity.rendered.payload, {
      drawing: liquidity.rendered.drawing,
      onSeries: drawnMarkers,
    }));
  }
  redrawPriceLines(pane, lines);
  // One primitive, FOUR sources: the range levels, the open leg's provisional extreme,
  // the FVG boxes, and the liquidity pools. Merged here rather than in `rangeDrawing` so
  // the overlays stay independently toggleable (swings on + ranges off must still draw
  // the open leg).
  // The ZONE bands go LAST so their labels yield to the range/OTE labels when plates
  // collide — a zone carries its information geometrically (height, position, colour),
  // a level does not. Liquidity after FVG for the same reason in miniature: an FVG box is
  // an entry zone, a pool is a target shelf, and a pool's `x3 141.20` is recoverable from
  // the sidebar while an unlabelled FVG is not.
  let zoneBands: ZoneBand[] = [];
  const repaint = () => {
    if (epoch !== epochOf(pane) || !paneAlive(pane) || asset !== el.asset.value || tf !== pane.tf) return;
    const price = pane.bars.at(-1)?.close;
    const payload = pane.overlays.fvg ? fvg.payload : null;
    const fvgGeometry = payload ? fvgBands(payload, price, pane.bars) : [];
    zoneBands = [...fvgGeometry, ...obGeometry, ...setupGeometry, ...liquidityGeometry];
    pane.range.setData({
      rays: [...rangeGeometry.rays, ...openLegRays],
      // Session levels and boxes FIRST, so every other zone paints over them: context, not zones.
      // The experiment's shapes LAST: they are the subject of a navigator jump, not context.
      bands: [...sessionLevelsGeometry, ...sessionsGeometry, ...bpGeometry, ...structureGeometry, ...bpImbGeometry, ...bpIdmGeometry, ...bpObGeometry, ...rangeGeometry.bands, ...zoneBands, ...experimentGeometry],
    });
    // Only update this pane's FVG disclosure in the shared summary, leaving the
    // other overlays' rows intact when their detector answers have not changed.
    if (payload) {
      for (const [key, value] of fvgSummary(payload, price, pane.bars)) {
        const cell = summary?.querySelector(`[data-fvg-pane="${pane.id}"][data-fvg-key="${key}"]`);
        if (cell) cell.textContent = value;
      }
    }
    renderPaneZoneView(pane, offScreenBands(paneVisibleRange(pane), zoneBands), zoneBands.length);
  };
  repaint();
  repaintFvg.delete(pane.id);
  if (fvg.payload && pane.overlays.fvg) {
    repaintFvg.set(pane.id, repaint);
    rows.push(...fvgSummary(fvg.payload, pane.bars.at(-1)?.close, pane.bars).map(([key, value]): [string, string] =>
      [key, ['drawn', 'filled'].includes(key)
        ? `<span data-fvg-pane="${pane.id}" data-fvg-key="${key}">${value}</span>` : value]));
  }
  // BOTH segment sets through the ONE primitive, concatenated at the write rather than merged into
  // a shared variable: `pane.breaks` keeps one list, and two overlays that each own a `let` can be
  // switched off independently (the separate-geometry contract every other layer here follows).
  pane.breaks.setData([...breakGeometry, ...ltfGeometry]);
  pane.breaks.setZigzag(zigzagGeometry);
  // AND AIM THE TAIL AT THE FORMING BAR IMMEDIATELY. `setZigzag` clears the live tip (the new
  // geometry's own tail supersedes it), so without this the tail would be a closed-bar reading
  // until the next poll cadence — the same staleness one cadence wide.
  paintZigzagTip(pane);
  // AFTER the primitive write, because the row's expand button reflects what was just drawn.
  // Called unconditionally so a pane whose weekly read now fails erases the previous line --
  // same contract as `renderPaneError` below.
  renderPaneWeekly(pane);
  // THIS PANE'S setup-view control, called UNCONDITIONALLY so a run with nothing withheld — or
  // with the `setup` layer unticked — ERASES the previous run's row. Same contract as
  // `renderPaneWeekly` above and `renderPaneError` below: a control offering to reveal setups on
  // a pane that is no longer drawing any is a control that lies about what a click would do.
  //
  // NOT inside the `requestAnimationFrame` the zone-view control uses: that one waits because it
  // needs the primitive's post-paint label counters, whereas `withheld` is known the moment the
  // payload is filtered. Deferring it would leave the row one frame stale for no gain.
  renderPaneSetupView(pane, setupWithheld);
  // Unconditional for the same reason: a pane that has since drawn an IDM must lose the line.
  renderPaneIdmNone(pane, idmNone);
  if (summary) summary.innerHTML = kv(rows);
  // THIS PANE'S failures, in THIS PANE'S bar. Called unconditionally so a run with nothing to
  // report ERASES the previous run's line: a fetch that has since succeeded must not keep showing
  // its old failure, which is the same contract as the geometry defaults above.
  renderPaneError(pane, failures);
  // Label suppression is known only AFTER the primitive paints (it depends on the pixel
  // width of each plate at the current zoom), so the row is appended on the next frame
  // rather than computed here. A silently-dropped label would make 225 breaks look like
  // 19 — see `BreakPrimitive.hiddenLabels`.
  //
  // Reported PER KIND. A blended "19 drawn" is what hid a starvation bug where the real
  // state was BOS 0 / CHoCH 19: the label pass gave CHoCH priority and 101 continuation
  // breaks never got a single word on screen. Both numbers now sit in the sidebar, so a
  // zero on either side is visible without instrumenting the canvas.
  //
  // COUNTED OVER EVERY SEGMENT THE PRIMITIVE WAS GIVEN, breaks AND ltf-evidence knowability
  // gaps, because the primitive reports ONE pair of counters for the whole frame. Keying the
  // denominator on `breakGeometry` alone would print "3 of 19 hidden" over a frame carrying 30
  // segments -- a count on the wrong side of the drawing, which is the `drawn N of M` defect
  // `.hermes.md` §9c names, and it is the same reasoning the zone-label row below already
  // applies to its own bands.
  const segmentsDrawn = breakGeometry.length + ltfGeometry.length;
  if (summary && segmentsDrawn) {
    requestAnimationFrame(() => {
      const n = pane.breaks.hiddenLabels;
      if (n > 0) {
        const d = pane.breaks.drawnLabels;
        summary.innerHTML += kv([
          ['labels hidden', `${n} of ${segmentsDrawn} — zoom in (lines all drawn)`],
          ['labels shown', `${d.BOS} BOS / ${d.CHoCH} CHoCH`],
        ]);
      }
    });
  }
  // Same contract for the ZONE labels, and it needs its own row because the failure
  // mode differs: a zone's plate is DROPPED on collision rather than nudged (43 boxes
  // on one series would otherwise be the confetti the break labels shipped three times
  // over). Every BOX is still drawn; only the redundant text yields. Without this row
  // an unlabelled zone would look like a zone that was never detected.
  //
  // Counted over EVERY band the primitive was given, not just the FVG ones. The primitive
  // reports one pair of counters for the whole frame, so keying this on `fvgGeometry`
  // alone (as it did before liquidity became bands) would attribute pool suppression to
  // the FVG total and read as "hidden 9 of 4" — an arithmetic impossibility that would
  // discredit the disclosure it is meant to provide.
  if (summary && zoneBands.length) {
    requestAnimationFrame(() => {
      const shown = pane.range.drawnBandLabels;
      const n = pane.range.hiddenBandLabels;
      // Off-pane zones are neither drawn nor "hidden" — they are not on screen at all
      // (the price scale shows a window, and a 2020 gap is nowhere near it). Reporting
      // shown + hidden against the TOTAL is what makes that third state visible; a bare
      // "hidden N of M" implied the rest were all painted, which was false for 24 of 39
      // zones on BTC 1D before the off-pane refusal landed.
      //
      // MEASURED, NOT INFERRED, and that is the fix. This line used to be
      //     const offPane = zoneBands.length - shown - n;
      // which was only true while EVERY band asked for a caption. The live-only caption
      // change sends an EMPTY label for a spent setup, `RangePrimitive` skips an empty
      // caption and counts it as neither drawn NOR hidden (it was never a candidate), so
      // every spent zone fell through the subtraction into "off screen". On BTC/15m that
      // read "34 zone(s) off screen" while 29 of those zones were painted on screen in
      // front of Andrei — the warning was reporting the caption policy, not the price
      // window. `offScreenBands` compares each band against the pane's own visible range.
      const offPane = offScreenBands(paneVisibleRange(pane), zoneBands);
      const rows: [string, string][] = [];
      if (n > 0) rows.push(['zone labels hidden', `${n} of ${shown + n} on screen — zoom in`]);
      // THE DISCLOSURE IS NOW A CONTROL. It used to end in "scroll the price scale", which was
      // true and useless: Andrei ticked `setup` on SOL 1D, the three live zones sat at 7.53-8.47,
      // 12.64-13.59 and 58.60-61.92 against a ~50-350 axis, and the honest message read as "the
      // feature is broken" because finding them meant dragging the axis by hand. See
      // `showOffPaneZones` for what the click does and why autoScale alone cannot do it.
      // ALSO RENDERED INTO THIS PANE'S OWN BAR. `#overlay-summary` is ONE shared box that every
      // pane overwrites (see the `summary.innerHTML =` above), so the row -- and the button with it
      // -- survives only for whichever pane refreshed LAST. Andrei's report was about the 1D pane,
      // whose row is destroyed by 15m's refresh milliseconds later, so a summary-only control would
      // have been unreachable for exactly the pane he was looking at. Measured: candidates
      // pane-htf button=0, pane-mtf button=0, pane-ltf button=1.
      renderPaneZoneView(pane, offPane, zoneBands.length);
      if (offPane > 0) {
        rows.push(['zones off screen', `${offPane} of ${zoneBands.length}`
          + ' — outside the visible price range' + zoneViewButton(pane)]);
      } else if (zoneViewPanes.has(pane.id)) {
        // The row would otherwise VANISH the moment the click succeeds (nothing is off pane any
        // more), taking the only way back to the default view with it.
        rows.push(['zones in view', `all ${zoneBands.length} on the price scale`
          + zoneViewButton(pane)]);
      }
      if (rows.length) summary.innerHTML += kv(rows);
    });
  }
}

/** Load the asset list for the CURRENT source and repopulate the selectors.
 *
 *  Called on boot and on every source switch: the two feeds serve different
 *  universes (bars_cache has 39 local dirs including ratio pairs and BTC.D;
 *  Hyperliquid has 335 perps and none of those synthetics), so the asset list is
 *  a property of the source, not of the app.
 */
async function loadAssetsForSource(): Promise<boolean> {
  const src = currentSource();
  try {
    assets = await getJSON<AssetsResponse>(`/api/assets?${q()}`);
  } catch (err) {
    setStatus(`${src?.id ?? 'source'}: ${err}`, true);
    assets = [];
    el.asset.innerHTML = '';
    return false;
  }
  if (!assets.length) {
    setStatus(`${src?.id ?? 'source'} returned no assets`, true);
    el.asset.innerHTML = '';
    return false;
  }

  const previous = el.asset.value;
  el.asset.innerHTML = assets.map((a) => `<option value="${a.asset}">${a.asset}</option>`).join('');
  // Keep the asset across a source switch when both feeds carry it — switching
  // source to compare the SAME instrument is the main reason to switch at all.
  const keep = assets.find((a) => a.asset === previous);
  const preferred = keep ?? assets.find((a) => a.asset === 'BTC') ?? assets[0];
  // Via the one writer, so the visible combobox follows. Note the `<select>` has
  // just been repopulated, so its `.value` may ALREADY read `preferred.asset`
  // (browsers select the first option) — `commitChartedAsset` returns `already`
  // there and still writes the mirror, which is why the postcondition holds on
  // boot as well as on a real switch.
  setChartedAsset(preferred.asset);

  // KEEP the pane's own timeframe when this asset offers it — the saved layout wins over a default.
  // Only fall back when the asset genuinely cannot serve it.
  const exec = panes.get(EXECUTION_PANE_ID);
  if (exec && !preferred.timeframes.includes(exec.tf) && preferred.timeframes.includes('1D')) {
    // `setTimeframe`, for the same reason as the select handler: this is a real pane, and a bare
    // field write leaves its axis configured for the timeframe it no longer shows. Found by
    // grepping every `.tf =` after fixing the one the bug report named -- the other assignment
    // (line ~712) is on a plain saved-state object, not a pane, so it stays a field write.
    exec.setTimeframe('1D');
  }
  return true;
}

// ═══════════════════════════════════════════════════════════════════════════
// ASSET PICKER — type-to-filter over the whole universe
// ═══════════════════════════════════════════════════════════════════════════
// Andrei, 2026-07-27, with Hyperliquid's own market picker as the reference: the
// native <select> holds 335 options on the default feed (103 of them `xyz:`), and
// a native select only jumps by first letter — so reaching `xyz:SNDK` meant typing
// `x` and scrolling past 102 siblings.
//
// SELECTION RUNS THE EXISTING PATH. The hidden `<select>` remains the state holder
// (`el.asset.value` is read in a dozen places and written programmatically by the
// panel's Chart button and by `loadAssetsForSource`), and this writes to it then
// dispatches a real `change`. One code path, whichever way the market was chosen —
// a second one is how the panel's button and the picker would drift apart.
//
// The filter/rank/highlight decisions live in `assetpicker.ts` (pure, tested);
// everything here is DOM.

let apMatches: AssetMatch[] = [];
let apIndex = -1;
let apOpen = false;

/** Mirror the authoritative `<select>` value into the input.
 *
 *  Still needed on its own for the cases where nothing was WRITTEN and the box must
 *  merely be snapped back to the truth (closing the dropdown on a half-typed query).
 *  Every path that CHANGES the market goes through `setChartedAsset` instead. */
function apSync() {
  el.assetInput.value = el.asset.value;
}

/**
 * THE ONE WRITER of `el.asset.value`. DOM adapter over `commitChartedAsset`.
 *
 *  Every path that changes the charted market — the picker, the panel's row click,
 *  the ticket's Chart button, a source switch, boot — routes through here, so the
 *  visible combobox cannot fall behind the chart (measured 2026-07-28: it did, on
 *  1 of 3 writers). The decision and the mirror live in `assetpicker.ts` where
 *  vitest can reach them; this function is only the `<select>`/`<input>` binding.
 *
 *  It deliberately does NOT dispatch `change`. The alternative — make every writer
 *  fire the event and let one handler do everything — was rejected because the
 *  `change` handler also clears `tpSelected` and the ticket, and the panel's callers
 *  set that selection and then ask for the chart: dispatching would erase the very
 *  selection that requested the switch. The paths differ by design; the sync is what
 *  they must share, so the sync is what got centralised.
 */
function setChartedAsset(asset: string): ChartedAssetResult {
  return commitChartedAsset(
    {
      get value() { return el.asset.value; },
      set value(v: string) { el.asset.value = v; },
      get options() { return [...el.asset.options].map((o) => o.value); },
      get mirror() { return el.assetInput.value; },
      set mirror(v: string) { el.assetInput.value = v; },
    },
    asset,
  );
}

function apClose() {
  apOpen = false;
  apIndex = -1;
  el.assetList.hidden = true;
  el.assetInput.setAttribute('aria-expanded', 'false');
  // Snap back to the real selection: a half-typed query left in the box would
  // claim the chart is showing a market it is not.
  apSync();
}

function apRender(query: string) {
  apMatches = filterAssets(assets, query);
  if (apIndex >= apMatches.length) apIndex = apMatches.length - 1;
  if (apMatches.length && apIndex < 0 && query.trim()) apIndex = 0;

  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;');
  const rows = apMatches.slice(0, 400).map((m, i) => {
    const label = m.hit
      ? `${esc(m.asset.slice(0, m.hit[0]))}<mark>${esc(m.asset.slice(m.hit[0], m.hit[1]))}</mark>${esc(m.asset.slice(m.hit[1]))}`
      : esc(m.asset);
    return `<div class="apick-row${i === apIndex ? ' on' : ''}" role="option" data-i="${i}" data-asset="${esc(m.asset)}">` +
      `<span class="tick">${label}</span>` +
      (m.hip3 ? '<span class="hip3">HIP-3</span>' : '') +
      `<span class="tfs">${m.timeframes.length} tf</span></div>`;
  });
  const status = pickerStatus(apMatches, query, assets.length);
  rows.push(`<div class="apick-status${apMatches.length ? '' : ' empty'}">${esc(status)}</div>`);
  el.assetList.innerHTML = rows.join('');
  el.assetList.hidden = false;
  el.assetInput.setAttribute('aria-expanded', 'true');
  apOpen = true;
  el.assetList.querySelector('.apick-row.on')?.scrollIntoView({ block: 'nearest' });
}

/**
 * Commit a market from the PICKER. Write the `<select>` through the one writer, then
 * fire its `change`, so `populateTimeframes` + `tpSelected` reset + `loadBars()` all
 * run exactly as they do for a manual selection.
 *
 * The `change` dispatch is specific to this path and is not a general mechanism: a
 * picker selection is a fresh, explicit choice of market, so dropping the panel
 * selection and the ticket is correct here. The panel's own switch must NOT do that
 * (see `setChartedAsset`).
 */
function apSelect(asset: string) {
  if (!assets.some((a) => a.asset === asset)) return;
  const verdict = setChartedAsset(asset);
  apClose();
  if (verdict === 'unavailable') return;
  el.asset.dispatchEvent(new Event('change', { bubbles: true }));
}

function apWire() {
  el.assetInput.addEventListener('focus', () => {
    // Show the whole universe on focus, and select the text so typing replaces
    // rather than appends to the current ticker.
    el.assetInput.select();
    apRender('');
  });
  el.assetInput.addEventListener('input', () => apRender(el.assetInput.value));
  el.assetInput.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!apOpen) { apRender(el.assetInput.value); return; }
      apIndex = moveHighlight(apIndex, e.key === 'ArrowDown' ? 1 : -1, apMatches.length);
      apRender(el.assetInput.value);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // Enter with nothing highlighted takes the single best match, so typing a
      // full ticker and pressing Enter works without touching the arrows.
      const pick = apIndex >= 0 ? apMatches[apIndex] : apMatches[0];
      if (pick) apSelect(pick.asset);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      apClose();
    }
  });
  // mousedown, not click: a click would fire after the input's blur has already
  // closed the list, so the row would be gone before it could be read.
  el.assetList.addEventListener('mousedown', (e) => {
    const row = (e.target as HTMLElement).closest('.apick-row') as HTMLElement | null;
    if (!row) return;
    e.preventDefault();
    apSelect(row.dataset.asset ?? '');
  });
  el.assetInput.addEventListener('blur', () => window.setTimeout(apClose, 120));
  // Belt and braces for a market change from anywhere else. `setChartedAsset` already
  // mirrors every programmatic write, so this listener is no longer the mechanism that
  // keeps the box honest — it only covers a genuine USER interaction with the hidden
  // `<select>` (keyboard, or a browser that exposes it), which fires `change` natively
  // and writes no mirror of its own.
  el.asset.addEventListener('change', apSync);
}

// ═══════════════════════════════════════════════════════════════════════════
// DRAWING LAYER — controller
// ═══════════════════════════════════════════════════════════════════════════
// Andrei, 2026-07-28: *"like in tradingview i need to have an ability to do basic drawing
// on the chart that should survive app restarts and ability to delete my drawings: like
// basic trend line, horizontal line, fib retracement with extensions, rectangle. Should
// have magnet mode snaps drawings mode to make them precise."*
//
// This section RENDERS and ROUTES, like the rest of the file. The fib ladder is computed
// by `ict.ranges.fib_levels` server-side (see `drawings.ts`'s header: a fib in JS would
// be the OTE band's wrong-side-of-EQ bug, in a place the user draws by hand), the pure
// decisions live in `drawings.ts`, and the pixels in `drawingprimitive.ts`.
//
// STATE IS KEYED BY (asset, tf) ON THE SERVER, so nothing here caches across a switch:
// `loadDrawings()` refetches and the primitive's data is REPLACED, because drawing BTC's
// levels on an ETH chart is worse than drawing stale ones.
//
// ONE LAYER PER PANE (2026-08-03). This section used to hold `const drawPrimitive = new
// DrawingPrimitive()` and a single `drawState`, attached to the EXECUTION pane's series by
// `initChart`. Andrei: "our drawing tools like lines, rectangle, Fib levels, etc are not working
// on our panes." They worked on exactly one -- measured, trendline armed, two clicks per pane:
// the 15m hint advanced 2 -> 1 and the 1W and 1D panes did nothing at all, because they had no
// drawing layer and no click subscription. `ChartPane` already constructed and attached its own
// `drawings` primitive; nothing was wired to it.
//
// So the pane owns the LAYER, the pending anchors and the loaded set (`pane.drawings`,
// `pane.drawPending`, `pane.drawState`), and every function here takes the pane it acts on. The
// armed TOOL and the magnet MODE stay module-level: one tool at a time across the whole app is
// what a toolbar means, and duplicating it per pane would let two panes disagree about what is
// armed. There is deliberately no module-level primitive left for a future edit to re-attach to
// the wrong series.

/** The tool the user has armed, or null (the normal, non-drawing state). */
let drawTool: DrawingKind | null = null;
/** Magnet mode. A MODE, not a radius — see `snapToBar`. */
let drawMagnet = false;

/**
 * Which pane holds the current SELECTION, or null.
 *
 * AT MOST ONE SELECTED DRAWING ACROSS ALL PANES, which is a decision and not an emergent
 * property. `el.drawDel.disabled` and the Delete key both need one unambiguous answer to "delete
 * what?", and three independently-selecting layers would give three. So selecting in pane B
 * clears pane A: `drawSelect` is the ONLY way selection is set, and it deselects everywhere else
 * first. The alternative -- per-pane selection plus a "focused pane" concept -- invents a second
 * piece of state to disambiguate the first.
 */
let drawSelPane: ChartPane | null = null;

/** localStorage key for the magnet flag. The MODE is a workspace preference (like the
 *  panel height); the DRAWINGS are not — they are analysis and live on the server. */
const DRAW_MAGNET_KEY = 'draw.magnet';

/** How many anchors each tool needs before the drawing is complete. Mirrors
 *  `chartapp/api/drawings.py::ANCHORS` — the server validates, this only decides when to
 *  stop collecting clicks. */
const DRAW_ANCHORS: Record<DrawingKind, number> = {
  trendline: 2, hline: 1, rect: 2, fib: 2,
};

/** THIS PANE's store. The tf comes from the pane, never from `execTf()` — that was what tied
 *  every read and write to the execution pane's timeframe. */
function drawApiBase(pane: ChartPane): string {
  return `/api/drawings/${encodeURIComponent(el.asset.value)}/${encodeURIComponent(pane.tf)}`;
}

/** Push the server's answer into ONE pane's layer and the sidebar. ONE place, so what is
 *  rendered is always what the server actually holds — never an optimistic echo. */
function drawApply(pane: ChartPane, p: DrawingsPayload | null) {
  pane.drawState = p;
  pane.drawings.setData(p ? p.drawings.map((d) => ({ ...d, anchors: [...d.anchors] })) : []);
  drawRenderChrome();
}

/** Set (or clear) the app-wide selection. See `drawSelPane`: exactly one drawing is selected
 *  across all three panes, so this clears every other pane's layer before setting this one's. */
function drawSelect(pane: ChartPane | null, id: string | null): void {
  for (const p of panes.values()) {
    if (p !== pane || id === null) p.drawings.setSelected(null);
  }
  if (pane && id !== null) pane.drawings.setSelected(id);
  drawSelPane = pane && id !== null ? pane : null;
  drawRenderChrome();
}

/** The selected drawing's id, wherever it lives. */
function drawSelectedId(): string | null {
  return drawSelPane?.drawings.selected ?? null;
}

async function loadDrawings(pane: ChartPane): Promise<void> {
  // Cleared FIRST: a failed fetch must not leave the previous series' marks on screen.
  drawApply(pane, null);
  pane.drawPending = [];
  try {
    const got = await getJSON<DrawingsPayload>(drawApiBase(pane));
    // STALE, the same contract `fetchPaneBars` enforces for candles: this pane's timeframe (or the
    // asset) may have changed while the fetch was in flight, and applying 1W's drawings to a pane
    // now showing 15m is the "geometry from a different chart" defect one layer up.
    if (got.tf !== pane.tf || got.asset !== el.asset.value) return;
    // And a pane torn down meanwhile by the pane-count picker (261019-pane-layout) draws nothing.
    if (!paneAlive(pane)) return;
    drawApply(pane, got);
  } catch (err) {
    if (!paneAlive(pane)) return;
    setStatus(`drawings: ${err}`, true);
  }
}

/** Persist THIS PANE's whole set and re-render from the RESPONSE, not from local state. */
async function drawSave(pane: ChartPane, next: Drawing[]): Promise<void> {
  try {
    const r = await acFetch(drawApiBase(pane), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // `levels` is stripped: it is derived from the anchors, and persisting it would let
      // a stale ladder outlive a change to the server's ratio set.
      body: JSON.stringify({
        drawings: next.map(({ id, kind, anchors, magnet }) => ({ id, kind, anchors, magnet })),
      }),
    });
    if (!r.ok) {
      // The server names what is wrong (`unknown drawing kind …`), so it is shown rather
      // than replaced with a generic failure.
      const detail = (await r.json().catch(() => ({}))).detail ?? r.statusText;
      setStatus(`drawings: ${detail}`, true);
      await loadDrawings(pane);    // resync: local state may now differ from the store
      return;
    }
    drawApply(pane, await r.json());
  } catch (err) {
    setStatus(`drawings: ${err}`, true);
  }
}

async function drawDelete(pane: ChartPane | null, id: string | null): Promise<void> {
  if (!pane || !id) return;
  try {
    const r = await acFetch(`${drawApiBase(pane)}?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
    if (!r.ok) throw new Error(r.statusText);
    drawSelect(null, null);
    drawApply(pane, await r.json());
  } catch (err) {
    setStatus(`drawings: ${err}`, true);
  }
}

/** Arm / disarm a tool. Re-pressing the active tool disarms it, so there is always a way
 *  back to plain chart interaction without hunting for an Escape key. */
function drawSetTool(tool: DrawingKind | null) {
  drawTool = drawTool === tool ? null : tool;
  // Every pane's half-finished drawing is abandoned, not just the one last clicked in: arming a
  // tool is a fresh start, and a stray anchor left on another pane would make its next click
  // complete a shape the user started minutes ago on a different timeframe.
  for (const p of panes.values()) p.drawPending = [];
  drawRenderChrome();
}

/** The toolbar's visible state + the sidebar disclosure. A mode you cannot see is a mode
 *  you fight, and a dropped drawing you are not told about is the silent-absence defect. */
function drawRenderChrome() {
  for (const b of el.drawTools.querySelectorAll<HTMLButtonElement>('button[data-tool]')) {
    b.classList.toggle('on', b.dataset.tool === drawTool);
  }
  el.drawMagnet.classList.toggle('on', drawMagnet);
  el.drawDel.disabled = !drawSelectedId();
  // Progress is stated, because a two-click tool that has consumed one click looks
  // identical to one that has not. The count is the FURTHEST-ALONG pane: with three panes the
  // user is mid-drawing on exactly one of them (see `drawClickAt`), and naming a pane in the
  // toolbar hint would be noise when only one can be in progress.
  const need = drawTool ? DRAW_ANCHORS[drawTool] : 0;
  const got = Math.max(0, ...[...panes.values()].map((p) => p.drawPending.length));
  const hint = drawTool
    ? `${drawTool}: click ${need - got} more point(s)`
      + (drawMagnet ? ' · magnet ON' : '')
    : '';
  el.drawTools.title = hint;
  const box = document.getElementById('overlay-summary');
  // The summary describes the EXECUTION pane, like every other row in that box.
  const state = panes.get(EXECUTION_PANE_ID)?.drawState ?? null;
  if (box && state) {
    const rows = drawingsSummary(state);
    if (hint) rows.push(['drawing', hint]);
    // Appended, never replacing: the overlay summary is written by `refreshOverlaysOnce`.
    const prev = box.querySelector('#draw-rows');
    prev?.remove();
    if (rows.length) {
      const div = document.createElement('div');
      div.id = 'draw-rows';
      div.innerHTML = kv(rows);
      box.appendChild(div);
    }
  }
}

/** A click on ONE pane's chart, when a tool is armed: collect an anchor, and place when complete. */
function drawClickAt(pane: ChartPane, time: number, price: number) {
  if (!drawTool) return;
  // ONE PANE AT A TIME. A drawing's anchors are coordinates on one price/time grid, so a first
  // click on 1W followed by a second on 15m does not describe a shape — it describes two points
  // that never appeared together. Switching panes mid-drawing therefore RESTARTS on the new pane
  // rather than mixing, and there is no way to represent the mixed state at all.
  for (const p of panes.values()) if (p !== pane) p.drawPending = [];
  // MAGNET: snap to the nearest O/H/L/C of the bar under the cursor, from THIS PANE's bars — a
  // weekly click must snap to weekly candles. `pane.bars` is what the pane actually rendered.
  const bar = barAt(pane.bars, time);
  const snapped = snapAnchor({ time, price }, bar, drawMagnet);
  pane.drawPending = [...pane.drawPending, snapped];
  const need = DRAW_ANCHORS[drawTool];
  if (pane.drawPending.length < need) {
    drawRenderChrome();
    return;
  }
  const made: Drawing = {
    id: `d${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
    kind: drawTool,
    anchors: pane.drawPending.slice(0, need),
    magnet: drawMagnet,
  };
  pane.drawPending = [];
  // The tool DISARMS after placing. TradingView keeps it armed; here a mode that
  // silently persists means the next click meant for the crosshair places a line.
  drawTool = null;
  void drawSave(pane, [...(pane.drawState?.drawings ?? []), made]);
}

/** Wire ONE pane's drawing layer: its click subscription, its selection and drag callbacks, and
 *  its labels. Called per pane, which is the whole fix — the click subscription used to exist on
 *  the execution pane only, so two thirds of the app's charts ignored every drawing tool. */
function drawWirePane(pane: ChartPane) {
  // COUNTED, because a missing wire is exactly the bug: `drawWire` called this for `activePane()`
  // only, so two of three panes had no click subscription and every drawing tool was dead on them.
  // A hook-driven test cannot see that (it passes the pane in), so the tally is the observable.
  drawWiredPanes.set(pane.id, (drawWiredPanes.get(pane.id) ?? 0) + 1);
  const onClick: MouseEventHandler<Time> = (param) => {
    if (!drawTool || param.time === undefined || !param.point) return;
    const price = pane.candleSeries.coordinateToPrice(param.point.y);
    if (price == null) return;
    drawClickAt(pane, Number(param.time), Number(price));
  };
  pane.chart.subscribeClick(onClick);
  // Kept by reference so `teardownPane` can unsubscribe it (261019-pane-layout FR-2.6).
  paneWires.get(pane.id)?.release.push(() => pane.chart.unsubscribeClick(onClick));

  pane.drawings.setCallbacks({
    onSelect: (id) => {
      // A click while a tool is armed is a PLACEMENT, not a selection — otherwise the
      // first click of a trend line would select whatever it happened to land near.
      if (drawTool) return;
      drawSelect(pane, id);
    },
    onAnchorDrop: (id, index, time, price) => {
      const cur = pane.drawState?.drawings ?? [];
      const bar = barAt(pane.bars, time);
      const next = cur.map((d) => {
        if (d.id !== id) return d;
        const anchors = [...d.anchors];
        anchors[index] = snapAnchor({ time, price }, bar, drawMagnet);
        return { ...d, anchors };
      });
      void drawSave(pane, next);
    },
  });

  // Labels: the fib's are per level (ratio AND price, per the reference), everything
  // else is one line. Both route through `priceText`, the app's ONE formatting rule.
  pane.drawings.setLabeller((d) => (
    d.kind === 'fib' && d.levels?.length
      ? d.levels.map((lv) => fibLabel(lv, (v) => priceText(v, d.anchors[0].price, d.anchors[1].price)))
      : [drawingLabel(d, (v) => priceText(v))]
  ));
}

/** How many drawing click subscriptions each pane got. See `drawWirePane`. */
const drawWiredPanes = new Map<string, number>();

/** True once `drawWire` has run: a pane built later by `applyPaneCount` is wired on its own. */
let drawWireDone = false;

function drawWire() {
  drawMagnet = localStorage.getItem(DRAW_MAGNET_KEY) === '1';
  drawWireDone = true;

  // EVERY PANE, not `activePane()`. `drawWirePane` was already written per-pane, but this caller
  // passed one pane -- so the click subscription still existed on the execution chart alone and the
  // reported bug survived the refactor. Caught by a REAL-MOUSE e2e assertion after a hook-driven one
  // passed: `__ictDraw.click(time, price, paneId)` takes the pane as an argument, so it can never
  // prove the app WORKS OUT the pane from the click.
  for (const p of panes.values()) drawWirePane(p);

  // Test hook, same rationale as `__ictInspect` / `__ictTicket` above: lightweight-charts
  // hit-tests inside its own event pipeline and IGNORES synthetic MouseEvents aimed at
  // the canvas (measured — a full mousedown/mouseup/click sequence at real coordinates
  // placed nothing), so the placement path cannot be driven from a harness at all. This
  // exposes the SAME functions the real handlers call, never a parallel implementation,
  // so a test through it exercises production code.
  //
  // PANE-ID KEYED, because "did the drawing land" is now a per-pane question and the reported
  // bug was invisible to any probe that could not name the pane.
  const paneOf = (id?: string) => panes.get(id ?? EXECUTION_PANE_ID) ?? null;
  const clickSubscribers = () =>
    Object.fromEntries([...panes.keys()].map((id) => [id, drawWiredPanes.get(id) ?? 0]));
  (window as unknown as { __ictDraw?: unknown }).__ictDraw = {
    /** Arm a tool, exactly as the toolbar button does. */
    tool: (t: DrawingKind | null) => { drawSetTool(t); return drawTool; },
    /** A chart click at (time, price) on ONE pane — the body of its `subscribeClick` branch. */
    click: (time: number, price: number, paneId?: string) => {
      const p = paneOf(paneId);
      if (p) drawClickAt(p, time, price);
    },
    magnet: (on?: boolean) => {
      if (on !== undefined) {
        drawMagnet = on;
        localStorage.setItem(DRAW_MAGNET_KEY, on ? '1' : '0');
        drawRenderChrome();
      }
      return drawMagnet;
    },
    select: (id: string | null, paneId?: string) => drawSelect(id === null ? null : paneOf(paneId), id),
    selected: () => drawSelectedId(),
    /** Which pane holds the selection — the cross-pane rule is only assertable with this. */
    selectedPane: () => drawSelPane?.id ?? null,
    /** What the SERVER holds for one pane, as last rendered — not a local optimistic copy. */
    state: (paneId?: string) => {
      const st = paneOf(paneId)?.drawState ?? null;
      return st ? JSON.parse(JSON.stringify(st)) : null;
    },
    /** What each pane's layer is actually DRAWING, by pane id. The drawn truth, so a test cannot
     *  be satisfied by another pane's drawing (the vacuous-assertion trap this bug hides in). */
    clickSubscribers,
    drawn: () => Object.fromEntries(
      [...panes.values()].map((p) => [p.id, p.drawings.data.map((d) => d.id)]),
    ),
    del: (id?: string | null, paneId?: string) => (
      id === undefined || id === null
        ? drawDelete(drawSelPane, drawSelectedId())
        : drawDelete(paneOf(paneId), id)
    ),
    reload: (paneId?: string) => {
      const p = paneOf(paneId);
      return p ? loadDrawings(p) : Promise.resolve();
    },
    /** The bars ONE pane rendered, so a test can pick anchors on that pane's own scale and assert
     *  the magnet against real OHLC. Rows, not a count: a 1W pane holds weekly timestamps, so a
     *  test driving `click` needs THIS pane's times -- a 15m time would land off its scale and a
     *  failure would read as "wrong coordinates" rather than "the click was ignored". */
    bars: (paneId?: string) => (paneOf(paneId)?.bars ?? []).map(
      ({ time, open, high, low, close }) => ({ time, open, high, low, close }),
    ),
  };

  el.drawTools.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest('button') as HTMLButtonElement | null;
    if (!btn) return;
    if (btn.dataset.tool) { drawSetTool(btn.dataset.tool as DrawingKind); return; }
    if (btn.id === 'draw-magnet') {
      drawMagnet = !drawMagnet;
      localStorage.setItem(DRAW_MAGNET_KEY, drawMagnet ? '1' : '0');
      drawRenderChrome();
      return;
    }
    if (btn.id === 'draw-del') void drawDelete(drawSelPane, drawSelectedId());
  });

  // Delete key, PLUS the toolbar button above — a keyboard-only delete is
  // undiscoverable, and a button-only one is slow once you have several marks.
  window.addEventListener('keydown', (e) => {
    if (e.key !== 'Delete' && e.key !== 'Backspace') return;
    // Never while typing: Backspace in the ticker box must edit text, not delete a mark.
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
    if (!drawSelectedId()) return;
    e.preventDefault();
    void drawDelete(drawSelPane, drawSelectedId());
  });
}

/**
 * ESCAPE DISCARDS A STAGED STOP/TARGET, on every pane at once.
 *
 * The card requires a cancel path, and it is the same key every other "I did not mean
 * that" gesture in this app uses. It clears the PENDING price only: the committed price
 * is the exchange's and this cannot touch it, so the worst Escape can do is put the
 * chart back to what the order graph says — which is precisely the point.
 *
 * Bound on `window` in capture-free normal order and skipped while typing, for the same
 * reason the Delete handler above is: Escape in the ticker box must close that box.
 */
function wireLevelCancel(win: Window = window) {
  win.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
    // Every position, not just the charted one: a staged price on a market he has since
    // scrolled away from is still a price he might act on later.
    const staged = liveLevels.ids().filter((id) => {
      const p = liveLevels.get(id);
      return p !== null && (p.pendingSl !== null || p.pendingTp !== null);
    });
    if (!staged.length) return;
    for (const id of staged) liveLevels.clearPending(id);
    // NO STATUS LINE: `setStatus` renders nothing unless `isError` (see its own comment),
    // and the feedback is already the right one — every pane's line snaps back to the
    // price the order graph reports, which is the fact being asserted.
    console.info(`[levels] discarded staged changes on ${staged.join(', ')}`);
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// TRADE PANEL — controller
// ═══════════════════════════════════════════════════════════════════════════
// The dock at the bottom of the chart: Hyperliquid's account tabs in
// TradingView's panel chrome. Rendering lives in `tradepanel.ts`; this section
// owns the state machine, polling, chart linkage and the two-phase write flow.
//
// It obeys the same rule as the rest of this file — it RENDERS and it ROUTES,
// it never decides anything money-critical. Risk verdicts come from the
// hl-review engine, TP/SL levels come from the order graph, and every mutation
// is argv built server-side from a whitelist and executed by exec_tools.

/** localStorage keys. Panel geometry is a workspace preference, and losing it on
 *  every reload is the kind of small friction that makes a tool go unused. */
const TP_KEYS = { tab: 'tp.tab', height: 'tp.height', collapsed: 'tp.collapsed', poll: 'tp.poll',
                  cursor: 'tp.setupcursor' };

const TP_MIN_H = 120;
/** Leave the chart at least this much room: a panel dragged to fill the window
 *  makes the app useless in a way that is annoying to undo. */
const TP_MAX_VH = 0.8;
/** Account poll cadence. Slower than the 5s bar poll on purpose: a snapshot is a
 *  10-dex sweep (~3s measured), positions do not change between ticks the way a
 *  forming candle does, and — measured on 2026-07-27 — a faster poll gets the tab
 *  HTTP-429'd (~33 /info requests per snapshot). Matches the backend's
 *  SNAPSHOT_TTL_S so a poll lands about when the cache expires. */
const TP_POLL_MS = 15000;

let accountHealth: AccountHealth | null = null;
let snapshot: Snapshot | null = null;
/**
 * THE one price model for the live position's stop and target (`livelevels.ts`).
 *
 * Four panes draw from it and any of them can drag it, which is why it is a single
 * module-level object rather than a field of a pane: a stop is ONE price on ONE
 * position, so per-pane copies would be four chances to disagree — the
 * second-source-of-truth bug this app has already paid for twice (`markerState`,
 * `Position.stop_loss`).
 *
 * Committed prices are written ONLY by `sync()` from the account snapshot. A drag
 * writes a PENDING price, which survives the 15 s poll and reaches the exchange only
 * through the gated confirm path — never on mouse-up.
 */
const liveLevels = new LiveLevelModel();
let tpTab: TabId = 'positions';
/** Which column each table is sorted by. Null / absent = the backend's natural order.
 *
 *  Lives HERE, next to `tpTab`, because this is already the panel's single source of
 *  truth for what is on screen -- `tpRenderBody` reads both and hands them to
 *  `renderTab`. A second copy inside `tradepanel.ts` is how this repo has produced
 *  its most-repeated defect class (two places disagreeing about one fact), so the
 *  renderers stay PURE: they receive the state, they never store it.
 *
 *  Keyed by tab because the columns differ per table -- an `entry_px` sort must not
 *  follow you into Open Orders, which has no such column. Deliberately NOT persisted
 *  to localStorage: a sort is a momentary "find this row" action, and a book silently
 *  still sorted the next morning is a book that gets misread. */
const tpSort: Partial<Record<TabId, SortState>> = {};
/** Which market's levels are mirrored onto the chart. Null = follow the chart. */
let tpSelected: string | null = null;
/** Levels that exist on the book but fall outside the visible price range, so
 *  `createPriceLine` drew nothing for them. Surfaced in the sub-bar: a missing line
 *  must never be mistaken for a missing order. */
let tpOffscreenLevels: string[] = [];
/** The scan's recent setups, and whatever went wrong reading them.
 *
 *  Kept separate from `snapshot`: these rows describe an INTENT the scan published,
 *  not the state of the book, and folding them into the account snapshot would let a
 *  research suggestion be mistaken for a live order. They also refresh on a different
 *  clock — the scan writes them a few times a day, the snapshot every 15 s. */
let setupsPayload: SetupsPayload | null = null;
let setupsError: string | null = null;
/** THE REVIEW CURSOR: which setup he last opened, so returning is not a scroll hunt.
 *
 *  Andrei, 2026-08-18: he must never scroll back to find where he left off — the list is
 *  87 rows and the ticket is a different tab. Persisted in `localStorage` beside the
 *  other panel preferences because a reload is exactly the moment losing your place
 *  hurts most, and it is a POSITION, not money: a stale one can only mis-highlight a
 *  row, and `resolveCursor` refuses to point at a row the payload no longer carries.
 *
 *  A KEY, NOT AN INDEX. The setups list is re-read on every visit to the tab and the
 *  scan writes a few rows a day, so a bare index would point at a neighbouring setup
 *  after any new alert. `setupKey` composes the identity, and since t_1849cfb1 it includes
 *  `idm_swept_time` -- so the identity IS the row's real primary key rather than a subset of
 *  it that folded four pairs of prod rows together and trapped the walk on them. */
let setupCursor: SetupCursor | null = null;
/** The morning stop review's record, and whatever went wrong reading it.
 *
 *  Its own payload and its own error line, for the same reason `setupsPayload` has
 *  them: these rows describe advice the reviewer PUBLISHED, not the state of the
 *  book, and folding them into the account snapshot would let a suggestion be
 *  mistaken for a live order. The "did you do it?" column is derived by crossing the
 *  two at render time — which is why neither has to know about the other. */
let advicePayload: AdvicePayload | null = null;
let adviceError: string | null = null;
/** What the manual scan is doing, in a sentence. See `tpTriggerScan`. */
let scanNote: string | null = null;
/** The execution being watched, null when no scan of ours is running. */
let scanExecution: string | null = null;
/** True from the click until the trigger POST settles. A SEPARATE flag from
 *  `scanExecution` because that one cannot be set until the POST comes back, which
 *  leaves an await-wide window in which a second click starts a second 21-minute
 *  execution. See `tpTriggerScan`. */
let scanStarting = false;
let scanStartedAt = 0;
let scanTimer: number | null = null;
/** Local wall-clock `HH:MM`, for saying WHEN a scan started.
 *
 *  Not `fmtTime` (which prints the full `M/D/YYYY - HH:MM:SS` the HL panel uses): a
 *  scan he started minutes ago does not need a date, and a status line he reads mid-
 *  trade should be scannable at a glance. */
const hhmm = (ms: number): string =>
  new Date(ms).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' });
/** How often to ask a RUNNING scan how it is doing.
 *
 *  20 s over a job that takes minutes: about a dozen DescribeExecution calls per run,
 *  against a status that cannot change faster than a Step Functions state transition.
 *  The 15 s account poll is tuned against an exchange rate limit; this one is not
 *  competing for it. */
const SCAN_POLL_MS = 20000;
/** When to STOP asking, ~40 minutes: double the scan's own ~20.
 *
 *  The poll is a chained `setTimeout` that re-arms only while the execution reports
 *  RUNNING, or while its status cannot be read -- and that second branch is the one
 *  that needs a floor. A status endpoint failing every 20 s would otherwise re-arm the
 *  timer forever, so a tab left open overnight would still be calling out at 3am about
 *  a scan that ended hours ago. At the deadline it says it lost track, which is true,
 *  and stops. It never guesses an outcome it did not observe. */
const SCAN_POLL_GIVEUP_MS = 40 * 60 * 1000;
/** The labels actually handed to `LevelPrimitive` on the last draw. Exists so a test
 *  can assert what is ON THE CHART rather than what `levelsFor` would produce —
 *  manage-mode suppression sits between the two. */
let tpDrawnLevels: string[] = [];

let tpTimer: number | null = null;
let tpLoading = false;
let tpError: string | null = null;

/**
 * IN-FLIGHT / FAILED CANCELS, keyed by the order's OWN oid as a string.
 *
 * Never by row index: the tables sort, so an index key would move a
 * "Cancelling..." or "Still open" label onto whichever order slid into that slot.
 * The oid comes off the clicked button's `data-oid`, so the key and the click agree
 * by construction.
 *
 * Entries are DROPPED, not set to a success value, when the order leaves the book --
 * the vanished row is the proof, and there is nothing left to label. See `tpCancelNow`.
 */
const tpCancelling = new Map<string, CancelRowState>();

/** One line under the tab bar for the last completed cancel: the order is gone, said
 *  once, because its row cannot say it. Cleared by the next cancel. */
let tpNote: string | null = null;

/** The action the dialog is currently composing. `token` is set by a dry-run. */
let tpAction: {
  verb: string;
  symbol: string;
  label: string;
  fields: { name: string; label: string; value: number | null; step: number }[];
  fixed: Record<string, unknown>;
  token: string | null;
} | null = null;

/** The manage dialog's phase. Drives which buttons exist — the decision itself is
 *  the pure `buttonState` in `oneclick.ts`, so it can be tested without a DOM. */
let tpPhase: DialogPhase = 'idle';

/** The panel's price lines, tracked separately from the overlay lines.
 *
 *  Separate list because they are cleared on different events: overlay lines
 *  change when a checkbox or asset changes, panel lines when the account
 *  snapshot or the selected market changes. One shared list meant one refresh
 *  wiping the other's geometry. */

async function postJSON<T>(url: string, body: unknown): Promise<T> {
  const r = await acFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const payload = await r.json().catch(() => ({ detail: r.statusText }));
  if (!r.ok) throw new Error(`${r.status} ${payload.detail ?? r.statusText}`);
  return payload as T;
}

/** The market the panel should act on: an explicit row click, else the chart's.
 *
 *  Chart assets are bare HL coins on the `hyperliquid` feed, and the panel's
 *  coins carry the dex prefix (`xyz:AMZN`), so the fallback matches on the bare
 *  tail as well — otherwise selecting AMZN on the chart would never light up its
 *  own position row. */
function tpActiveCoin(): string | null {
  if (tpSelected) return tpSelected;
  const asset = el.asset.value;
  if (!asset || !snapshot) return null;
  const bare = (c: string) => (c.includes(':') ? c.split(':')[1] : c);
  const hit = [...snapshot.positions, ...snapshot.orders].find(
    (r) => r.coin === asset || bare(r.coin) === bare(asset),
  );
  return hit ? hit.coin : null;
}

/** Draw the account's own levels for the active market onto the chart.
 *
 *  This is the whole reason the panel lives next to a chart rather than in a
 *  separate window: seeing the resting stop and the liquidation price against
 *  the structure they were chosen from is the check the numbers alone cannot
 *  give you. */
function liveDrawnLevels(): { levels: PanelLevel[]; withHandles: AccountLevel[] } {
  const coin = tpActiveCoin();
  // SUPPRESSED while the ticket is managing this market. The ticket draws its own
  // draggable legs at the same prices, and two primitives with independent collision
  // lists cannot avoid each other — measured on the live xyz:AMZN short: all four
  // prices were drawn twice and the plates overprinted their twins ("SE 0 273 1" on
  // top of "SL 273 -$50.17"). The ticket's version wins because it is the
  // INTERACTIVE one; the read-only levels would be a decoration you cannot grab.
  const managed = tkState.managing && tkState.managing === coin;
  const levels: PanelLevel[] = snapshot && coin && !managed ? levelsFor(snapshot, coin) : [];

  // Drawn by `LevelPrimitive`, NOT `createPriceLine`. That API can only show its
  // `title` in the price-axis label, and the axis label is off by design (a dozen CE
  // levels plus the range levels stack into a column over recent price action) — so
  // the account's own entry/SL/TP rendered as unlabelled dotted lines. Reported
  // against the live xyz:TSLA bracket: "only red and blue lines and no indication
  // what's SL, entry or TP".
  const drawn = levels.map((lv) => ({
    price: lv.price,
    color: lv.color,
    label: `${lv.title} ${fmtPx(lv.price)}`,
    dashed: lv.dashed,
  }));
  // THE LIVE POSITION'S STOP AND TARGET BECOME DRAGGABLE HERE (2026-08-18, t_35eac373).
  // Andrei: "I'd like to have an ability to move SL/TP on all panes like it is done at
  // Hyperliquid." `withDragHandles` adds a handle carrying the POSITION ID to the two
  // legs the model speaks for and rewrites their price when one is staged; every other
  // line — entry, liquidation, resting orders, a second TP leg — is returned untouched
  // and stays read-only. Adding handles rather than rendering from the model is
  // deliberate: `levelsFor` emits every protective leg plus the resting orders, so
  // drawing from the model would DROP lines the chart shows today.
  return { levels, withHandles: coin ? withDragHandles(drawn, liveLevels, coin) : drawn };
}

/**
 * The levels for the 1W / 1D panes: the live position's lines UNIONED with the ticket's
 * planned legs. The merge itself is `livelevels.mergeContextLevels` — a pure function, so
 * the union has a real witness rather than a substring grep over this file (which is how
 * redteam D1 shipped green). See its docstring for the measured defect.
 */
function contextLevels(): AccountLevel[] {
  const live = liveDrawnLevels().withHandles;
  const tkHoldsPrices =
    tkState.entry !== null || tkState.stop !== null || tkState.tps.some((tp) => tp !== null);
  // Same symbol only: `tkDrawLines` clears its own legs when the ticket belongs to another
  // market, and drawing them here would put another symbol's plan on this chart.
  const foreign = Boolean(tkState.symbol && el.asset.value && tkState.symbol !== el.asset.value);
  const legs = tkHoldsPrices && !foreign ? ticketGeometry(tkState, tkPreview).legs : [];
  return mergeContextLevels(live, legs);
}

function tpDrawLevels() {
  const { levels, withHandles } = liveDrawnLevels();
  activePane().levels.setData(withHandles);
  tpDrawnLevels = withHandles.map((d) => d.label);

  // AND THE SAME PRICES, READ-ONLY, ON THE 1W AND 1D PANES. Andrei: "we have entry/TP at 15m pane
  // only, and not on others" -- a stop chosen off a 15m swing is only trustworthy if you can see
  // where it sits against the weekly structure. `drawContextLevels` skips the execution pane, so
  // the line above stays the only writer there; see its docstring for why the draggable primitive
  // is not reused.
  //
  // ONE CONTEXT WRITER, and it is `tkDrawLines` (redteam D1, 2026-08-19). It draws the ticket's
  // own legs on the execution pane AND `contextLevels()` — the UNION of the live position's lines
  // and the plan — on 1W/1D. It runs UNCONDITIONALLY, empty list included: `drawContextLevels([])`
  // CLEARS, and a stale entry line on the 1W chart after the position closes is worse than no line
  // -- it is a price he might act on. `tpDrawLevels` has no early return above this point; keep it
  // that way.
  tkDrawLines();

  // Name the levels that fall OUTSIDE the pane. `createPriceLine` draws nothing for
  // them, and "no line" is indistinguishable from "no order" — the same failure
  // class as the panel once rendering a failed dex read as a calm "No open
  // positions". Real case, verified against raw HL `/info`: the resting xyz:TSLA
  // short at 355.9 carries a take-profit trigger at 100, far below a 280-520 axis.
  // Autoscaling to include it is deliberately NOT the fix — a 100 level on a ~308
  // instrument compresses the candles into a band.
  //
  // MEASURED: `priceToCoordinate` does NOT return null off-screen, it extrapolates
  // (that TP returned y=485 on a 327px pane; 999999 returned y=-1064623). Testing
  // for `null` — my first version — detected nothing.
  const paneH = el.chart.getBoundingClientRect().height;
  const offscreen = levels
    .filter((lv) => {
      const y = activePane().candleSeries.priceToCoordinate(lv.price);
      return y === null || y < 0 || y > paneH;
    })
    .map((lv) => `${lv.title} ${fmtPx(lv.price)}`);

  // Re-render the sub-bar only on CHANGE: this runs from the render path, so an
  // unconditional re-render would recurse.
  if (offscreen.join('|') !== tpOffscreenLevels.join('|')) {
    tpOffscreenLevels = offscreen;
    tpRenderChrome();
  }
}

function tpRenderChrome() {
  const counts = tabCounts(snapshot);
  el.tpTabs.innerHTML =
    TABS.map(
      (t) =>
        `<button class="tp-tab ${t.id === tpTab ? 'active' : ''}" data-tab="${t.id}" role="tab">${
          t.label
        }${counts[t.id] === null ? '' : ` <span class="cnt">(${counts[t.id]})</span>`}</button>`,
    ).join('') +
    `<span id="tp-right">
       <span class="tp-dot ${
         accountHealth?.ok === false ? 'off' : worstSeverity(snapshot).toLowerCase()
       }" title="hl-review verdict"></span>
       <span class="muted">${
         accountHealth?.wallet.display ?? 'no wallet'
       }</span>
       ${
         accountHealth?.trading.trading_enabled
           ? `<span class="tp-write on" title="Armed: ${esc(
               accountHealth?.trading.arm_reason ?? 'signing key present',
             )}. A confirmed action REACHES THE EXCHANGE.">LIVE — writes armed</span>`
           : `<span class="tp-write off" title="${esc(
               accountHealth?.trading.arm_reason ?? 'no signing key',
             )}. Dry-runs and previews work; nothing can reach the exchange.">READ-ONLY — no order can be sent</span>`
       }

       <button id="tp-refresh" title="force a fresh /info sweep">refresh</button>
       <button id="tp-collapse">${document.body.classList.contains('tp-collapsed') ? '▴' : '▾'}</button>
       <button id="tp-maxi">${document.body.classList.contains('tp-max') ? '❐' : '⤢'}</button>
     </span>`;

  const coin = tpActiveCoin();
  const stale = snapshot?.cached ? ` <span class="muted">(cached ${snapshot.cache_age_s}s)</span>` : '';
  el.tpSub.innerHTML = tpError
    ? `<span class="bad">${tpError}</span>`
    : snapshot
      ? `<span>${snapshot.ts}${stale} · swept ${snapshot.dexes_swept.length} dexes in ${
          snapshot.elapsed_s
        }s</span>
         <span>${coin ? `chart levels: <b>${coin}</b>` : 'no position/order on the charted market'}</span>
         ${tpSelected ? '<button id="tp-clearsel" class="mini">follow chart</button>' : ''}
         ${
           tpOffscreenLevels.length
             ? `<span class="warn" title="These orders ARE on the book — verified against HL /info — but their prices fall outside the visible price range, so no line is drawn. Zoom out or scroll the price axis to see them.">⚠ ${
                 tpOffscreenLevels.length
               } level(s) off-screen: ${esc(tpOffscreenLevels.join(', '))}</span>`
             : ''
         }
         ${tpLoading ? '<span class="muted">refreshing…</span>' : ''}
         ${tpNote ? `<span class="cancel-done">${esc(tpNote)}</span>` : ''}`

      : accountHealth?.ok === false
        ? `<span class="bad">panel unavailable — ${
            accountHealth.engine.error ?? accountHealth.wallet.error ?? 'unknown'
          }</span>`
        : '<span class="muted">loading…</span>';
}


// ───────────────────────────────────────────── the profile modal (wallet + MFA)
//
// Andrei asked for "an icon with drop-down menu in a top right corner", wallet configuration moved
// into a dedicated profile view, and the wallet indicator moved here too.
//
// `wireWalletForm` is REUSED UNCHANGED: `renderWalletPanel` emits the same `creds-*` element ids the
// bottom-panel tab did, so the save/delete/validate path has exactly one implementation. Writing a
// second one for the modal would have been a second source of truth about how a signing key is
// submitted — the highest-stakes form in the app.
let profileTab: ProfileTab = 'wallet';
/** A TOTP enrolment in progress: kept out of the render so a re-render cannot lose the secret. */
let pendingEnrolment: { secret: string; uri: string; verify: (c: string) => Promise<void> } | null =
  null;
let mfaOn = false;

function profileEls() {
  return {
    btn: document.getElementById('profile-btn') as HTMLButtonElement | null,
    menu: document.getElementById('profile-menu'),
    who: document.getElementById('profile-who'),
    modal: document.getElementById('profile'),
    title: document.getElementById('profile-title'),
    body: document.getElementById('profile-body'),
    close: document.getElementById('profile-close') as HTMLButtonElement | null,
  };
}

function profileMenuOpen(open: boolean) {
  const { btn, menu } = profileEls();
  if (!btn || !menu) return;
  menu.hidden = !open;
  btn.setAttribute('aria-expanded', String(open));
}

async function profileRender() {
  const { body, title } = profileEls();
  if (!body || !title) return;
  if (profileTab === 'wallet') {
    title.textContent = 'Wallet';
    body.innerHTML = renderWalletPanel(credState);
    wireWalletForm();
    return;
  }
  if (profileTab === 'alerts') {
    title.textContent = 'Telegram alerts';
    // `credState` TOO: the stop-loss checkbox on this panel is gated on a connected wallet, and
    // its subscription flag is read from the credentials payload (never from `/api/account/telegram`
    // — that endpoint neither carries nor may carry it; `sl_monitor.set_enabled` is the sole writer).
    body.innerHTML = renderAlertsPanel(alertState, credState);
    wireAlertsForm();
    return;
  }
  title.textContent = 'Security';
  body.innerHTML = renderSecurityPanel({ enabled: mfaOn, enrolling: pendingEnrolment });
  await wireSecurityPanel();
}

async function profileOpen(tab: ProfileTab) {
  profileTab = tab;
  profileMenuOpen(false);
  const { modal } = profileEls();
  if (!modal) return;
  modal.hidden = false;

  if (tab === 'wallet') {
    // Refetch rather than trust cached state: the modal is where a user comes to CHANGE this, so it
    // should show what the server currently holds, not what it held at page load.
    try {
      credState = await getJSON<CredentialState>('/api/account/credentials');
    } catch {
      // Leave the previous state; the panel still renders and the form still works.
    }
  } else if (tab === 'alerts') {
    // Same reasoning as the wallet tab: this is where alerts get changed, so show what is stored.
    // The response carries `token_saved`, never the token itself.
    try {
      alertState = await getJSON<AlertState>('/api/account/telegram');
    } catch {
      // Leave the previous state; the empty state is a valid thing to render.
    }
    // The wallet read is a SEPARATE round trip because it answers a separate question: is there an
    // address for stop-loss monitoring to select, and is that subscription currently on. Failing it
    // leaves the checkbox in its guarded (disabled) state, which is the safe default.
    try {
      credState = await getJSON<CredentialState>('/api/account/credentials');
    } catch {
      // Leave the previous state; the guard renders from whatever we last knew.
    }
  } else if (acTokens) {
    try {
      mfaOn = await totpEnabled(acTokens);
    } catch {
      mfaOn = false;
    }
  }
  await profileRender();
}

async function wireSecurityPanel() {
  const msg = document.getElementById('mfa-msg');
  const say = (t: string, cls: '' | 'err' | 'ok' = '') => {
    if (!msg) return;
    msg.textContent = t;
    msg.className = cls;
  };

  document.getElementById('mfa-begin')?.addEventListener('click', () => {
    if (!acTokens) {
      say('sign in again before enrolling', 'err');
      return;
    }
    say('preparing…');
    const label = decodeClaims(acTokens.idToken)?.email ?? 'account';
    void beginTotpEnrolment(acTokens, label)
      .then(async (enr) => {
        pendingEnrolment = enr;
        await profileRender();
        // QR rendered AFTER the panel exists, and the library is imported lazily so the ~250KB only
        // loads for the one screen that needs it.
        const holder = document.getElementById('mfa-qr');
        if (holder) {
          const QR = await import('qrcode');
          const canvas = document.createElement('canvas');
          await QR.toCanvas(canvas, enr.uri, { width: 168, margin: 1 });
          holder.appendChild(canvas);
        }
      })
      .catch((err) => say(String(err?.message ?? err), 'err'));
  });

  document.getElementById('mfa-cancel')?.addEventListener('click', () => {
    // Abandoning enrolment is safe BECAUSE `verify` was never called: Cognito only counts the factor
    // after verifySoftwareToken + setUserMfaPreference, so a discarded secret leaves the account
    // exactly as it was rather than half-enrolled.
    pendingEnrolment = null;
    void profileRender();
  });

  document.getElementById('mfa-form')?.addEventListener('submit', (ev) => {
    ev.preventDefault();
    const input = document.getElementById('mfa-code') as HTMLInputElement | null;
    const enr = pendingEnrolment;
    if (!input || !enr) return;
    const bad = validateTotpCode(input.value);
    if (bad) {
      say(bad, 'err');
      return;
    }
    say('verifying…');
    void enr
      .verify(input.value)
      .then(async () => {
        pendingEnrolment = null;
        mfaOn = true;
        await profileRender();
        say('two-factor authentication is on', 'ok');
      })
      .catch((err) => say(String(err?.message ?? err), 'err'));
  });
}

function initProfileMenu() {
  const { btn, menu, who, modal, close } = profileEls();
  if (!btn || !menu || !modal) return;

  if (who && acTokens) {
    const c = decodeClaims(acTokens.idToken);
    who.textContent = whoLabel(c?.email ?? null, c?.sub ?? null);
  }

  btn.addEventListener('click', (ev) => {
    ev.stopPropagation();
    profileMenuOpen(menu.hidden);
  });
  // Click-away, so the menu cannot be left open over the chart.
  document.addEventListener('click', () => profileMenuOpen(false));
  menu.addEventListener('click', (ev) => ev.stopPropagation());

  for (const b of menu.querySelectorAll<HTMLButtonElement>('button[data-profile]')) {
    b.addEventListener('click', () => {
      const which = b.dataset.profile;
      if (which === 'signout') {
        acSignOut();
        return;
      }
      if (which === 'wallet' || which === 'security' || which === 'alerts') void profileOpen(which);
    });
  }

  close?.addEventListener('click', () => {
    modal.hidden = true;
    pendingEnrolment = null;
  });
  // Scrim click closes; clicks inside the card must not.
  modal.addEventListener('click', (ev) => {
    if (ev.target === modal) {
      modal.hidden = true;
      pendingEnrolment = null;
    }
  });
  document.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape' && !modal.hidden) {
      modal.hidden = true;
      pendingEnrolment = null;
    }
  });
}

function tpRenderBody() {
  // The wallet form MOVED to the profile modal (2026-07-31) — see `profile.ts`.
  // The ticket tab renders from the TICKET's state, not from the account
  // snapshot, so it is handled before delegating to the snapshot views.
  // `renderTab` returns null for it precisely so this branch cannot be forgotten.
  if (tpTab === 'setups') {
    // Setups come from the scan's own table, not from the account snapshot, so this
    // tab has its own payload and its own error line. Same hand-off as the ticket.
    //
    // The SNAPSHOT is passed too, and only for the cross-join: the setups are shared
    // across users while the book is per-user, so the crossing happens here rather
    // than inside `/api/setups`. Same division `renderAdvice` above uses.
    el.tpBody.innerHTML = renderSetups(setupsPayload, setupsError, scanNote, snapshot,
                                       tpCancelling, tpCursorIndex());
    return;
  }
  if (tpTab === 'advice') {
    // The advice record crossed with the live book: the rows come from the reviewer's
    // own store, the "did you do it?" column from the snapshot the panel already holds.
    el.tpBody.innerHTML = renderAdvice(advicePayload, snapshot, adviceError);
    return;
  }
  const fromSnapshot = renderTab(tpTab, snapshot, accountHealth, tpActiveCoin(), tpSort[tpTab] ?? null,
                                 tpCancelling);
  // NON-NULL for every remaining tab: the ticket left the dock on 2026-08-18 (card
  // t_ab054079) and lives in `#tk-mount` instead, so `renderTab` no longer has a case
  // that hands the body back to a caller. `?? ''` rather than `!` so a future tab that
  // forgets its branch renders an empty body instead of the string "undefined".
  el.tpBody.innerHTML = fromSnapshot ?? '';
}

/** Latest credential state from `/api/account/credentials`. Never holds a private key. */
let credState: CredentialState | null = null;

/**
 * PUT/DELETE with the bearer token attached, mirroring `postJSON`.
 *
 * A third helper rather than parameterising `postJSON`, because the credential routes are
 * the only PUT/DELETE in the app and changing `postJSON`'s signature would touch every
 * existing caller on the money path for no benefit.
 */
async function sendJSON<T>(method: 'PUT' | 'DELETE', url: string, body?: unknown): Promise<T> {
  const r = await acFetch(url, {
    method,
    headers: { 'Content-Type': 'application/json' },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!r.ok) {
    const detail = await r.json().catch(() => ({ detail: r.statusText }));
    throw new Error(`${r.status} ${detail.detail ?? r.statusText}`);
  }
  return r.json() as Promise<T>;
}

/**
 * Re-probe `/api/account/health` after credentials change.
 *
 * The panel reports itself unavailable when no wallet resolves, so saving a wallet must
 * re-ask rather than wait for the next poll — otherwise the user saves a key and stares at
 * the same error, which is exactly the dead end this whole feature removes.
 */
async function acReprobeHealth(): Promise<void> {
  try {
    accountHealth = await getJSON<AccountHealth>('/api/account/health');
    tpError = null;
  } catch (err) {
    tpError = `trade panel unavailable: ${err}`;
  }
}

/**
 * Attach handlers to the wallet form.
 *
 * Called after every render because `innerHTML` replaces the nodes — the same pattern the
 * other tabs use. Listeners are attached to freshly-created elements, so no removal is
 * needed.
 */
function wireWalletForm() {
  const addr = document.getElementById('creds-address') as HTMLInputElement | null;
  const key = document.getElementById('creds-key') as HTMLInputElement | null;
  const testnet = document.getElementById('creds-testnet') as HTMLInputElement | null;
  const save = document.getElementById('creds-save') as HTMLButtonElement | null;
  const del = document.getElementById('creds-delete') as HTMLButtonElement | null;
  const msg = document.getElementById('creds-msg');
  if (!addr || !key || !save || !msg) return;

  const say = (text: string, cls: '' | 'err' | 'ok' = '') => {
    msg.textContent = text;
    msg.className = cls;
  };

  save.addEventListener('click', () => {
    const input = {
      address: addr.value,
      privateKey: key.value,
      testnet: Boolean(testnet?.checked),
    };
    // Client-side validation FIRST, so a typo costs no round trip and no server log line
    // mentioning a rejected credential. The server validates again — it must, since it is
    // the trust boundary — but the user gets the specific message here.
    const problem = validate(input);
    if (problem) {
      say(problem, 'err');
      return;
    }
    save.disabled = true;
    say('saving…');
    void sendJSON<CredentialState>('PUT', '/api/account/credentials', {
      address: input.address.trim(),
      private_key: input.privateKey.trim(),
      testnet: input.testnet,
    })
      .then((res: CredentialState) => {
        credState = res;
        // CLEAR THE KEY FIELD IMMEDIATELY on success. Leaving a signing key in a DOM node
        // for the lifetime of the tab is a needless exposure — it is already stored, and
        // the form is write-only by design.
        key.value = '';
        say('saved — reloading the account panel', 'ok');
        // The account panel was unavailable BECAUSE there was no wallet; re-probe health so
        // it comes alive without a manual refresh.
        void acReprobeHealth().then(() => {
          tpRender();
          void tpLoad(true);
        });
      })
      .catch((err: unknown) => {
        say(String((err as Error)?.message ?? err), 'err');
        save.disabled = false;
      });
  });

  del?.addEventListener('click', () => {
    // No confirm() dialog: removal is non-destructive to funds and immediately reversible
    // by re-entering the key. A confirm on a safe action trains people to click through
    // confirms on unsafe ones.
    del.disabled = true;
    say('removing…');
    void sendJSON<CredentialState>('DELETE', '/api/account/credentials')
      .then((res: CredentialState) => {
        credState = res;
        say('credentials removed', 'ok');
        void acReprobeHealth().then(() => tpRender());
      })
      .catch((err: unknown) => {
        say(String((err as Error)?.message ?? err), 'err');
        del.disabled = false;
      });
  });
}

/**
 * Latest Telegram alert state from `/api/account/telegram`. NEVER holds a bot token — the server
 * does not return one, so there is nothing here to leak.
 */
let alertState: AlertState | null = null;

/**
 * Attach handlers to the Telegram alerts form.
 *
 * REUSES `sendJSON` and the same shape as `wireWalletForm`, deliberately: this is the second
 * write-only credential form in the app and it should submit the same way the first one does. A
 * second submission helper would be a second answer to "how does a secret leave this page".
 */
function wireAlertsForm() {
  const token = document.getElementById('tg-token') as HTMLInputElement | null;
  const chat = document.getElementById('tg-chat') as HTMLInputElement | null;
  const save = document.getElementById('tg-save') as HTMLButtonElement | null;
  const del = document.getElementById('tg-delete') as HTMLButtonElement | null;
  const msg = document.getElementById('tg-msg');
  if (!token || !chat || !save || !msg) return;

  const say = (text: string, cls: '' | 'err' | 'ok' = '') => {
    msg.textContent = text;
    msg.className = cls;
  };

  // ─── the stop-loss monitoring checkbox. AN INDEPENDENT PRODUCT ON THE SAME PANEL: its own
  // endpoint (`PUT /api/account/sl-monitor`), its own message element, its own state. It is neither
  // read from nor written to `/api/account/telegram` — that endpoint does not carry the flag, and
  // adding it there would make a second writer for one attribute.
  //
  // DISABLED WITHOUT A WALLET, by render, because the server refuses the write without an address.
  // No request is attempted in that state, so there is no success line to mistake for one.
  //
  // THE CHECKBOX IS REVERTED ON FAILURE and reflects the SERVER's value on success. A box left
  // ticked after a refused save is a UI claiming a subscription the server does not hold — the one
  // thing this control must never do is make someone believe their stops are watched when they
  // are not.
  const slmon = document.getElementById('tg-slmon') as HTMLInputElement | null;
  const slmonMsg = document.getElementById('tg-slmon-msg');
  slmon?.addEventListener('change', () => {
    const wanted = slmon.checked;
    const sayMon = (text: string, cls: '' | 'err' | 'ok' = '') => {
      if (!slmonMsg) return;
      slmonMsg.textContent = text;
      slmonMsg.className = cls;
    };
    slmon.disabled = true;
    sayMon('saving…');
    void sendJSON<{ sl_monitor_enabled?: boolean }>('PUT', '/api/account/sl-monitor', {
      enabled: wanted,
    })
      .then((res) => {
        // THE SERVER'S VALUE, not `wanted`: it is the one that was stored.
        const on = res?.sl_monitor_enabled === true;
        slmon.checked = on;
        if (credState) credState.sl_monitor_enabled = on;
        sayMon(on ? 'watching your stops — one message a morning' : 'not watching your stops', 'ok');
      })
      .catch((err: unknown) => {
        slmon.checked = !wanted;
        sayMon(String((err as Error)?.message ?? err), 'err');
      })
      .finally(() => {
        slmon.disabled = false;
      });
  });

  const submit = (ev?: Event) => {
    ev?.preventDefault();
    // Client-side validation FIRST, so a typo costs no round trip and no server log line about a
    // rejected token. The server validates again — it must, it is the trust boundary.
    const problem = validateAlerts(token.value, chat.value);
    if (problem) {
      say(problem, 'err');
      return;
    }
    save.disabled = true;
    say('saving…');
    void sendJSON<AlertState>('PUT', '/api/account/telegram', {
      bot_token: token.value.trim(),
      chat_id: chat.value.trim(),
    })
      .then((res: AlertState) => {
        alertState = res;
        // CLEAR THE TOKEN FIELD IMMEDIATELY on success. Leaving a credential in a DOM node for the
        // lifetime of the tab is a needless exposure — it is stored, and the form is write-only.
        token.value = '';
        say('alerts are on — the next morning scan goes to your chat', 'ok');
        void profileRender();
      })
      .catch((err: unknown) => {
        say(String((err as Error)?.message ?? err), 'err');
        save.disabled = false;
      });
  };

  document.getElementById('tg-form')?.addEventListener('submit', submit);
  save.addEventListener('click', submit);

  del?.addEventListener('click', () => {
    // No confirm(): turning alerts off is immediately reversible by re-entering the token, and a
    // confirm on a safe action trains people to click through confirms on unsafe ones.
    del.disabled = true;
    say('turning off…');
    void sendJSON<AlertState>('DELETE', '/api/account/telegram')
      .then((res: AlertState) => {
        alertState = res;
        say('alerts are off and your bot token has been deleted', 'ok');
        void profileRender();
      })
      .catch((err: unknown) => {
        say(String((err as Error)?.message ?? err), 'err');
        del.disabled = false;
      });
  });
}

/** Fetch the credential state. Safe to call when unauthenticated — a 401 just clears it. */
async function acLoadCredentials(): Promise<void> {
  try {
    credState = await getJSON<CredentialState>('/api/account/credentials');
  } catch {
    // Not fatal and not worth an error banner: an unconfigured or unauthenticated state is
    // exactly what the wallet tab exists to resolve, and it renders its own empty state.
    credState = null;
  }
}

function tpRender() {
  tpRenderChrome();
  tpRenderBody();
  // THE TICKET LIVES OUTSIDE THIS PANEL and reads two things from the snapshot the panel
  // just refreshed (equity, and whether writes are armed), so it has to be refreshed with
  // it -- otherwise a ticket sized against a stale equity sits beside a correct table.
  //
  // `false` = PREVIEW ONLY, deliberately: this runs on the 15 s poll, and a full render
  // would rewrite the form's `<input>` elements under a user who is typing in one. Same
  // reason `tkSchedulePreview(false)` exists.
  tkRender(false);
}

async function tpLoad(force = false) {
  if (!accountHealth?.ok) return;
  tpLoading = true;
  tpRenderChrome();
  try {
    snapshot = await getJSON<Snapshot>(`/api/account/snapshot?days=30${force ? '&force=true' : ''}`);
    tpError = null;
  } catch (err) {
    // Kept visible in the sub-bar rather than silently leaving stale tables: an
    // account panel that quietly stops updating is worse than one that says so.
    tpError = `account snapshot: ${err}`;
  } finally {
    tpLoading = false;
    // COMMITTED PRICES COME FROM HERE AND NOWHERE ELSE. `sync` keeps any staged (dragged,
    // un-sent) price, so a 15 s poll landing mid-gesture cannot wipe a stop the user is
    // still positioning — and it DROPS a position that closed, staged price and all.
    liveLevels.sync(snapshot);
    tpRender();
    tpDrawLevels();
  }
}

/** Read the scan's recent setups. Own funnel, own error line — see `setupsPayload`.
 *
 *  NOT polled. The scan writes these rows a few times a day, so a 15 s timer would be
 *  ~5,000 reads a day of a table that changed three times. It is read when the tab is
 *  opened and when the refresh button is pressed, which is exactly when the answer can
 *  have changed for the person looking at it. */
async function tpLoadSetups() {
  try {
    // NO `?limit=` (2026-08-18). It used to ask for 40, and prod holds 87 rows, so a real
    // sent xyz:BABA alert at newest-first index 41 was absent from the tab with nothing
    // on screen saying why. The server now returns everything plus `total`/`truncated`.
    setupsPayload = await getJSON<SetupsPayload>('/api/setups');
    setupsError = null;
  } catch (err) {
    // Visible, not swallowed: an empty setups table and an unreadable one must not
    // look the same. The same rule the account tables follow.
    setupsError = String(err);
  }
  if (tpTab === 'setups') {
    tpRenderBody();
    // The rows only exist once the read lands, so the restore has to happen HERE too:
    // arriving at the tab renders an empty table first, and a scroll against that does
    // nothing. Idempotent — it is a no-op when no cursor row is rendered.
    tpScrollToCursor();
  }
}

/** Fetch whatever `tab` renders from, when it is NOT the account snapshot.
 *
 *  ONE FUNNEL FOR EVERY ROUTE INTO A TAB, and that is the whole point. The read used to
 *  live inline in the tab-strip click handler, so the OTHER route in -- the tab restored
 *  from `localStorage` by `tpRestoreGeometry` on boot -- rendered the Setups table
 *  without anybody asking the server for rows. Andrei's HAR proves it: 44 requests, 0
 *  non-2xx, `account/snapshot` polled 6 times, and NOT ONE request whose URL contains
 *  "setups". He saw the empty state because nothing had fetched, not because the table
 *  was empty (the dev alerts table held 25 rows at the time).
 *
 *  READS ONLY. `GET /api/setups` is a scan of ~25-67 DynamoDB rows. `POST
 *  /api/setups/scan` -- the 21-minute, 132-asset Step Functions execution that Telegrams
 *  Andrei -- is reachable ONLY from the `tp-scan` button via `tpTriggerScan`, and must
 *  never be reachable from here or from any timer. `scanStarting` guards that button and
 *  is deliberately not touched by these reads. */
function tpLoadForTab(tab: TabId): void {
  // Read on OPEN. Neither table is polled (see `tpLoadSetups`), so arriving at the tab
  // is the moment to find out whether the scan or the reviewer has run since last time.
  if (tab === 'setups') void tpLoadSetups();
  if (tab === 'advice') void tpLoadAdvice();
}

/** Read the morning stop review's record. Own funnel, own error line.
 *
 *  NOT polled, for the same reason the setups table is not: the reviewer writes these
 *  rows once a morning, so a 15 s timer would be thousands of reads a day of a table
 *  that changed once. Read when the tab is opened, which is when the answer can have
 *  changed for the person looking at it. */
async function tpLoadAdvice() {
  try {
    advicePayload = await getJSON<AdvicePayload>('/api/manage/advice?limit=60');
    adviceError = null;
  } catch (err) {
    // Visible, not swallowed. An unreadable record and a morning with no advice must
    // not look the same — the whole point of keeping the record is that its silences
    // are evidence.
    adviceError = String(err);
  }
  if (tpTab === 'advice') tpRenderBody();
}

/** Start the daily scan NOW, and say what it is doing until it finishes.
 *
 *  Andrei: *"I should have an ability to push a button and trigger refresh myself at
 *  any time."* This starts the SAME Step Functions execution the 07:05 America/New_York schedule
 *  starts — same definition, same Lambdas, same writer — so the button and the
 *  morning brief cannot advise different setups.
 *
 *  IT POLLS RATHER THAN SPINNING. A real scan is 337 assets plus ten chart renders:
 *  MINUTES, not seconds. A spinner would imply it is nearly done and a fire-and-forget
 *  "started" would leave him re-clicking the button to find out. So the button states
 *  the elapsed time in words, he keeps working, and the table is re-read exactly once
 *  when the execution reports SUCCEEDED — the only moment at which there is anything
 *  new to read. Polling every 20 s over minutes is a handful of DescribeExecution
 *  calls, which is why this one is polled and the setups table is not.
 */
async function tpTriggerScan() {
  // THE GUARD IS SET BEFORE THE FIRST `await`, AND THAT IS THE WHOLE POINT.
  // `scanExecution` was the only re-entry guard, and it is not assigned until the POST
  // RESOLVES. Between the click and that response there is an await, so two clicks a
  // second apart both saw `scanExecution === null`, both passed the check and both
  // POSTed a bare trigger: TWO 21-minute Step Functions executions, twice the Lambda
  // spend, and two real Telegram messages to Andrei. `scanStarting` closes that window
  // synchronously -- a flag set in the same tick as the click cannot be raced by a
  // second click, because the event loop cannot interleave before the first `await`.
  if (scanStarting || scanExecution) {
    scanNote = 'A scan is already running. The table updates when it finishes.';
    tpRenderBody();
    return;
  }
  scanStarting = true;
  scanNote = 'Starting the scan…';
  tpRenderBody();
  try {
    const r = await postJSON<{ available: boolean; reason?: string; execution_arn?: string }>(
      '/api/setups/scan', {},
    );
    if (!r.available || !r.execution_arn) {
      // NOT FAKED. If the app cannot start a scan here, it says so and re-reads the
      // table instead — which is still useful (the 07:05 America/New_York run may have written rows
      // since the tab was opened) and is honest about what the button just did.
      scanNote = `The scan cannot be started from here: ${r.reason ?? 'not connected'}. Re-read the table instead.`;
      await tpLoadSetups();
      tpRenderBody();
      return;
    }
    scanExecution = r.execution_arn;
    scanStartedAt = Date.now();
    // THE CLOCK TIME, NOT "a few minutes". He reads this while trading and wants to know
    // whether the thing he started is the thing still running, which "a few minutes" ago
    // cannot answer after he has looked away twice. `hhmm` is local wall-clock.
    scanNote = `Scan running, started ${hhmm(scanStartedAt)} — this takes about 20 minutes.`;
    tpRenderBody();
    tpPollScan();
  } catch (err) {
    scanNote = `The scan could not be started: ${err}`;
    tpRenderBody();
  } finally {
    // Cleared whichever way the POST went. On success `scanExecution` has taken over as
    // the guard; on failure the button must be pressable again, so a single transport
    // blip cannot wedge the feature shut until a reload.
    scanStarting = false;
  }
}

/** Ask how the running scan is doing, until it is not running. */
function tpPollScan() {
  if (scanTimer !== null) window.clearTimeout(scanTimer);
  scanTimer = window.setTimeout(async () => {
    const arn = scanExecution;
    if (!arn) return;
    const mins = Math.max(1, Math.round((Date.now() - scanStartedAt) / 60000));
    try {
      const r = await postJSON<{ status?: string }>(
        `/api/setups/scan?execution_arn=${encodeURIComponent(arn)}`, {},
      );
      if (r.status === 'RUNNING') {
        scanNote = `Scan running, started ${hhmm(scanStartedAt)} (${mins}m so far) — this takes about 20 minutes.`;
        tpRenderBody();
        tpPollScan();
        return;
      }
      scanExecution = null;
      if (r.status === 'SUCCEEDED') {
        await tpLoadSetups();
        // THE COUNT IS READ BACK, NOT ASSUMED. "Scan finished" beside an empty table is
        // the exact ambiguity this task exists to kill: he cannot tell a scan that found
        // nothing from a scan whose rows failed to load. So the sentence reports what the
        // re-read actually returned, and says nothing when the re-read itself failed --
        // `setupsError` is already on screen in that case and a count would contradict it.
        const found = setupsError ? null : (setupsPayload?.setups.length ?? null);
        scanNote = found === null
          ? `Scan finished after ${mins}m, but the table could not be re-read.`
          : found === 0
            ? `Scan finished after ${mins}m and found no setups.`
            : `Scan finished after ${mins}m and found ${found} ${found === 1 ? 'setup' : 'setups'}.`;
      } else {
        // A FAILED scan is reported as failed. Silently falling back to "finished"
        // would present a stale table as a fresh one.
        scanNote = `The scan did not finish (${String(r.status ?? 'unknown').toLowerCase()}) after ${mins}m. These rows are from the previous run.`;
      }
      tpRenderBody();
    } catch (err) {
      // A failed status read does not mean a failed scan, so the execution is kept and
      // polling continues — reporting it as dead would be a guess.
      scanNote = `Scan running, started ${hhmm(scanStartedAt)} (${mins}m so far) — its progress could not be read just now: ${err}`;
      tpRenderBody();
      // BOUNDED. This is the only branch that re-arms without a RUNNING answer from the
      // server, so it is the only one that could spin forever on a broken status read.
      if (Date.now() - scanStartedAt < SCAN_POLL_GIVEUP_MS) {
        tpPollScan();
        return;
      }
      // Give up SAYING SO. `scanExecution` is cleared so the button works again, and the
      // sentence claims no outcome -- the scan may well have succeeded, and asserting
      // either way would be the same lie in a new place.
      scanExecution = null;
      scanNote = `The scan was started at ${hhmm(scanStartedAt)} and this app lost track of it. Press Refresh setups to re-read the table.`;
      tpRenderBody();
    }
  }, SCAN_POLL_MS);
}

function tpStartPolling() {
  if (tpTimer !== null) window.clearInterval(tpTimer);
  tpTimer = null;
  if (!accountHealth?.ok) return;
  if (localStorage.getItem(TP_KEYS.poll) === 'off') return;
  // COLLAPSED = NOT POLLING. Measured from Andrei's HAR H5: 4 snapshot calls consumed 12.8 s of the
  // 50 s steady-state window (median 3341 ms), second only to the live polls — and it ran whether or
  // not the panel was on screen. Nobody is reading a table they have collapsed.
  //
  // Gated HERE rather than inside `tpLoad`, because the timer itself is the waste: a guard in the
  // callback still wakes up every 15 s and still has to decide. `tpLoad(true)` — the refresh button
  // and the post-action re-read — is untouched, so a deliberate check always works, collapsed or not.
  if (document.body.classList.contains('tp-collapsed')) return;
  tpTimer = window.setInterval(() => {
    // HIDDEN TAB = NOT POLLING, the same rule and for the same reason as `/api/live` (livepoll.ts).
    // This is the SLOWEST route in the app -- production CloudWatch, 2 h window: 200 calls, 1383 ms
    // avg, 4471 ms max, because a snapshot fans out over 10 live dexes x 3 /info calls -- and it was
    // firing on a background tab. Andrei's VPN exits in FRANCE, so each of those paid a transatlantic
    // round trip to refresh a positions table nobody was looking at.
    //
    // Gated in the CALLBACK here, not by clearing the timer as the collapse check does above,
    // because visibility flips constantly while a collapse is a deliberate act: tearing down and
    // rebuilding the interval on every alt-tab is more moving parts than one free native read.
    // `document.visibilityState` costs nothing.
    if (document.visibilityState === 'hidden') return;
    void tpLoad();
  }, TP_POLL_MS);
}

/**
 * Returning to the tab re-reads the account NOW.
 *
 * The other half of the gate above. A positions table is the one panel where "stale but silent" is
 * unacceptable -- it is the money path -- so skipping the hidden polls is only safe because coming
 * back is an immediate read. `tpLoad()` without `force`, so the backend's 12 s `SNAPSHOT_TTL_S` still
 * absorbs a burst of alt-tabbing rather than launching a 33-request sweep per focus event.
 *
 * Registered once at module scope, guarded on the timer existing -- so a collapsed panel, a
 * `poll=off` preference, or an unhealthy account stays quiet on focus exactly as it does on the timer.
 */
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') return;
  if (tpTimer === null) return;
  void tpLoad();
});

/**
 * MOUSE-UP ON A DRAGGED LEG: validate, then ASK. Never write.
 *
 * This is the whole confirm boundary of the feature (card t_56bf1670), and it is three
 * steps in this exact order — the same ordering `manage.py::_modify_sl` uses (rules,
 * then `--confirm`, then the write):
 *
 *  1. VALIDATE (`validateStaged`, zero network calls). A refusal REVERTS the staged
 *     price on every pane and states the reason in the status bar. Nothing is clamped:
 *     a clamped stop is a price the user did not choose, drawn as though they had.
 *  2. ASK, through the EXISTING dialog (`tpOpenAction` -> `/api/trade/modify`, i.e.
 *     `modify-sl` / `modify-tp`). No second endpoint, no parallel arming mechanism —
 *     the dialog's own "writes disabled" note and `buttonState` gating apply unchanged.
 *  3. Only a press of Apply sends anything, and only a `/info` read-back promotes the
 *     staged price to committed (`tpApplyAction`).
 *
 * The dialog is opened with the POSITION ID the primitive was bound to, carried from the
 * drag handle — never the hovered row and never the nearest line.
 */
function tpCommitStaged(id: string, leg: PendingLeg, price: number) {
  // The validate-and-compose half is `stagedModifyRequest`, so what a test asserts about
  // the refusal and about the request's verb/symbol/params is what this path does.
  const { refusal, request } = stagedModifyRequest(liveLevels, id, leg, price);
  if (refusal || !request) {
    // REVERT, not clamp: back to whatever the order graph says, on all four panes at
    // once. No `tpDrawLevels()` here — `setPending` emits and the one subscription at
    // the pane wiring redraws every pane (main.ts:1016).
    liveLevels.setPending(id, leg, null);
    setStatus(`refused: ${refusal}`, true);
    return;
  }
  const p = liveLevels.get(id);
  if (!p) return;
  tpOpenAction(request.verb, request.symbol, {
    // NO copy HERE — the title, the button and the price field's own label all come from
    // the verb (`actionCopy`, card t_c977f6d4). This call site used to pass the heading
    // and the field label, which is how the heading and the button came to name different
    // legs. All it supplies now is the VALUE: pre-filled from the DRAG, still editable,
    // and the field is what gets sent, so a user who typed over it gets what they typed.
    price,
    note: commitSummary(p, leg, price),
  });
  // AFTER `tpOpenAction`, which clears it: this is what tells the post-write read-back
  // WHICH price to hold the exchange to, and which staged line to drop either way.
  tpStagedCommit = { id, leg, price };
}

/** The drag whose confirm dialog is open, so the post-write `/info` read-back can be
 *  checked against the price that was confirmed. Null for a dialog opened from a table
 *  button — that path types its own number and has no staged price to promote. */
let tpStagedCommit: { id: string; leg: PendingLeg; price: number } | null = null;

// ── the two-phase write flow ───────────────────────────────────────────────
/** Open the dialog for one action. Nothing is sent until Dry-run is pressed. */
function tpOpenAction(verb: string, symbol: string, opts: {
  /**
   * The starting value of the verb's price field. The NAME and the LABEL are not the
   * caller's to choose — they come from `actionCopy(verb).price`, so a table button and a
   * drag cannot label the same leg differently (card t_c977f6d4). Omitted or `null`
   * leaves the field blank; `undefined` for a verb with no price field is a no-op.
   */
  price?: number | null;
  fixed?: Record<string, unknown>;
  /** Rendered into the result area on open: what exactly is about to be sent. */
  note?: string;
} = {}) {
  // ONE SOURCE FOR THE HEADING AND FOR THE BUTTON (card t_c977f6d4). `label` used to be a
  // required opt every call site typed by hand, while `#tp-apply`'s label was a STATIC
  // string in index.html that nothing ever rewrote — so a take-profit dialog carried a
  // confirm button reading "Set stop", on the control that IS the write. Both strings now
  // come from the verb, in `oneclick.actionCopy`, which is also where a test can hold them
  // to naming the same leg.
  const copy = actionCopy(verb);
  tpAction = {
    verb,
    symbol,
    label: copy.title,
    fields: copy.price
      ? [{ name: copy.price.name, label: copy.price.label, value: opts.price ?? null, step: 0.0001 }]
      : [],
    fixed: opts.fixed ?? {},
    token: null,
  };
  // ANY dialog opening clears the drag's read-back subject, and `tpCommitStaged` re-sets
  // it AFTER this call. Otherwise a table-button dialog opened after an abandoned drag
  // would inherit that drag's id and promote a price nobody confirmed for it.
  tpStagedCommit = null;
  el.tpDlgTitle.textContent = `${copy.title} — ${symbol}`;
  // THE LABEL ON THE CONTROL THAT SENDS IT. Set here, from the SAME `copy` row as the
  // title above, so the two cannot name different legs. index.html's static `Set stop`
  // is now only the pre-open placeholder.
  el.tpApply.textContent = copy.button;
  el.tpDlgInputs.innerHTML = tpAction.fields
    .map(
      (f) =>
        `<label><span>${f.label}</span><input type="number" step="${f.step}" id="tp-f-${f.name}" value="${
          f.value ?? ''
        }" /></label>`,
    )
    .join('');
  el.tpDlgResult.innerHTML = opts.note ? `<div class="tp-note">${opts.note}</div>` : '';
  tpPhase = 'idle';
  tpSyncButtons();
  // NO ADVISORY PREVIEW (card t_a23d9cc5, Andrei: "no need to execute dry-runs or
  // something"). The `/api/trade/preview` verdict panel that used to update as the
  // user typed is gone from this dialog: it was `authoritative: false` by its own
  // payload, it used a different liq buffer than the binding check (2% vs 0.5%), and
  // it explained a refusal that had not happened yet. The BINDING refusal arrives
  // from the write itself, in one request, with the venue's own sentence. Editing a
  // field only resets the phase so the button is pressable again.
  for (const f of tpAction.fields) {
    document.getElementById(`tp-f-${f.name}`)
      ?.addEventListener('input', () => { tpPhase = 'idle'; tpSyncButtons(); });
  }
  el.tpDialog.showModal();
}

/**
 * Reflect the dialog's phase onto its buttons + note. The DECISION is the pure
 * `buttonState`/`dialogNote` pair in `oneclick.ts`; this only applies it, so the
 * rule that "a refusal leaves no pressable write control" is testable without a DOM.
 */
function tpSyncButtons() {
  const armed = Boolean(accountHealth?.trading.trading_enabled);
  const st = buttonState({
    verb: tpAction?.verb ?? '',
    phase: tpPhase,
    hasToken: Boolean(tpAction?.token),
    armed,
  });
  const put = (btn: HTMLButtonElement, s: { shown: boolean; enabled: boolean }) => {
    btn.style.display = s.shown ? '' : 'none';
    btn.disabled = !s.enabled;
  };
  put(el.tpApply, st.apply);
  put(el.tpDryrun, st.dryrun);
  put(el.tpConfirm, st.confirm);
  el.tpDlgNote.textContent = dialogNote(
    { verb: tpAction?.verb ?? '', phase: tpPhase, hasToken: Boolean(tpAction?.token), armed },
    accountHealth?.trading.arm_reason ?? null,
  );
}

/**
 * ONE CLICK: the whole write, in one request (`/api/trade/modify`).
 *
 * `modify-sl` / `modify-tp` only — enforced server-side by `_MODIFY_VERBS`; `isOneClick`
 * here only decides which button is shown. THIS DIALOG IS THE CONFIRMATION (cards
 * t_824b65c5, t_a23d9cc5): there is no preview stage, no dry-run request, no plan token,
 * no advisory verdict panel, and nothing about an argv or a server stdout reaches the
 * user. The server rounds the price to the venue's own precision, performs the
 * cancel+replace in-process via ccxt, and re-reads the book.
 *
 * THE READ-BACK IS THE ONLY EVIDENCE THE DIALOG OFFERS, and it now runs on BOTH paths —
 * the drag and the typed table-button dialog — because the typed path is exactly where a
 * free-form number gets rounded to the venue's tick and the user needs to see the rounded
 * truth. `run.confirmed` is a self-report and it has lied (`cancel` returned
 * `{"cancelled": false, "error": null}` for orders already gone, §0b.1), so this re-reads
 * the account snapshot (`?force=true`, i.e. the ORDER GRAPH from `/info`) and renders what
 * the venue now says. A price that came back rounded is NORMAL and labelled as rounding,
 * not as a warning — the server's ccxt `price_to_precision` is the rounding authority and
 * there is deliberately no client-side rounder to disagree with it.
 *
 * The staged drag override is dropped either way: what the panes draw afterwards is the
 * committed price `sync()` just took from `/info`, never the price we hoped for.
 */
async function tpApplyAction() {
  if (!tpAction || !isOneClick(tpAction.verb)) return;
  const staged = tpStagedCommit;
  tpStagedCommit = null;
  const kind = tpAction.verb === 'modify-sl' ? 'SL' : 'TP';
  const symbol = tpAction.symbol;
  const params = tpParams();
  // The number the user is holding the venue to: whatever is in the field, which on the
  // drag path was pre-filled from the gesture and may since have been typed over.
  const typed = Number(kind === 'SL' ? params.stop : params.tp);
  // ── THE ONE CLIENT-SIDE CHECK, WHERE BOTH UI PATHS CONVERGE (R4-9) ──────────
  //
  // `validateStaged` used to be reachable ONLY from the drag (`tpCommitStaged`), so the
  // Positions-table button — `tpHandleActionClick` -> `tpOpenAction('modify-tp', …)` —
  // walked straight to `/api/trade/modify` past every one of its refusals. Two entry
  // points to one mutation, one of them unguarded: a user whose drag was refused got a
  // different answer, or none, by pressing the button instead.
  //
  // IT IS HERE, NOT IN EITHER HANDLER, because this is the only line both paths cross —
  // and it also closes a hole the drag path had on its own: the dialog's field is
  // EDITABLE by design, so a price refused at mouse-up could be typed back in and sent.
  // The check runs on the number that is about to leave the browser, which is the only
  // number worth checking.
  //
  // IT IS CONVENIENCE, NOT THE GUARANTEE. The binding refusals stay on the server
  // (`risk_gates` RULE 5 / RULE 3 for a stop, RULE 11 for a target, and
  // `manage._rung_to_replace` for a ladder rung) and nothing here is a substitute for
  // them: the only claim is that an invalid level costs zero network calls and gets the
  // same sentence from either entry point. `null` from `validateStaged` never means
  // "approved".
  const leg: PendingLeg = kind === 'SL' ? 'sl' : 'tp';
  const refusal = validateStaged(liveLevels, symbol, leg, typed);
  if (refusal) {
    el.tpDlgResult.innerHTML = `<div class="tp-note bad">refused: ${esc(refusal)}</div>`;
    tpPhase = 'refused';
    // A staged drag line for a price we will not send must not keep looking armed.
    if (staged) liveLevels.setPending(staged.id, staged.leg, null);
    tpSyncButtons();
    return;
  }
  tpPhase = 'in-flight';
  tpSyncButtons();
  el.tpDlgResult.innerHTML = '<div class="tp-note warn">sending to the exchange…</div>';
  try {
    // ONE number leaves the browser. `size` is deliberately not sent: the server
    // reads it off the live position, and a client-supplied size would protect a
    // position that may not exist.
    const run = await postJSON<ModifyRun>('/api/trade/modify', {
      verb: tpAction.verb,
      symbol,
      price: typed,
    });
    el.tpDlgResult.innerHTML = renderModifyRun(run);
    tpPhase = run.refused ? 'refused' : 'done';
    if (run.refused) {
      // The book was NOT changed, so the staged price is a level the exchange refused:
      // revert it rather than leaving a line that looks armed. (`setPending` emits; the
      // model's one subscription repaints every pane.)
      if (staged) liveLevels.setPending(staged.id, staged.leg, null);
      return;
    }
    await tpLoad(true);
    if (staged) liveLevels.setPending(staged.id, staged.leg, null);
    if (Number.isFinite(typed)) {
      const pos = snapshot?.positions.find((p) => p.coin === symbol) ?? null;
      // `run.price` IS what went to the wire (the server's ccxt `price_to_precision`),
      // so THAT is what the venue is held to; `typed` is only what the user asked for and
      // the gap between them is disclosed as rounding. Holding the venue to `typed` inside
      // a relative tolerance was redteam D6 — a ±$65 blind window on BTC.
      //
      // `placedOrderId(run)` is the ORDER the write created, and matching the resting leg
      // to it is what makes the read-back evidence (R4-1/R4-2 review, 2026-08-21). The old
      // call passed no id and `renderReadback` scanned for the first leg of that `kind`,
      // while the server moves the NEAREST rung — so on a ladder the read-back reported a
      // leg the write never touched, in both directions.
      el.tpDlgResult.innerHTML += renderReadback(kind, typed, pos, run.price, placedOrderId(run));
    }
  } catch (err) {
    // THE VENUE'S OWN SENTENCE, VERBATIM. `postJSON` throws `${status} ${detail}`, and
    // `detail` is the refusal text the backend received. No "something went wrong": a
    // generic message hides the one fact that decides what to do next.
    el.tpDlgResult.innerHTML = `<div class="tp-note bad">${esc(String(err))}</div>`;
    tpPhase = 'refused';
    if (staged) liveLevels.setPending(staged.id, staged.leg, null);
  } finally {
    tpSyncButtons();
  }
}

/** Read the dialog's params. Blank inputs are OMITTED, not zeroed — `close` with
 *  no size means a full close, and coercing an empty field to 0 would turn that
 *  into an invalid order. */
function tpParams(): Record<string, unknown> {
  const out: Record<string, unknown> = { ...(tpAction?.fixed ?? {}) };
  for (const f of tpAction?.fields ?? []) {
    const input = document.getElementById(`tp-f-${f.name}`) as HTMLInputElement | null;
    const raw = input?.value?.trim();
    if (raw) out[f.name] = Number(raw);
  }
  return out;
}

async function tpDryRun() {
  if (!tpAction) return;
  tpPhase = 'in-flight';
  tpSyncButtons();
  el.tpDlgResult.innerHTML = '<div class="tp-note">running the safety checks…</div>';
  try {
    const run = await postJSON<TradeRun>('/api/trade/plan', {
      verb: tpAction.verb,
      symbol: tpAction.symbol,
      params: tpParams(),
    });
    tpAction.token = run.plan_token ?? null;
    el.tpDlgResult.innerHTML = renderTradeRun(run);
    // Confirm unlocks ONLY on a clean dry-run that minted a token AND with writes
    // armed. Three independent conditions, none of them inferable from the others —
    // now expressed once, in `buttonState`, where a test can hold them.
    tpPhase = run.refused ? 'refused' : (run.plan_token ? 'planned' : 'idle');
  } catch (err) {
    el.tpDlgResult.innerHTML = `<div class="tp-note bad">${String(err)}</div>`;
    tpPhase = 'refused';
  } finally {
    tpSyncButtons();
  }
}

async function tpConfirmAction() {
  // ONE PRESS FOR A CLOSING VERB (card t_21d2c005). `close` no longer has a Dry-run
  // stage, so there is no token to hold when Confirm is pressed: this branch mints one
  // and spends it inside the click. It is the SAME argv-bound two-request path
  // `tpCancelNow` uses — what was removed is the HUMAN step, not the binding. The token
  // never reaches the user because a token is not a decision they can inform.
  if (tpAction && isNoDryRun(tpAction.verb) && !tpAction.token) return void tpConfirmNow();
  if (!tpAction?.token) return;
  tpPhase = 'in-flight';
  tpSyncButtons();
  el.tpDlgResult.innerHTML = '<div class="tp-note warn">sending to the exchange…</div>';
  try {
    const run = await postJSON<TradeRun>('/api/trade/execute', {
      verb: tpAction.verb,
      symbol: tpAction.symbol,
      // The SAME params the token was minted over. Re-reading the inputs is
      // deliberate: if the user edited a field after the dry-run, the token will
      // not verify and the backend refuses — which is exactly the intent.
      params: tpParams(),
      plan_token: tpAction.token,
    });
    el.tpDlgResult.innerHTML = renderTradeRun(run);
    // A confirmed action invalidates the token (single use, by policy here) and
    // demands a RE-READ: exec_tools' own `confirmed` flag is a self-report.
    tpAction.token = null;
    tpPhase = run.refused ? 'refused' : 'done';
    if (!run.refused) await tpLoad(true);
  } catch (err) {
    el.tpDlgResult.innerHTML = `<div class="tp-note bad">${String(err)}</div>`;
    tpPhase = 'refused';
  } finally {
    tpSyncButtons();
  }
}

/**
 * A CLOSING VERB'S WHOLE WRITE, IN ONE PRESS OF CONFIRM AND IN ONE REQUEST.
 *
 * Andrei, 2026-08-25, on the close dialog: *"закрывать сразу без дополнительных
 * проверок на бекенде, но с подтверждением пользователя … просто дать возможность
 * нажать confirm"*. The modal stays (a close realises PnL, so a mis-click must cost a
 * deliberate press in a dialog naming the symbol); the PREVIEW goes.
 *
 * WHAT THE PREVIEW WAS. Measured in `exec_tools/cli/manage.py::_close`: no risk gate at
 * all. Its refusals are argument validation — `--size`/`--pct` mutually exclusive,
 * `--pct` in (0, 100], `--size` positive and no larger than the venue's own position
 * size — and every one of them sits ABOVE the `--confirm` branch, so they still run on
 * this confirmed write and arrive in the server's own words. The dry-run was a preview of
 * a risk-REDUCING act, which is the argument `cancel` won on 2026-08-13 and the reason
 * `place` keeps its two phases.
 *
 * MEMBERSHIP, NOT A PARAMETER (`isNoDryRun`). There is no argument a future call site
 * could pass to route `place` through here.
 *
 * THE PLAN CALL IS GONE FROM THIS PATH, and that is card t_992b85d1 rather than a
 * tidy-up. It used to run `/api/trade/plan` here purely to mint the HMAC token that
 * `/api/trade/execute` demanded, and Andrei's press returned:
 *
 *     400 plan_token does not authorise these arguments.
 *
 * Not a credential — OUR token, refused by OUR api before Hyperliquid was asked. Measured
 * in CloudWatch on the dev function: the plan answered `200` in **15.8s** from one log
 * stream and the execute `400` from a DIFFERENT one, i.e. a different Lambda container —
 * and `trade_actions._secret()` mints a fresh key per PROCESS with `CHARTAPP_PLAN_SECRET`
 * unset there. So the verifier never held the signer's key, and a 15.8s plan GUARANTEES
 * it: the token could not verify, ever. The backend now names `close`/`cancel` in
 * `_NO_TOKEN_VERBS` and `execute` skips `verify_token` for them, so the round trip bought
 * nothing but a guaranteed refusal and fifteen seconds. What a token binds is two requests
 * separated by a HUMAN DECISION; inside one click it bound a request to itself.
 */
async function tpConfirmNow() {
  if (!tpAction || !isNoDryRun(tpAction.verb)) return;
  const { verb, symbol } = tpAction;
  const params = tpParams();
  tpPhase = 'in-flight';
  tpSyncButtons();
  el.tpDlgResult.innerHTML = '<div class="tp-note warn">sending to the exchange…</div>';
  try {
    // NO `plan_token`: this verb is in the backend's `_NO_TOKEN_VERBS`. Omitted rather
    // than sent empty, so a backend that DID still require one refuses loudly here
    // instead of being handed a string that fails its HMAC for a second reason.
    const run = await postJSON<TradeRun>('/api/trade/execute', { verb, symbol, params });
    el.tpDlgResult.innerHTML = renderTradeRun(run);
    tpPhase = run.refused ? 'refused' : 'done';
    // exec_tools' `confirmed: true` is a self-report (§0b.1) — the panel shows the re-read.
    if (!run.refused) await tpLoad(true);
  } catch (err) {
    el.tpDlgResult.innerHTML = `<div class="tp-note bad">${esc(String(err))}</div>`;
    tpPhase = 'refused';
  } finally {
    tpSyncButtons();
  }
}

/** Map a table button to an action dialog. The defaults are PRE-FILLED FROM THE
 *  ORDER GRAPH (current SL/TP), so a stop move starts from the real resting stop
 *  rather than from a blank field or — worse — from the fabricated
 *  `position.stop_loss`. */
function tpHandleActionClick(act: string, coin: string, oid: string | null) {
  const pos = snapshot?.positions.find((p) => p.coin === coin);
  switch (act) {
    case 'modify-sl':

      tpOpenAction('modify-sl', coin, { price: pos?.protection.sl ?? null });
      break;
    case 'modify-tp':
      // NO size field: `/api/trade/modify` reads only verb/symbol/price, so a typed
      // size was dead UI that `tpParams()` still put in the body. The server sizes the
      // leg from the resting rung (or the position when it is attached), which is what
      // keeps `isPositionTpsl` and what stops a rung being promoted to full size.
      tpOpenAction('modify-tp', coin, { price: pos?.protection.tp ?? null });
      break;
    case 'close-full':
      tpOpenAction('close', coin, { fixed: { pct: 100 } });
      break;
    case 'cancel':
      // ONE CLICK, NO DIALOG -- see `tpCancelNow` and `NO_CONFIRM_VERBS`. Reached
      // only with the oid read off the clicked button, which is the order identity.
      if (oid) void tpCancelNow(oid, coin);
      break;
  }
}

/**
 * ONE CLICK CANCELS A RESTING ORDER. No dialog, no dry-run, no confirm.
 *
 * Andrei, 2026-08-13: *"it should allow canceling orders immidiately without any
 * additional checks."* The check it drops is a PREVIEW of a risk-reducing act: a
 * cancel removes a resting order, so it cannot open a position, cannot raise
 * exposure, and cannot lose money by being wrong. §0b.2's gate is at the
 * order-PLACEMENT boundary and this is the other side of it.
 *
 * THE ALLOWLIST IS A MEMBERSHIP TEST, NOT A PARAMETER. `isNoConfirm` is checked here
 * against the literal `'cancel'`, so there is no argument any future call site could
 * pass to route `close` (which REALISES PnL and is not undone by re-placing), the
 * modifies, or the ticket's `place` through this function. Those still go through
 * `tpOpenAction` and still need their confirm.
 *
 * THE ROW IS THE SAFETY, BECAUSE ONE CLICK MEANS A MIS-CLICK CANCELS A REAL ORDER --
 * and some of these orders are stops protecting live positions. So the row states
 * what happened, in words, immediately and then truthfully:
 *
 *   * "Cancelling..." the moment the click lands (optimistic, and says so);
 *   * the row is GONE once the forced re-read no longer carries the order -- which is
 *     the only proof a cancel worked;
 *   * "Still open - cancel failed" on ANY other ending, with the Cancel button back.
 *
 * NEVER "Cancelled" ON A SELF-REPORT. exec_tools' `cancel` has returned
 * `{"cancelled": false, "error": null}` for orders that were already gone (§0b.1), so
 * `run.refused` alone decides nothing. `cancelOutcome` requires the HTTP path to have
 * succeeded AND the order to have left the book. An HTTP 500 (which is what the
 * dialog was returning) therefore lands on "Still open", which is TRUE -- the book was
 * not changed.
 *
 * NO UNDO. An order cannot be un-cancelled on an exchange; a button that re-places it
 * at whatever price the book now offers would be a different order wearing the word
 * undo. The row saying what happened is the whole mistake-catching mechanism.
 */
async function tpCancelNow(oid: string, coin: string) {
  // Not a flag on a shared dialog: the verb is a literal, checked against the set.
  if (!isNoConfirm('cancel')) return;
  // Re-entry guard. Two clicks on one row must not send two cancels, and the guard is
  // set BEFORE the first await -- the same race `tpTriggerScan` was fixed for.
  if (tpCancelling.get(oid) === 'cancelling') return;
  tpCancelling.set(oid, 'cancelling');
  // Last cancel's "gone from the book" line is stale the moment a new one starts.
  tpNote = null;
  tpRenderBody();
  let ok = false;
  try {
    // ONE REQUEST, NO TOKEN (card t_992b85d1). This used to run `/api/trade/plan` first
    // purely to mint the HMAC token `/api/trade/execute` demanded, and that pair CANNOT
    // work on the deployed app: measured in CloudWatch, a plan and its execute answered
    // from two different Lambda log streams — two containers — while
    // `trade_actions._secret()` mints a fresh key per PROCESS with `CHARTAPP_PLAN_SECRET`
    // unset there, so the verifier never held the signer's key. `close` was the verb
    // Andrei pressed and reported (`400 plan_token does not authorise these arguments`);
    // this path had the identical defect and was found by mechanism rather than by a
    // click. `cancel` is in the backend's `_NO_TOKEN_VERBS` alongside it.
    //
    // What was removed is a binding between a request and ITSELF: a token proves the
    // confirmed argv is the argv a HUMAN read in a dry-run, and there is no human step
    // inside one click. `cancel` also has zero `risk_gates` calls in `manage.py::_cancel`,
    // so nothing that refuses moved.
    const params = { order_id: oid };
    const run = await postJSON<TradeRun>('/api/trade/execute', {
      verb: 'cancel', symbol: coin, params,
    });
    ok = !run.refused;
  } catch {
    // A transport or HTTP failure means the book was NOT changed. Nothing to report
    // beyond that, and the row is where it gets reported.
    ok = false;
  }
  // THE PROOF: re-read the book. `ok` is the CLI's self-report and is not enough.
  await tpLoad(true);
  const stillResting = (snapshot?.orders ?? []).some((o) => o.oid !== null && String(o.oid) === oid);
  if (cancelOutcome({ ok, stillResting }) === 'cancelled') {
    // The order is gone from the book, so its row is gone with it -- there is nothing
    // left to label. Drop the state rather than leaving a stale entry keyed on an oid
    // that will never render again.
    tpCancelling.delete(oid);
    tpNote = cancelNote(oid, coin);
  } else {
    tpCancelling.set(oid, 'failed');
  }
  tpRender();
}

/** Point the CHART at `coin`. The one way any panel row changes the charted market.
 *
 *  Returns what happened, because "this feed cannot chart that market" is a real
 *  outcome the caller must report rather than swallow: `switched` / `already` /
 *  `unavailable`. `onLoaded` fires once the bars are in — immediately when the
 *  market is already charted — for callers that must act on the new price scale.
 *
 *  Shared by the row click and the ticket's `Chart` button, because a level drawn on
 *  another instrument's scale is meaningless (measured: AMZN's 225-278 levels on a
 *  0-100,000 BTC axis collapsed into one pixel row) and two copies of "switch the
 *  chart" would drift.
 *
 *  The market change itself is delegated to `setChartedAsset` — this function used to
 *  write `el.asset.value` directly and was the 1-of-3 writer that left the visible
 *  combobox reading the PREVIOUS market (measured 2026-07-28: select `xyz:AMZN`,
 *  box `BTC`, status bar and candles both on AMZN). */
function tpShowOnChart(coin: string, onLoaded?: () => void): ChartedAssetResult {
  const verdict = setChartedAsset(coin);
  if (verdict === 'already') onLoaded?.();
  if (verdict === 'switched') {
    // ALL THREE PANES, not just the execution one. This path (a position row / the ticket's `Chart`
    // button) called `loadBars()` alone, so clicking SOL in the positions table moved the 15m pane
    // and left 1W/1D on the previous market. The asset PICKER path already did both, which is why it
    // looked fixed from one entry point and broken from the other — Andrei's HAR settled it: the
    // SOL/15m request had no `loadContextPanes` frame in its initiator stack.
    invalidateAllOverlays();
    void loadContextPanes();
    void loadBars().then(() => onLoaded?.());
  }
  return verdict;
}

/** The sentence shown when a market cannot be charted on the current feed. */
function tpUnavailableNote(coin: string): string {
  return (
    `the '${el.source.value}' feed has no chart for ${coin} — its levels are NOT ` +
    'drawn against its own price scale. Switch the source to hyperliquid.'
  );
}

/** Drag-to-resize. Writes `--tp-h`, which the CSS grid row reads. */
function tpWireGrip() {
  let startY = 0;
  let startH = 0;
  const onMove = (e: PointerEvent) => {
    const max = window.innerHeight * TP_MAX_VH;
    const h = Math.max(TP_MIN_H, Math.min(max, startH + (startY - e.clientY)));
    document.documentElement.style.setProperty('--tp-h', `${Math.round(h)}px`);
  };

  const onUp = () => {
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
    localStorage.setItem(
      TP_KEYS.height,
      getComputedStyle(document.documentElement).getPropertyValue('--tp-h').trim(),
    );
  };
  el.tpGrip.addEventListener('pointerdown', (e) => {
    startY = (e as PointerEvent).clientY;
    startH = el.panel.getBoundingClientRect().height;
    // Collapsed + drag = expand: dragging a collapsed panel open is the gesture
    // people reach for, and doing nothing there feels broken.
    document.body.classList.remove('tp-collapsed');
    localStorage.setItem(TP_KEYS.collapsed, '0');
    // THIRD un-collapse path, and the easy one to miss — found by grepping `tp-collapsed` rather than
    // patching only the button I had in front of me. Any route that clears the class must restart the
    // timer, or dragging a collapsed panel open leaves it permanently static.
    tpStartPolling();
    void tpLoad();
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    e.preventDefault();
  });
}

function tpRestoreGeometry() {
  const h = localStorage.getItem(TP_KEYS.height);
  if (h) {
    // CLAMPED to this window, not trusted verbatim. A height saved on a tall
    // window (or on an external monitor) would otherwise be restored on a short
    // one and push the panel past the viewport — which is how the tab strip and
    // the whole ticket ended up unreachable.
    const saved = parseInt(h, 10);
    const max = Math.round(window.innerHeight * TP_MAX_VH);
    const use = Number.isFinite(saved) ? Math.max(TP_MIN_H, Math.min(max, saved)) : null;
    if (use !== null) document.documentElement.style.setProperty('--tp-h', `${use}px`);
  }
  if (localStorage.getItem(TP_KEYS.collapsed) === '1') document.body.classList.add('tp-collapsed');
  const tab = localStorage.getItem(TP_KEYS.tab) as TabId | null;
  if (tab && TABS.some((t) => t.id === tab)) tpTab = tab;
  // THE REVIEW CURSOR, restored defensively: a hand-edited or half-written value must not
  // throw on boot and take the whole panel down with it. A cursor that fails to parse is
  // simply no cursor — the list then opens at the top, which is the state before this
  // feature existed rather than a broken one.
  try {
    const raw = localStorage.getItem(TP_KEYS.cursor);
    const c = raw ? JSON.parse(raw) : null;
    if (c && typeof c.key === 'string' && Number.isInteger(c.index)) setupCursor = c;
  } catch {
    setupCursor = null;
  }
}

/** Keep the panel inside the viewport when the window changes size.
 *
 *  Without this, shrinking the window (or rotating a laptop to a smaller external
 *  display) leaves `--tp-h` at its old absolute value and the panel overflows —
 *  the same failure as a stale restored height, arriving by a different route. */
function tpClampToViewport() {
  const max = Math.round(window.innerHeight * TP_MAX_VH);
  const cur = el.panel.getBoundingClientRect().height;
  if (cur > max) {
    document.documentElement.style.setProperty('--tp-h', `${max}px`);
    localStorage.setItem(TP_KEYS.height, `${max}px`);
  }
}

function tpWire() {
  tpWireGrip();
  tpRestoreGeometry();
  // A window resize can invalidate the panel height the same way a stale restored
  // one can, so both routes end at the same clamp.
  window.addEventListener('resize', tpClampToViewport);

  // The order ticket's own listeners live on the same delegated body, so they are
  // installed here — before any render — for the same reason: the body's HTML is
  // replaced wholesale on every refresh.
  tkWire();

  // Delegated: the chrome and tables are re-rendered wholesale on every refresh,
  // so per-element listeners would be re-attached (and leak) each time.
  el.tpTabs.addEventListener('click', (e) => {
    const t = (e.target as HTMLElement).closest('[data-tab]') as HTMLElement | null;
    if (t) {
      const next = t.dataset.tab as TabId;
      tpTab = next;
      localStorage.setItem(TP_KEYS.tab, tpTab);
      tpLoadForTab(next);
      tpRender();
      // BACK ON THE SETUPS TAB, LAND ON THE ROW HE LEFT. Landing at the top of 87 rows
      // after reading row 43 is the failure this cursor exists to remove, and the tab
      // strip is the other route in besides the ticket's own Back button.
      if (next === 'setups') tpScrollToCursor();
      return;
    }

    const id = (e.target as HTMLElement).id;
    if (id === 'tp-refresh') void tpLoad(true);
    if (id === 'tp-collapse') {
      const now = document.body.classList.toggle('tp-collapsed');
      localStorage.setItem(TP_KEYS.collapsed, now ? '1' : '0');
      tpRenderChrome();
      // Re-evaluate the timer: collapsing stops it, expanding starts it again. Without this the
      // panel would stay dead after being reopened — a fix that breaks the feature it optimises.
      tpStartPolling();
      // Expanding shows a table that may be a while stale, so re-read once immediately rather than
      // making the user wait up to TP_POLL_MS to find out what their account is doing.
      if (!now) void tpLoad();
    }
    if (id === 'tp-maxi') {
      document.body.classList.toggle('tp-max');
      document.body.classList.remove('tp-collapsed');
      tpRenderChrome();
      // Maximising also un-collapses (line above), so the timer must come back with it.
      tpStartPolling();
    }
  });

  el.tpSub.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).id === 'tp-clearsel') {
      tpSelected = null;
      tpRender();
      tpDrawLevels();
    }
  });

  el.tpBody.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (target.id === 'tp-scan') {
      void tpTriggerScan();
      return;
    }
    // A SORTABLE HEADER cycles ASC -> DESC -> natural for this tab and re-renders.
    // Same `dataset` convention as the overlay checkboxes, and it reuses THIS
    // delegated listener rather than attaching one per `<th>` -- the panel replaces
    // its innerHTML every 15 s, so per-header listeners would be re-bound forever.
    const th = target.closest('th[data-sort]') as HTMLElement | null;
    if (th) {
      tpSort[tpTab] = nextSort(tpSort[tpTab] ?? null, th.dataset.sort ?? '');
      tpRenderBody();
      return;
    }
    const btn = target.closest('button[data-act]') as HTMLElement | null;
    if (btn) {
      tpHandleActionClick(btn.dataset.act ?? '', btn.dataset.coin ?? '', btn.dataset.oid ?? null);
      return;
    }
    // A SETUP ROW loads that setup into the order ticket. Checked BEFORE the
    // `data-coin` selection path below, because a setup row carries `data-coin` too
    // and the account path would only select-and-chart it — the whole point is that
    // the prices land in the ticket so nothing gets retyped.
    const setupRow = target.closest('tr[data-setup]') as HTMLElement | null;
    if (setupRow) {
      tpLoadSetupIntoTicket(setupRow.dataset);
      return;
    }
    // AN ADVICE ROW charts that market and loads the ADVISED stop into the ticket.
    // Checked before the `data-coin` selection path below for the same reason a setup
    // row is: it carries `data-coin` too, and the account path would only chart it.
    const adviceRow = target.closest('tr[data-advice]') as HTMLElement | null;
    if (adviceRow) {
      tpLoadAdviceIntoTicket(adviceRow.dataset.advice ?? '');
      return;
    }
    // A row click selects that market, charts it, and mirrors its levels.
    //
    // Charting it is the part that was missing (Andrei, 2026-07-27): rows already
    // carried `data-coin` and selected a market, but the chart stayed where it was —
    // so the panel's `xyz:` equities were selected against BTC candles. A second
    // click deselects (back to follow-the-chart) but deliberately leaves the chart
    // put: undoing a navigation the user asked for would fight them.
    const coin = (target.closest('tr[data-coin]') as HTMLElement | null)?.dataset.coin;
    if (coin) {
      if (tpSelected === coin) {
        tpSelected = null;
      } else {
        tpSelected = coin;
        // `tpDrawLevels` again once the bars land, so levels sit on the new scale.
        const shown = tpShowOnChart(coin, tpDrawLevels);
        tpError = shown === 'unavailable' ? tpUnavailableNote(coin) : null;
      }
      tpRender();
      tpDrawLevels();
    }

  });

  el.tpApply.addEventListener('click', () => void tpApplyAction());
  el.tpDryrun.addEventListener('click', () => void tpDryRun());
  el.tpConfirm.addEventListener('click', () => void tpConfirmAction());
  el.tpCancelDlg.addEventListener('click', () => el.tpDialog.close());
  // A DISMISSED DIALOG PUTS THE LINE BACK, HOWEVER IT WAS DISMISSED. On the dialog's own
  // `close` event, not on the Cancel button's click: `<dialog>` also closes on ESCAPE, and
  // that path used to leave the staged price on the chart (redteam D2, 2026-08-19).
  // Measured: focus lands in `#tp-f-stop`, so `wireLevelCancel`'s `tagName === 'INPUT'`
  // guard skips the clear, the button handler never runs, and all four panes then agreed
  // on a stop 168 points on the WRONG side of the real one — immune to the 15s poll by
  // design (`fromRow` preserves pending). One handler on the element that actually knows
  // it closed, rather than a second guard in the key handler.
  //
  // `tpApplyAction` clears `tpStagedCommit` before it awaits, so a close during the write
  // reverts nothing: the read-back is what decides, and the staged line is dropped on
  // every one of its terminal paths.
  el.tpDialog.addEventListener('close', () => {
    if (!tpStagedCommit) return;
    liveLevels.setPending(tpStagedCommit.id, tpStagedCommit.leg, null);
    tpStagedCommit = null;
  });
}

async function tpBoot() {
  tpWire();
  // PAINT THE TICKET ONCE, HERE, before anything can fail. It is permanently mounted in
  // the right column now, so an empty `#tk-mount` is a visibly broken column rather than
  // an unopened tab -- and every later render is triggered by a state change that may
  // never come (`tkSyncSymbol` returns early when the symbol has not changed, which on a
  // reload it has not). `renderTicket` needs no snapshot: composing and previewing is a
  // read-only act and the preview reports its own UNAVAILABLE gates without an account.
  tkRender();
  // Load the credential state BEFORE health. The wallet tab must be usable even when
  // health fails — "no wallet address" is precisely the failure the tab fixes, so its data
  // cannot depend on the probe that is failing because of it.
  await acLoadCredentials();
  try {
    accountHealth = await getJSON<AccountHealth>('/api/account/health');
  } catch (err) {
    tpError = `trade panel unavailable: ${err}`;
    tpRender();
    return;
  }
  tpRender();
  // The ticket needs a symbol and its card list even when the account is
  // unreadable: composing and previewing an order is a read-only act, and the
  // preview's own equity-dependent gates already report UNAVAILABLE without a
  // snapshot. Only the WRITE half depends on the account.
  tkSyncSymbol();
  if (!accountHealth.ok) return;
  await tpLoad();
  // THE RESTORED TAB FETCHES TOO. `tpRestoreGeometry` can put the panel straight back on
  // Setups (or Trade Management) from `localStorage`, and neither renders from the
  // snapshot `tpLoad` just read -- so without this the tab shows its empty state and
  // nothing ever asked the server. This is the route Andrei's HAR caught: zero requests
  // containing "setups". Same funnel as the tab click, so the two cannot drift.
  tpLoadForTab(tpTab);
  tpStartPolling();
}

// ═══════════════════════════════════════════════════════════════════════════
// ORDER TICKET — controller
// ═══════════════════════════════════════════════════════════════════════════
// The draggable entry / SL / TP lines and the ticket form that mirrors them.
// Two-way: dragging a line updates the form, typing in the form moves the line.
//
// It obeys the same rule as everything else in this file — it RENDERS and it
// ROUTES. Risk / reward / R:R / the modelled liquidation / every RULE verdict
// arrive from /api/trade/preview, which calls the audited
// `exec_tools.cli.risk_gates`. Placement goes through /api/trade/plan ->
// /api/trade/execute, i.e. the existing two-phase, argv-bound, env-gated path.

/** The ticket's live state. The ONE source both the form and the chart read.
 *  Shape + reset rules live in `ticket.ts`, where they are unit-tested. */
let tkState: TicketState = freshTicket();
let tkPreview: TicketPreview | null = null;
/** WHY `/api/trade/bracket/sized` said no, KEYED TO THE ARGUMENTS IT REFUSED.
 *
 *  TWO STATES, NEVER COLLAPSED (card t_0bd35d10). `null` = pending, a
 *  `{key, message}` = the server's own refusal sentence together with the
 *  `sizingFingerprint` of the ticket it was raised for. Before this existed the
 *  catch clause below discarded the reply, so a REFUSAL rendered identically to a
 *  call in flight and the panel read "waiting for the server" through sixteen
 *  consecutive 400s.
 *
 *  IT IS KEYED RATHER THAN CLEARED BY HAND (review round 1, F1). The first fix
 *  cleared a bare string at two sites and `tkSyncSymbol` was not one of them, so
 *  changing the asset dropdown after a refusal printed "equity for BTC is read
 *  from the main perp dex" in six money rows of a SOL ticket holding no prices —
 *  uninformative replaced by confidently wrong. `currentSizingError` resolves a
 *  key mismatch to pending, so no future writer of the ticket's prices has to
 *  remember anything. */
let tkSizingRefusal: SizingRefusal | null = null;
let tkBusy = false;
/** The coarse-pointer arm on `#tk-place` (FR3.3, intent 261018-mobile-v1, 2026-10-18): the
 *  first tap on a touch-first device only arms the button, and `placearm.placeStep` decides
 *  whether a tap places. Bound to the prices it was armed on; `null` = not armed. */
let tkArm: PlaceArm = null;
/** The last place/modify outcome, rendered under the button. HTML from `renderTradeRun`. */
let tkResult = '';
/** Debounce handle for the preview call — see `tkSchedulePreview`. */
let tkPreviewTimer: number | null = null;
const tkPrimitive = new TicketPrimitive();

/** Cheap price formatter for the LINE LABELS while dragging.
 *
 *  Deliberately mirrors `tradepanel.fmtPx`'s magnitude-driven precision by
 *  delegating to it — the ticket must not invent a second price format, or the
 *  same number would read differently in the table and on the line. */
const TK_KEYS = { state: 'tk.state' };

/** Persist what `ticket.ticketPrefs` allows — preferences only, nothing while managing. */
function tkSave() {
  const prefs = ticketPrefs(tkState);
  if (prefs) localStorage.setItem(TK_KEYS.state, JSON.stringify(prefs));
}

function tkRestore() {
  try {
    const raw = localStorage.getItem(TK_KEYS.state);
    if (!raw) return;
    const d = JSON.parse(raw) as Partial<TicketState>;
    if (d.side === 'buy' || d.side === 'sell') tkState.side = d.side;
    if (d.type === 'limit' || d.type === 'market') tkState.type = d.type;
    if (d.sizing === 'risk_pct' || d.sizing === 'size') tkState.sizing = d.sizing;
    if (typeof d.risk_pct === 'number') tkState.risk_pct = d.risk_pct;
    if (typeof d.leverage === 'number') tkState.leverage = d.leverage;
  } catch {
    // A corrupt preference blob must not stop the app booting.
  }
}

/** Make sure the ticket is REACHABLE, whatever the layout is doing.
 *
 *  THE TICKET MOVED OUT OF THE BOTTOM DOCK (2026-08-18, card t_ab054079) and into the
 *  right column, so this function no longer grows `--tp-h`: the aside is its own scroll
 *  box and nothing about the panel's height can clip the form any more. What it still
 *  has to do is UN-COLLAPSE the column, because `body.insp-collapsed` narrows the aside
 *  to 30px and hides every child but the toggle — a `+ Trade` press that appeared to do
 *  nothing is exactly the failure the old height check existed to prevent, arriving by
 *  the other axis.
 *
 *  `body.tp-max` hides the aside outright, so it is cleared too. Both are written back
 *  to the same localStorage keys their own toggles use, so the state the user sees and
 *  the state that is remembered cannot disagree. */
function tkEnsureRoom() {
  if (document.body.classList.contains('insp-collapsed')) {
    document.body.classList.remove('insp-collapsed');
    el.inspToggle.setAttribute('aria-expanded', 'true');
    el.inspToggle.innerHTML = '\u00bb hide panel';
    try {
      localStorage.setItem(INSPECTOR_COLLAPSED_KEY, inspectorCollapsedValue(false));
    } catch {
      // Storage blocked (private mode); the column is open for this session regardless.
    }
  }
  // Maximised panel hides the whole aside. Restore the normal three-row layout rather
  // than leaving the user pressing a button whose target is `display: none`.
  document.body.classList.remove('tp-max');
}

/** Show the Order Ticket, with sensible defaults. THE entry point.
 *
 *  Called by the toolbar's `+ Trade` button and by the setup/advice row loaders, so
 *  every route lands in the same state — a second "open the ticket" path would drift.
 *
 *  It seeds the entry from the current mark when empty. A blank form on a chart
 *  whose price is right there is a needless first step, and an entry price is
 *  what unlocks every other number in the preview. */
function tkOpen() {
  tkEnsureRoom();
  tkSyncSymbol();
  if (tkState.entry === null && !tkState.managing) {
    tkState.entry = tkMarkPrice();
    tkFocusChart();
  }
  void tkFetchPreview(true);
}

function tkRenderBody(): string {
  return renderTicket(tkState, tkPreview, {
    tradingEnabled: Boolean(accountHealth?.trading.trading_enabled),
    armReason: accountHealth?.trading.arm_reason,

    equity: equityFrom(snapshot),
    busy: tkBusy,
    chartSymbol: el.asset.value,
    result: tkResult,
    review: tkReviewNav(),
    // WHY the derived rows are empty, when they are: a refusal, not a wait.
    // Card t_0bd35d10. Handed over KEYED, so `renderTicket` shows it only while it
    // still describes the ticket on screen.
    sizingRefusal: tkSizingRefusal,
    // "Tap again to place" while a coarse-pointer arm is live for THESE prices (FR3.3).
    armed: isArmed(tkArm, Date.now(), ticketKey(tkState)),
    // The ready line counts the taps this device needs (t_1ea62600).
    coarse: coarsePointer(),
  });
}

/** Push the ticket's geometry to the chart. Called on every state change.
 *
 *  REFUSES to draw when the ticket's market is not the charted one. A level drawn
 *  on another instrument's price scale is meaningless and looks authoritative —
 *  measured live: AMZN's 225-278 levels on a BTC 0-100,000 axis collapsed into a
 *  single pixel row. `tpLoadAdviceIntoTicket` moves the chart, and the asset dropdown
 *  clears the ticket, so this guard only fires in the window between the two;
 *  drawing nothing (and saying why in the sub-bar) is the honest state. */
function tkDrawLines() {
  if (tkState.symbol && el.asset.value && tkState.symbol !== el.asset.value) {
    tkPrimitive.setData({ legs: [], bands: [] });
    // NOT `[]`: the context panes carry the LIVE POSITION's lines too, and clearing them
    // because the ticket belongs to another market is half of the D1 defect (see
    // `contextLevels`). `contextLevels` drops the ticket's legs on this same condition.
    drawContextLevels(panes.values(), EXECUTION_PANE_ID, contextLevels());
    return;
  }
  const geo: TicketGeometry = ticketGeometry(tkState, tkPreview);
  tkPrimitive.setData(geo);
  // AND THE SAME LEGS, READ-ONLY, ON THE 1W AND 1D PANES. Andrei: "when trade setup is
  // happening, I should see trade parameter levels on all 3 timeframes". Done from HERE rather
  // than from a second render loop, because this function is what the drag callback already calls
  // on every pointer-move -- so the context panes cannot lag behind a dragged stop.
  //
  // THROUGH `contextLevels`, NOT `geo.legs` (redteam D1, 2026-08-19): passing the ticket's legs
  // alone made this the ONLY writer whenever the ticket held any price, so the live position's
  // stop either disappeared from 1W/1D or was replaced by the planned one at a different price
  // under the same `SL` label. `contextLevels` unions both and prefixes the plan.
  drawContextLevels(panes.values(), EXECUTION_PANE_ID, contextLevels());
}


/**
 * Re-render the ticket in its mount and redraw its chart lines.
 *
 * `full` re-renders the FORM too. A drag passes `false`: rewriting the form's
 * `<input>` elements mid-drag would blow away the field the user is editing and
 * (worse) steal focus on every pointer-move. The line labels still update,
 * because they are canvas, not DOM.
 *
 * ONE MOUNT since 2026-08-18 (card t_ab054079): the ticket lives in `#tk-mount`
 * inside the right column and nowhere else, so this is unconditional — there is no
 * "is the ticket tab active" question left to get wrong.
 */
function tkRender(full = true) {
  if (full) el.tkMount.innerHTML = tkRenderBody();
  tkDrawLines();
  if (!full) tkPatchStats();
}

/** Update only the stats/verdict half of the ticket, preserving input focus. */
function tkPatchStats() {
  const host = el.tkMount.querySelector('.tk-preview');
  if (!host) return;
  const fresh = document.createElement('div');
  fresh.innerHTML = tkRenderBody();
  const next = fresh.querySelector('.tk-preview');
  if (next) host.innerHTML = next.innerHTML;
}

/**
 * Ask the backend for the risk numbers. Debounced, and that is a measured need:
 * a drag fires pointermove at screen refresh rate, and each preview imports the
 * risk-gates module server-side. 120ms keeps it feeling live while collapsing a
 * drag into a handful of calls.
 *
 * NOTE the ordering: the LINES follow the cursor immediately (local geometry),
 * the NUMBERS follow at debounce speed. A ticket whose lines lagged the pointer
 * would feel broken; numbers that settle a moment later read as normal.
 */
function tkSchedulePreview(full = false) {
  if (tkPreviewTimer !== null) window.clearTimeout(tkPreviewTimer);
  tkPreviewTimer = window.setTimeout(() => void tkFetchPreview(full), 120);
}

async function tkFetchPreview(full = false) {
  if (!tkState.entry) {
    tkPreview = null;
    // No entry means no sizing call was made, so a refusal from a PREVIOUS entry
    // must not stay on screen describing prices that are no longer in the form.
    // Belt and braces: `currentSizingError` would already resolve it to pending on
    // the key mismatch. Dropped here too because a refusal nothing can render is
    // dead state, and keeping it would invite a reader to trust the key alone.
    tkSizingRefusal = null;
    tkRender(full);
    return;
  }
  try {
    // THE DERIVED NUMBERS COME FROM THE SAME CODE THAT PLACES THE ORDER.
    // `/api/trade/bracket/sized` calls `bracket._derive`, exactly as `place` does, so the
    // leverage and wipe-out price shown here ARE the ones that would be used. Computing
    // them any other way would let the display disagree with the trade -- the user would
    // approve numbers that are not the numbers sent.
    //
    // Best-effort: a failure here must NOT blank the panel. The stop-vs-liquidation rows
    // fall back to their "waiting for the server" state, which is honest, rather than
    // showing a locally-guessed leverage that nothing will honour.
    let tkSized: {
      leverage?: number; liq_price?: number; size?: number; risk_usd?: number;
      asset_max_leverage?: number;
      risk_bound?: 'stop' | 'liquidation';
      // THE EQUITY THE SIZE WAS DERIVED FROM, and its provenance (card t_e59172b3).
      // `balance` is the whole trading account (unified spot USDC total) on a
      // unified account; `equity_basis` / `deployable` / `deployable_basis` say which
      // pot and what can actually be deployed.
      balance?: number;
      equity_basis?: string;
      deployable?: number;
      deployable_basis?: string;
      account_mode?: string;
    } | null = null;
    if (tkState.entry) {
      // THE KEY IS TAKEN BEFORE THE AWAIT, not after it. The user can type or drag
      // during the ~1 s the call takes, and a refusal stamped with the state as it
      // is when the reply lands would claim to describe prices the server was never
      // asked about — the same lie this key exists to make unrepresentable.
      const askedAbout = sizingFingerprint(tkState);
      try {
        tkSized = await postJSON('/api/trade/bracket/sized', {
          symbol: tkState.symbol,
          entry: tkState.entry,
          // NO STOP IS A VALID SHAPE. The server then sizes from the isolated
          // LIQUIDATION distance at the market's max leverage -- Andrei, 2026-08-07:
          // "without the stop the risk is limited to liquidation level of the isolated
          // margin deal". `side` is REQUIRED in that case and only in that case: with
          // no stop nothing else says which side of entry the liquidation is on. It is
          // NOT a derived field -- it says which trade, not how much money -- so
          // `_reject_derived_fields` still refuses `size` and `leverage` as before.
          side: tkState.side,
          ...(tkState.stop ? { stop: tkState.stop } : {}),
          ...(tkState.tps[0] ? { tp: tkState.tps[0] } : {}),
        });
        // ANSWERED, so any previous refusal is gone. Clearing here rather than only
        // at the top of the function keeps the two facts (a reply exists / it was a
        // refusal) written by the same code path that learns them.
        tkSizingRefusal = null;
      } catch (err) {
        // KEEP THE SENTENCE. `catch { tkSized = null; }` is what card t_0bd35d10 is
        // about: the reply had arrived, 400, carrying the only fact that says what to
        // do next ("this account's equity for BTC is read from the main perp dex ...
        // the funds are sitting on a DIFFERENT dex"), and this line threw it away --
        // after which the panel's derived rows read "waiting for the server" forever,
        // which is what Andrei saw and reported as "the panel does not update".
        // `postJSON` already formats status + detail through `apiErrorMessage`.
        tkSized = null;
        tkSizingRefusal = {
          key: askedAbout,
          message: err instanceof Error ? err.message : String(err),
        };
      }
    }
    tkPreview = await postJSON<TicketPreview>('/api/trade/preview', {
      symbol: tkState.symbol,
      side: tkState.side,
      entry: tkState.entry,
      stop: tkState.stop,
      tps: tkState.tps.filter((t): t is number => typeof t === 'number' && t > 0),
      // `leverage` is still SENT because today's preview needs a leverage to
      // model a liquidation against and to check RULE 1's ceiling — but it is
      // not TYPEABLE and it NO LONGER DECIDES RULE 2 or RULE 3. Those two are
      // judged against `derived_*` below or not at all.
      //
      // `risk_pct` IS NO LONGER SENT AT ALL (R4-4). It was the input to a
      // server-side risk-% size fallback that fired whenever the
      // `/bracket/sized` call above failed — and at `TK_DEFAULTS.risk_pct = 1`
      // against the write path's `DEFAULT_RISK_FRACTION = 0.02` it showed EXACTLY
      // HALF the money the Place button opens (measured: shown 0.100000 / $100 at
      // 1.00%, placed 0.200000 / $200 at 2.00%). The fallback is gone from
      // `trade_ticket.preview`, so on a failed sizing call every money row now
      // reads "waiting for the server" instead of a calming wrong number.
      leverage: tkState.leverage,
      // THE SERVER'S OWN SIZE AND LEVERAGE, from the `/bracket/sized` call above
      // -- i.e. `bracket._derive`, the same derivation the Place button runs.
      // RULE 2's margin cap is notional/leverage, so handing it the client's
      // placeholder `TK_DEFAULTS.leverage` (3) refused Andrei's freshly loaded ETH
      // row at $430.63 margin for a trade the server sizes at 20x for $129.19
      // against its $254.40 cap -- and "until I modify entry/TP/SL" cleared it
      // only because a later pass raced in a truer leverage. Sent as a PAIR:
      // `null` when the sizing call has not answered (or failed), and the backend
      // then reports RULE 2/3 UNAVAILABLE rather than refusing a number nobody
      // will trade. They are NOT accepted on the write path -- `place` derives its
      // own and `_reject_derived_fields` refuses them by name.
      derived_size: tkSized?.size ?? null,
      derived_leverage: tkSized?.leverage ?? null,
      // THE EQUITY THE DERIVED SIZE WAS SIZED FROM, when the sizing call answered;
      // the panel's own snapshot only when it did not (card t_e59172b3). The two are
      // DIFFERENT NUMBERS: `balances.account_value` is `hl_check.merge_states`' SUM of
      // the per-dex clearinghouse `accountValue`s (core 1,722 + xyz 2,068 = 3,790 on
      // 2026-09-19), while `bracket._derive` sizes 2% of the unified spot USDC total
      // (16,069). Judging a size derived from the second against the first printed
      // "RULE 3: 8.48% ($321.38) exceeds the 2% account ceiling ($75.80 on $3789.94)"
      // and "RULE 2: $21,424.94 exceeds 4x pool equity" -- two advisory BREACH rows
      // under a trade the write path sizes at exactly 2%. RULE 2/3 must be judged
      // against the SAME base that produced the size, so the sizing reply's own
      // `balance` is relayed, exactly as `derived_size` / `derived_leverage` are.
      // `equityFrom(snapshot)` stays the answer when there is no sizing reply, so a
      // managed position still gets its exit-side gates.
      equity: tkSized?.balance ?? equityFrom(snapshot),
      // WHICH pot that equity is, for RULE 3's `equity_source` label. Display only.
      equity_source: tkSized?.equity_basis ?? null,
      // Managing a live position: hands the backend the exit-side gate set AND
      // the venue's own liq price. The estimator's conservative model produced a
      // false RULE 5 "dead stop" on the real AMZN short (estimate 266.82 vs the
      // exchange's 278.26) — see `trade_ticket.preview`.
      managing: Boolean(tkState.managing),
      liq_price: tkState.managing
        ? (snapshot?.positions.find((p) => p.coin === tkState.managing)?.liq_px ?? null)
        : null,
      // THE VENUE'S OWN PER-ASSET maxLeverage, from the same `/bracket/sized` reply.
      // It is the fourth, REQUIRED input to the liquidation formula (the maintenance
      // fraction is 1/(2*maxLeverage)), and the preview endpoint makes no network call
      // so it cannot read it itself. `null` when the sizing call has not answered:
      // the backend then reports `liq_source: unavailable` with the reason, rather
      // than modelling from a substitute. Substituting is the whole of R5-2 — the
      // deleted naive estimator put the modelled liquidation FARTHER from entry than
      // reality in 997 of 998 cases, so RULE 5 accepted stops the position liquidates
      // before. Relayed, never guessed here.
      asset_max_leverage: tkSized?.asset_max_leverage ?? null,
      // WHY there is no derived pair, when the sizing call ANSWERED and refused.
      // RULE 2/3 are UNAVAILABLE either way; this decides only whether their note
      // says "waiting for the server's derived size and leverage" or names the
      // refusal. Sent from the SAME resolution the rows use, so the panel cannot
      // show a refusal in one place and a wait in another — measured live on the
      // card's screenshot arguments: four money rows carried the sentence while two
      // gate rows still told him to wait for it.
      sizing_error: currentSizingError(tkState, tkSizingRefusal),
    });

    // MERGE the authoritative derivation over the advisory preview. `max_leverage` and
    // `liq_beyond_stop` were stubbed pending this endpoint; they are now real, and they
    // come from `bracket._derive` -- the same function `place` runs. `liq_price` is
    // overwritten ONLY for a new entry: when managing a live position the venue's own liq
    // is already authoritative and a modelled one would be a regression (the estimator
    // condemned a safe stop on the real AMZN short: 266.82 vs the exchange's 278.26).
    if (tkPreview && tkSized && !tkState.managing) {
      const liq = tkSized.liq_price ?? null;
      const stop = tkState.stop;
      tkPreview.max_leverage = tkSized.leverage ?? null;
      tkPreview.size = tkSized.size ?? null;
      tkPreview.risk_usd = tkSized.risk_usd ?? null;
      tkPreview.liq_estimate = liq;
      tkPreview.liq_source = stop === null
        ? 'derived by the server from the market\'s max leverage'
        : 'derived by the server from your stop';
      // `risk_bound` carries WHICH price the server bounded the loss at. Copied
      // through rather than inferred from `stop === null` here: an empty stop field
      // is also true mid-type, and the panel must not claim a liquidation-bounded
      // trade the server has not sized.
      tkPreview.risk_bound = tkSized.risk_bound ?? null;
      // WHICH POT THE SIZE CAME FROM, and what can actually be deployed (card
      // t_e59172b3). Relayed, never computed: `equity_basis` is the server's own
      // sentence naming the pot ("whole account: unified spot USDC total ..." or
      // "this dex's pool only: <why>"), so a fallback to the dex slice is SAID on the
      // ticket instead of reappearing as a silently 9x-smaller size.
      tkPreview.equity_basis = tkSized.equity_basis ?? null;
      tkPreview.deployable = tkSized.deployable ?? null;
      tkPreview.deployable_basis = tkSized.deployable_basis ?? null;
      tkPreview.account_mode = tkSized.account_mode ?? null;
      tkPreview.liq_beyond_stop =
        liq === null || stop === null
          ? null
          : tkState.side === 'buy'
            ? liq < stop
            : liq > stop;
      tkPreview.liq_buffer_pct =
        liq === null || stop === null || !tkState.entry
          ? null
          : (Math.abs(liq - stop) / tkState.entry) * 100;
    }

  } catch (err) {
    // Kept visible: a ticket that silently stops checking is the failure mode
    // this whole design is arranged against.
    tkPreview = null;
    tpError = `ticket preview: ${err}`;
  }
  tkRender(full);
}

/** Follow the chart's market. Reset rule + its rationale: `ticket.ticketForSymbol`.
 *
 *  IT GOES THROUGH `tkFetchPreview`, not `tkRender` (review round 1 of card
 *  t_0bd35d10). `ticketForSymbol` clears the prices, so the derived block is
 *  describing a trade that no longer exists in the form: the entry-less branch of
 *  `tkFetchPreview` is the ONE place that drops `tkPreview` and the held refusal
 *  together, and calling the renderer directly left the previous market's server
 *  numbers on screen beside an empty form. The refusal cannot go stale either way
 *  now — it is keyed (`sizingFingerprint`) — but the preview is not, so the reset
 *  is routed through the function that owns both. */
function tkSyncSymbol() {
  const asset = el.asset.value;
  if (!asset || asset === tkState.symbol) return;
  tkState = ticketForSymbol(tkState, asset);
  tkResult = '';
  void tkFetchPreview(true);
}

/** Persist the cursor with the state change, so the two can never disagree.
 *
 *  A single funnel because there are three writers (opening a row, next, prev) and the
 *  bug this feature exists to remove is "the app thinks I am somewhere I am not". */
function tpSetCursor(c: SetupCursor | null) {
  setupCursor = c;
  if (c) localStorage.setItem(TP_KEYS.cursor, JSON.stringify(c));
  else localStorage.removeItem(TP_KEYS.cursor);
}

/** Which row the cursor points at in the CURRENT payload, or null. */
function tpCursorIndex(): number | null {
  return resolveCursor(setupsPayload?.setups ?? [], setupCursor);
}

/** The sequence position shown in the ticket, or undefined when the ticket did not come
 *  from a setup row. `hasPrev`/`hasNext` are computed against the live list, so an edge
 *  disables its button rather than wrapping into a review that looks unfinished.
 *
 *  GATED ON THE TICKET STILL HOLDING THAT SETUP'S MARKET. The cursor outlives the ticket
 *  (that is its job), so after `new trade` or a manage-a-position load the cursor is
 *  still set while the form describes something else entirely — and `setup 43 of 87`
 *  printed over a hand-built trade would claim a provenance it does not have. */
function tkReviewNav(): ReviewNav | undefined {
  const list = setupsPayload?.setups ?? [];
  const at = tpCursorIndex();
  if (at === null || !list.length) return undefined;
  if (tkState.managing || tkState.symbol !== list[at].asset) return undefined;
  return reviewNavAt(list.length, at);
}

/** Walk to the setup one place up (`-1`) or down (`+1`) the DISPLAYED list.
 *
 *  Same array the renderer walks, so "next" is always the row visually below — the day
 *  separators are extra `<tr>`s inside that one `.map`, not a nesting, so there is no
 *  second level for a traversal to fall through. Out of range is a NO-OP: the buttons
 *  are disabled at the edges and this is the second guard, because a keyboard or a stale
 *  render must not step off the end.
 *
 *  IT RE-RESOLVES THE STORED KEY ON EVERY STEP, AND THAT IS THE FEATURE, not the bug that
 *  was here: re-resolving is what keeps the cursor on HIS row while the scan writes new
 *  alerts above it. The bug (t_1849cfb1) was that the identity could not separate two rows,
 *  so `next` wrote index 22, the resolve handed back 21, and the pair was an absorbing state
 *  that stranded 65 of 87 rows. Fixed at the source -- `setupKey` now carries
 *  `idm_swept_time`, the row's real differentiator -- and `resolveCursor` breaks any residual
 *  ambiguity by the stored index, so a step can never resolve backwards.
 *
 *  AND THE STALL GUARD IS DELIBERATELY NOT DUPLICATED HERE. The card offered one -- "if the
 *  re-resolved index equals the previous one, advance from the STORED index instead" -- and it
 *  was written and then REMOVED, because on this composition it is not belt-and-braces, it is
 *  a regression: `resolved` differs from `stored` in exactly one situation, which is the scan
 *  having shifted the list, and preferring `stored` there would step from the pre-shift
 *  position and re-break the identity restore this cursor exists for. The guard belongs one
 *  function down, in `resolveCursor`, where every caller routes through it and where "never
 *  resolve backwards" can be stated without a second opinion about which index is true. */
function tpStepSetup(delta: -1 | 1) {
  const list = setupsPayload?.setups ?? [];
  const at = tpCursorIndex();
  if (at === null) return;
  const next = stepIndex(list.length, at, delta);
  if (next === null) return;
  const s = list[next];
  tpSetCursor({ key: setupKey(s), index: next });
  tpLoadSetupIntoTicket({
    coin: s.asset, idx: String(next), dir: s.direction,
    entry: s.entry === null ? '' : String(s.entry),
    sl: s.sl === null ? '' : String(s.sl),
    tp: s.tp === null ? '' : String(s.tp),
  } as DOMStringMap);
}

/** Back to the setups list, AT the row that was opened — the whole point of the cursor.
 *
 *  Scrolls the panel body, not the window: the table lives inside `#tp-body`, which is
 *  the scrolling box. `block: 'center'` rather than `'nearest'` so a row already just
 *  off-screen is actually moved into view. */
function tpBackToSetups() {
  tpTab = 'setups';
  localStorage.setItem(TP_KEYS.tab, tpTab);
  tpRender();
  tpScrollToCursor();
}

/** Bring the cursor row into view, if it is rendered. Nothing to do when it is not. */
function tpScrollToCursor() {
  const row = el.tpBody.querySelector('tr[data-cursor]');
  if (row) row.scrollIntoView({ block: 'center' });
}

/** Load a SCAN SETUP into the ticket and chart it on all three panes.
 *
 *  Andrei, 2026-08-08: *"I want to have an ability click on the ticker, quickly adjust
 *  trade parameters (entry, TP, SL) if needed by dragging its levels on the chart and
 *  enter the trade... It might be a better UX than copy-pasting it from telegram."*
 *
 *  So this is the anti-retype path, and it deliberately mirrors `tpLoadAdviceIntoTicket`
 *  rather than inventing a second one: same `tpShowOnChart` (all three panes, see its
 *  comment), same `tkEnsureRoom`, same tab switch. The prices come from
 *  `ticketFromSetup`, which is pure and gated.
 *
 *  IT SWITCHES TO THE TICKET TAB rather than leaving a hint in the setups table. The
 *  request names dragging and entering as the next actions, and both live in the
 *  ticket — a banner saying "the ticket now holds this" would be one more click
 *  between reading a setup and acting on it, which is the cost this feature exists to
 *  remove. The setups table is one click back and does not lose its place.
 *
 *  NOTHING IS PLACED. This fills a form; arming still goes through the ticket's own
 *  Place button and `POST /api/trade/bracket`, which derives its own size and leverage.
 */
function tpLoadSetupIntoTicket(d: DOMStringMap) {
  // BYTE FOR BYTE off the row, no case change and no trim of the dex prefix: the scan
  // partitions its table by this exact string.
  const asset = d.coin ?? '';
  if (!asset) return;
  // REMEMBER WHERE HE WAS, from the row's own index into the payload the render walked.
  // The key is composed from the payload row rather than from the DOM: the identity must
  // be the same string `resolveCursor` computes on the way back, and re-deriving it from
  // stringified `data-` attributes is a second spelling of the same fact.
  const idx = Number(d.idx);
  const list = setupsPayload?.setups ?? [];
  const at = Number.isInteger(idx) && idx >= 0 && idx < list.length ? idx : null;
  if (at !== null) tpSetCursor({ key: setupKey(list[at]), index: at });
  // An absent price stays absent. `Number('')` is 0 — a stop of zero is a WRONG stop,
  // not a missing one — so the empty attribute the renderer writes for a null level
  // must map back to null rather than through a numeric coercion.
  const px = (raw: string | undefined): number | null => {
    if (raw === undefined || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  };
  tkState = ticketFromSetup(tkState, {
    asset,
    direction: d.dir ?? '',
    entry: px(d.entry),
    sl: px(d.sl),
    tp: px(d.tp),
  });
  tkResult = '';
  // NO TAB SWITCH any more: the ticket is always on screen in the right column, so the
  // setups table the user clicked from STAYS on screen too. That was the one thing this
  // path used to cost -- reading a setup meant losing the list it came from, which is why
  // the review cursor and the `Back to setups` button had to exist.
  tkEnsureRoom();
  // Chart it on ALL THREE panes, through the one funnel that does that. Same
  // correctness argument as `tpLoadAdviceIntoTicket`: levels drawn against another
  // instrument's price scale look authoritative and mean nothing.
  tpSelected = asset;
  const shown = tpShowOnChart(asset, () => {
    tkFocusChart();
    void tkFetchPreview(true);
  });
  tpError = shown === 'unavailable'
    ? `${tpUnavailableNote(asset)} The ticket still holds this setup.`
    : null;
  // `verdict === 'already'` runs the callback synchronously but no bars load, so the
  // preview and the legs are pushed here for that case; `tkRender` is idempotent.
  tkRender();
  tpRenderChrome();
  tpRenderBody();
}

/** Load an ADVICE ROW's advised stop into the ticket and chart it on all three panes.
 *
 *  Andrei asked to see the morning stop review's suggestions in the web UI so he could
 *  act on one in the same place he reads it. So this mirrors `tpLoadSetupIntoTicket`
 *  rather than inventing a second path: same `tpShowOnChart` (all three panes), same
 *  `tkEnsureRoom`, same tab switch. The prices come from `ticketFromAdvice`, which is
 *  pure and gated.
 *
 *  IT MANAGES, IT DOES NOT OPEN. `ticketFromAdvice` returns a MANAGING ticket built
 *  from the live position, because the advice is about a trade he already has — a
 *  new-entry ticket carrying an entry and a stop would, if he pressed Place, open a
 *  SECOND position in the market he was being advised to protect.
 *
 *  NOTHING IS PLACED. This fills a form. Arming still goes through the ticket's own
 *  Place button and its two-phase preview; this function posts nothing at all.
 *
 *  The row is looked up BY INDEX into the payload the render used, so the advised stop
 *  arrives as the number the route sent rather than as a string round-tripped through
 *  a data attribute (`Number('')` is 0, and a stop of zero is a WRONG stop). */
function tpLoadAdviceIntoTicket(idx: string) {
  const row = advicePayload?.advice[Number(idx)];
  if (!row) return;
  // BYTE FOR BYTE, no case change: the reviewer partitions its store by this exact
  // string and the venue knows the market by it.
  const asset = row.asset;
  const next = ticketFromAdvice(tkState, snapshot, row);
  // Chart it regardless — seeing the market is useful even when the position is gone
  // and there is therefore no honest ticket to build for it.
  tpSelected = asset;
  const shown = tpShowOnChart(asset, () => {
    tkFocusChart();
    if (next) void tkFetchPreview(true);
  });
  if (!next) {
    // No live position: say so instead of filling a form from a trade that has ended.
    tpError = `${asset} is charted, but that trade is no longer open, so nothing was `
      + 'loaded into the order ticket.';
    tpRenderChrome();
    tpRenderBody();
    return;
  }
  tkState = next;
  tkResult = '';
  // No tab switch: the ticket is always mounted in the right column, so the advice
  // table stays visible beside the stop it advised.
  tkEnsureRoom();
  tpError = shown === 'unavailable'
    ? `${tpUnavailableNote(asset)} The ticket still holds the advised stop.`
    : null;
  // Manage mode suppresses this market's read-only levels (the ticket draws draggable
  // ones instead), so they have to be recomputed — same as the setup-load path.
  tpDrawLevels();
  tkRender();
  tpRenderChrome();
  tpRenderBody();
}

/**
 * Wire the ticket. Delegated listeners on ITS OWN MOUNT (`#tk-mount`), because the
 * mount's HTML is replaced wholesale on every render and per-element handlers would
 * leak on each one.
 *
 * SCOPED TO `#tk-mount`, not `#tp-body` (2026-08-18, card t_ab054079): the ticket left
 * the bottom dock, and a listener still attached to the dock would fire for the tables'
 * clicks and never for the ticket's own.
 */
function tkWire() {
  tkRestore();
  tkPrimitive.setCallbacks({

    // During a drag: update state + lines only. No network, no DOM rewrite.
    onDrag: (id, price) => {
      tkApplyDrag(id, price);
      tkDrawLines();
    },
    // On release: now pay for the numbers.
    onDrop: (id, price) => {
      tkApplyDrag(id, price);
      tkSave();
      void tkFetchPreview(true);
    },
  });

  el.tkMount.addEventListener('input', (e) => {
    const input = (e.target as HTMLElement).closest('[data-tk]') as HTMLInputElement | null;
    if (!input || input.tagName !== 'INPUT') return;
    tkApplyField(input.dataset.tk ?? '', input.value);
    tkResult = '';
    tkSave();
    // `false`: do NOT re-render the form while the user is typing in it.
    tkSchedulePreview(false);
  });

  el.tkMount.addEventListener('change', (e) => {
    const sel = (e.target as HTMLElement).closest('select[data-tk]') as HTMLSelectElement | null;
    if (!sel) return;
    tkSchedulePreview(true);
  });

  el.tkMount.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest('button[data-tk]') as HTMLElement | null;
    if (btn) {
      const key = btn.dataset.tk ?? '';
      const val = btn.dataset.val ?? '';
      if (key === 'side' && (val === 'buy' || val === 'sell')) tkState.side = val;
      if (key === 'type' && (val === 'limit' || val === 'market')) tkState.type = val;
      if (key === 'sizing' && (val === 'risk_pct' || val === 'size')) tkState.sizing = val;
      if (key === 'entry-mark') tkState.entry = tkMarkPrice();
      // A stale "placed" banner beside changed prices would describe a different trade.
      tkResult = '';
      if (key === 'focus') {
        tkFocusChart();
        return;
      }
      if (key === 'review-prev' || key === 'review-next') {
        // The review walk. It loads the neighbouring setup through the SAME path a
        // clicked row uses, so a stepped-to setup and a clicked one cannot end up in
        // different states.
        tpStepSetup(key === 'review-prev' ? -1 : 1);
        return;
      }
      if (key === 'go-setups') {
        tpBackToSetups();
        return;
      }
      if (key === 'go-positions' || key === 'go-orders') {
        // Explicit way BACK to the management tables. The ticket used to be a
        // one-way door: nothing in it pointed at Positions/Open Orders, so the
        // only exit was noticing the tab strip above it (which the layout bug was
        // simultaneously hiding). Reported by Andrei 2026-07-27.
        tpTab = key === 'go-positions' ? 'positions' : 'orders';
        localStorage.setItem(TP_KEYS.tab, tpTab);
        tpRender();
        return;
      }
      if (key === 'new-order') {

        // Leaving MANAGE mode clears the position's prices too: keeping them
        // would arm a NEW entry at the levels of one that already exists, which
        // is how you double an exposure by accident.
        tkState.managing = null;
        tkState.entry = null;
        tkState.stop = null;
        tkState.tps = [null, null];
        tkState.size = null;
        tkState.sizing = 'risk_pct';
        // Same reasoning applied to the manage decision itself: a `movedLeg` surviving
        // into a NEW-entry ticket would be a leg of a position this ticket no longer
        // manages.
        tkState.movedLeg = null;
        tkState.managedFrom = null;
        tkPreview = null;
        // Leaving manage mode restores the read-only levels this market suppressed.
        tpDrawLevels();
        tkRender();
        return;
      }

      tkSave();
      void tkFetchPreview(true);
      return;
    }
    if ((e.target as HTMLElement).id === 'tk-place') tkPlaceTap();
  });
}

/** One tap on `#tk-place`. On a fine pointer it places at once, exactly as since c0e36402. On a
 *  coarse pointer the first tap only arms ("Tap again to place") and a second tap within
 *  ARM_WINDOW_MS on the same prices places (FR3.3, intent 261018-mobile-v1, 2026-10-18). It sits
 *  in front of `tkPlace` and can only add a refusal; `tkPlace` itself is unchanged (FR3.5). */
function tkPlaceTap() {
  const step = placeStep(tkArm, { coarse: coarsePointer(), now: Date.now(), key: ticketKey(tkState) });
  tkArm = step.arm;
  if (step.action === 'place') {
    void tkPlace();
    return;
  }
  tkRender();
  // Drop the armed label when the window closes; the re-render reads `isArmed`, so a stale
  // timer after a newer arm or a placement redraws the same truth.
  window.setTimeout(() => tkRender(), ARM_WINDOW_MS);
}

/** The latest mark, for the `mark` button. Read off the last drawn candle. */
function tkMarkPrice(): number | null {
  const last = candleTimes.length
    ? activePane().candleSeries.dataByIndex(candleTimes.length - 1)
    : null;
  const close = (last as { close?: number } | null)?.close;
  return typeof close === 'number' ? close : null;
}

/** Zoom the TIME axis to the recent bars so the levels are legible.
 *
 *  Measured on the rendered page: with all 2,169 BTC 1D bars visible the price
 *  axis spanned 0-140,000 over ~290px, so a 64,000 entry with a 62,500 stop and a
 *  68,000 target collapsed into a ~10px ribbon — the R:R bands the ticket exists
 *  to show were 3-6px tall and unreadable. `autoscaleInfo` keeps the legs IN
 *  range; this makes that range tight enough to read, by dropping the ancient
 *  history that is dominating the price extent.
 *
 *  Time-axis only. It deliberately does NOT set a fixed price range: the series
 *  autoscale (now including the ticket's legs) is what keeps the stop on screen,
 *  and a hardcoded price window here would fight the user's own zoom. */
function tkFocusChart() {
  const bars = candleTimes.length;
  if (!bars) return;
  activePane().chart.timeScale().setVisibleLogicalRange({
    from: Math.max(0, bars - TK_FOCUS_BARS),
    // A few bars of empty space on the right, so the level labels and the
    // right-edge band captions are not drawn over the newest candles.
    to: bars + 8,
  });
}

/** How many recent bars `focus` shows. ~120 is enough context to see the swing a
 *  stop was chosen from while keeping the price range tight. */
const TK_FOCUS_BARS = 120;

/** Apply a dragged leg to the state. Pure assignment — no validation here.
 *
 *  Validation belongs to the backend: a stop dragged past entry is REPORTED by
 *  the preview (`stop_wrong_side`, with the constitution's RULE 5 verdict beside
 *  it) rather than silently clamped. Clamping would hide the mistake and teach
 *  the wrong thing about where a stop may sit.
 *
 *  THE ASSIGNMENT ITSELF IS `ticket.applyMovedLeg`, which also records WHICH leg moved —
 *  the only input to the manage verb (`ticket.manageIntent`). `tkPlace` used to infer that
 *  verb from `st.stop !== null`, so a TP drag sent `modify-sl` at the untouched stop price
 *  (R4-3, critical). It lives in `ticket.ts` because vitest cannot import this file, so a
 *  copy here could only be tested by a second copy in the test — which would keep passing
 *  after this one broke. This function is now the id translation and nothing else. */
function tkApplyDrag(id: string, price: number) {
  // The primitive's leg ids are `entry` / `stop` / `tp1` / `tp2`, which are already the
  // names `applyMovedLeg` takes — so the drag needs no mapping, and a `tp3` a future
  // geometry might draw records no moved leg rather than a wrong one.
  applyMovedLeg(tkState, id, price);
}

function tkApplyField(key: string, raw: string) {
  const v = raw.trim() === '' ? null : Number(raw);
  const num = v !== null && Number.isFinite(v) ? v : null;
  switch (key) {
    // TYPING A PRICE IS THE SAME DECISION AS DRAGGING ONE, so it goes through the same
    // function: a `movedLeg` recorded only by the drag would make the typed path fall back
    // to "nothing was moved" and refuse a real edit.
    case 'entry': case 'stop': case 'tp1': case 'tp2':
      applyMovedLeg(tkState, key, num);
      break;
    case 'risk_pct': tkState.risk_pct = num; break;
    case 'size': tkState.size = num; break;
    // Leverage has no meaningful null: 1x is "no leverage".
    case 'leverage': tkState.leverage = num ?? 1; break;
  }
}

/**
 * ONE PRESS PLACES THE TRADE.
 *
 * There is no Dry-run → Confirm pair here any more, and no dialog. The backend
 * still mints a plan token over the exact params (that is the mechanism §0b.2's
 * order-placement boundary is built from), so this function does the plan round
 * trip and then the execute round trip INSIDE the single click — the token never
 * reaches the user, because a token is not a decision they can inform.
 *
 * A refusal from the plan phase stops here and is shown verbatim: the book is
 * untouched and nothing is retried automatically.
 *
 * WHAT IS NOT IN THE PAYLOAD. No `size`, no `leverage`, no `risk_pct`. Those are
 * the SERVER's outputs (see the STUBBED FIELDS note in ticket.ts); sending a
 * client number for any of them would create a second source of truth about how
 * much money is at stake, and it would be the client's number that placed the
 * order. The exits keep their own verbs because that is what the audited CLI
 * exposes.
 */
async function tkPlace() {
  const st = tkState;
  if (tkBusy) return;
  const symbol = st.managing ?? st.symbol;
  const tp1 = st.tps.find((t): t is number => typeof t === 'number' && t > 0);

  // THE MANAGE BRANCH IS DECIDED BY `ticket.manageIntent`, ONCE, and this function does
  // not re-derive any part of it (R4-3, critical). It used to read
  // `st.stop !== null ? 'modify-sl' : 'modify-tp'` here and then pick the price from the
  // verb it had just guessed — two statements inferring one fact from state that cannot
  // express it, so a TP drag sent the untouched stop. Nothing below chooses a verb.
  const intent = st.managing ? manageIntent(st) : null;

  tkBusy = true;
  tkResult = '<div class="tp-note warn">Checking and placing — the server is running its safety checks…</div>';
  tkRender();
  try {
    if (intent) {
      // A REFUSAL COSTS ZERO NETWORK CALLS and is shown in the panel's own words. The
      // no-op case is the one that matters: re-sending a resting price would cancel and
      // re-place the same order, and there is no atomic amend on this venue, so the
      // position would be unprotected for a window in exchange for nothing.
      if (intent.refusal) {
        tkResult = `<div class="tp-note bad">${esc(intent.refusal)}</div>`;
        return;
      }
      // MOVING A LEVEL ON A LIVE POSITION: one request, no dry-run, no token
      // (`/api/trade/modify`, card t_a23d9cc5). The server rounds the price to the venue's
      // precision, so ONLY the price is sent and the rounded value comes back.
      const modified = await postJSON<ModifyRun>('/api/trade/modify', intent.request);
      tkResult = renderModifyRun(modified);
      // THE BANNER NOW REPORTS THE SERVER'S OWN RE-READ (R2-5 / R4-15), which is why
      // this comment no longer says "`confirmed` is a self-report — re-read the book".
      // It WAS true and the re-read it pointed at was `tpLoad(true)`, which begins
      // `if (!accountHealth?.ok) return;` and can therefore decline in silence: on this
      // path the banner then stood alone, promising evidence nothing drew. The proof is
      // in `modified.readback` / `modified.naked` and `renderModifyRun` reads it, so the
      // sentence above is already the book's answer rather than the CLI's.
      //
      // `tpLoad` is still called, for the OTHER thing it does: refreshing the account
      // snapshot so the panel's own rows and the chart's levels stop showing the old
      // price. That is a display concern, not the evidence, and it may fail silently
      // without making the banner a lie.
      if (!modified.refused) await tpLoad(true);
      return;
    }
    let run: TradeRun;
    {
      // A NEW ENTRY goes to /api/trade/bracket: ONE request that sizes, picks the
      // leverage and places, because Andrei asked for one button and a plan token
      // whose only consumer minted and burned it inside the same click was
      // ceremony. `size` and `leverage` are DELIBERATELY absent from this payload
      // — the server derives them and REJECTS a request that names either, which
      // is the whole safety model of this panel. Do not add them here "for
      // convenience"; `_reject_derived_fields` will refuse the request.
      run = await postJSON<TradeRun>('/api/trade/bracket', {
        symbol,
        entry: st.entry,
        // `side` says WHICH trade, not how much money, and it is load-bearing for a
        // stopless entry: with no stop it is the only thing that says which side of
        // entry the liquidation sits on. `stop` is omitted rather than sent as null
        // when there is none -- the server then sizes from the liquidation distance.
        side: st.side,
        ...(st.stop === null ? {} : { stop: st.stop }),
        ...(tp1 === undefined ? {} : { tp: tp1 }),
      });
    }
    tkResult = renderTradeRun(run);
    // exec_tools' `confirmed` is a self-report (§0b.1) — re-read the book.
    if (!run.refused) await tpLoad(true);
  } catch (err) {
    tkResult = `<div class="tp-note bad">${esc(String(err))}</div>`;
  } finally {
    tkBusy = false;
    tkRender();
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// EXPERIMENT NAVIGATOR — wiring (t_42cdbde6 U7). The DECISIONS live in ./navigator and
// ./experiment (pure, tested); this is DOM plumbing only.
// ═══════════════════════════════════════════════════════════════════════════
// Research source only. The list is asked for once, an experiment's index once per open, and an
// event's detail once per deep link -- all through `getJSONOnce`, so there is no new `fetch(` and
// no retry that would double a 404. The panel is written by ONE `innerHTML` assignment
// (`renderNavigator`), from `navigatorHtml`, which escapes every string it is given.

let navListAsked = false;
/** Bumped by every open: an index that arrives after a newer open is dropped. */
let navOpenSeq = 0;

/** An experiment id as a URL path: segment by segment, so `group/name` stays a path. */
function expPath(exp: string): string {
  return exp.split('/').map(encodeURIComponent).join('/');
}

function renderNavigator(): void {
  const box = document.getElementById('navigator');
  if (!box) return;
  const research = el.source.value === RESEARCH;
  box.hidden = !research;
  if (!research) return;
  const filtered = filterEvents(nav.events, nav.filters);
  // The detail block scrolls on its own (index.html `.nav-detail`); a re-render for the SAME event
  // (its drawn overlay arriving) keeps the reader's place in it instead of jumping back to the top.
  const keep = box.dataset.navDetailFor === (nav.current ?? '')
    ? box.querySelector<HTMLElement>('.nav-detail')?.scrollTop ?? 0 : 0;
  box.innerHTML = navigatorHtml(nav, filtered, summarize(filtered), navDrawn);
  box.dataset.navDetailFor = nav.current ?? '';
  const detail = box.querySelector<HTMLElement>('.nav-detail');
  if (detail && keep) detail.scrollTop = keep;
}

/** Overlays gated to one source get a disabled checkbox on every other one, with the reason as
 *  its title -- a tick there would draw nothing, so it must not look like it could. */
function syncOverlayGates(): void {
  const src = el.source.value;
  for (const o of OVERLAYS) {
    if (!o.gate) continue;
    for (const cb of document.querySelectorAll<HTMLInputElement>(`input[data-ov="${o.id}"]`)) {
      cb.disabled = src !== o.gate;
      if (cb.parentElement) cb.parentElement.title = cb.disabled ? `${o.gate} source only` : o.label;
    }
  }
}

/** The navigator's part of the URL, rewritten in place: a jump is not a history entry. */
function writeLink(): void {
  if (el.source.value !== RESEARCH) {
    if (location.search) history.replaceState(null, '', location.pathname);
    return;
  }
  history.replaceState(null, '', formatLink({
    source: RESEARCH, asset: el.asset.value || null, tf: execTf() || null, exp: nav.exp, event: nav.current,
  }));
}

/** A manual asset or source change: the event-centred window and the current event go (BR3.6). */
function clearNavAnchor(): void {
  jumpToken += 1;
  for (const p of panes.values()) p.around = null;
  nav = clearCurrent(nav);
  navDrawn = null;
  writeLink();
}

function refreshExperimentPanes(): void {
  for (const p of panes.values()) if (p.overlays.experiment) void refreshOverlays(p);
}

async function ensureNavList(): Promise<void> {
  if (navListAsked) return;
  navListAsked = true;
  try {
    const r = await getJSONOnce<ExperimentList>('/api/experiments');
    nav = { ...nav, experiments: r.experiments, listError: null };
  } catch (err) {
    // Asked again on the next switch to research: a failed read is not "no experiments".
    navListAsked = false;
    nav = { ...nav, experiments: [], listError: `experiments: ${errText(err)}` };
  }
  renderNavigator();
}

function errText(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

async function openExperiment(exp: string): Promise<boolean> {
  const seq = ++navOpenSeq;
  try {
    const url = `/api/experiments/${expPath(exp)}/events`;
    let idx = await getJSONOnce<EventIndex>(url);
    // A store experiment (card t_8a20d8d2) comes in pages: read every one before the list opens,
    // so a filter or prev/next never runs over part of the record. `appendPage` refuses any page
    // but the one asked for, so the loop moves strictly forward and ends.
    while (idx.next_page !== undefined && idx.next_page !== null) {
      if (seq !== navOpenSeq) return false;
      nav = { ...nav, message: `${exp}: reading events, page ${idx.next_page + 1} of ${idx.n_pages}` };
      renderNavigator();
      idx = appendPage(idx, await getJSONOnce<EventIndex>(`${url}?page=${idx.next_page}`));
    }
    if (seq !== navOpenSeq) return false;
    nav = openIndex(nav, idx);
    navDrawn = null;
  } catch (err) {
    if (seq !== navOpenSeq) return false;
    nav = { ...nav, message: `${exp}: ${errText(err)}` };
    renderNavigator();
    return false;
  }
  renderNavigator();
  writeLink();
  refreshExperimentPanes();
  return true;
}

/** Centre a pane on the bar at (or just after) `time`. */
function centreOn(pane: ChartPane, time: number): void {
  const bars = pane.bars;
  if (!bars.length) return;
  let i = bars.findIndex((b) => b.time >= time);
  if (i < 0) i = bars.length - 1;
  pane.chart.timeScale().setVisibleLogicalRange({ from: i - 60, to: i + 60 });
}

/**
 * Open one event on the chart (BR3.1-BR3.3): its asset, the execution pane on the decision
 * timeframe (or the link's), every pane's window centred on its time, the experiment layer on.
 * A later jump wins: `jumpToken` is checked after the loads, and `fetchPaneBars` drops a window
 * asked for under an earlier token.
 *
 * A LABEL EVENT OPENS ON ITS WHOLE WINDOW (t_75c35d19, Andrei 2026-10-16): the execution pane's
 * visible range is `labelRange` -- the event's own Asia start to its horizon -- and every pane's
 * `around` is that range's midpoint, so both ends sit inside the served window. An index row has no
 * windows, so a label row's jump reads its detail first (one extra request, labels only); if that
 * read fails the jump centres on the time as before and says why. Traded events are unchanged.
 */
async function jumpTo(target: JumpTarget, tf: string | null = null): Promise<void> {
  const src = currentSource();
  if (!src || src.id !== RESEARCH) return;
  const token = ++jumpToken;
  let frame: { from: number; to: number } | null = null;
  let frameNote: string | null = null;
  if (target.outcome_class === 'label') {
    let windows: EventWindow[] | undefined = target.context?.windows;
    if (windows === undefined && nav.exp !== null) {
      try {
        const d = await getJSONOnce<EventDetail>(
          `/api/experiments/${expPath(nav.exp)}/events/${encodeURIComponent(target.event_id)}`);
        windows = d.context?.windows;
      } catch (err) {
        frameNote = `event windows: ${errText(err)}; centred on the event time`;
      }
      if (token !== jumpToken) return;
    }
    frame = labelRange(target.outcome_class, target.time, windows);
  }
  const around = frame ? Math.round((frame.from + frame.to) / 2) : target.time;
  if (target.asset !== el.asset.value) {
    if (setChartedAsset(target.asset) === 'unavailable') {
      nav = { ...nav, message: `event is on ${target.asset}, which this source does not list` };
      renderNavigator();
      return;
    }
    // The manual asset path's own resets: the panel selection and the ticket belong to the market
    // they were read off.
    tpSelected = null;
    tkSyncSymbol();
  }
  const exec = activePane();
  const wantTf = tf ?? target.decision_tf;
  if (src.timeframes.includes(wantTf) && exec.tf !== wantTf) {
    exec.setTimeframe(wantTf);
    const sel = document.querySelector<HTMLSelectElement>(`[data-pane="${exec.id}"] select.pane-tf`);
    if (sel) sel.value = wantTf;
  }
  exec.overlays.experiment = true;
  const cb = document.querySelector<HTMLInputElement>(`[data-pane="${exec.id}"] input[data-ov="experiment"]`);
  if (cb) cb.checked = true;
  savePaneState();
  for (const p of panes.values()) p.around = around;
  nav = { ...nav, current: target.event_id, message: frameNote };
  navDrawn = null;
  renderNavigator();
  writeLink();
  invalidateAllOverlays();
  await Promise.allSettled([loadBars(), loadContextPanes()]);
  if (token !== jumpToken) return;
  if (frame) {
    exec.chart.timeScale().setVisibleRange({ from: frame.from as UTCTimestamp, to: frame.to as UTCTimestamp });
  } else {
    centreOn(exec, target.time);
  }
}

function navStep(dir: 1 | -1): void {
  const row = adjacent(filterEvents(nav.events, nav.filters), nav.current, dir);
  if (row) void jumpTo(row);
}

/** One delegated listener per gesture (click, change), registered once: the panel is rewritten on
 *  every render, so a per-row listener would be re-attached on every filter change (PD-8). */
function wireNavigator(): void {
  const box = document.getElementById('navigator');
  if (!box) return;
  box.addEventListener('click', (ev) => {
    const hit = (ev.target as HTMLElement | null)?.closest<HTMLElement>('[data-nav-exp],[data-nav-step],[data-nav-event]');
    if (!hit || !box.contains(hit)) return;
    const d = hit.dataset;
    if (d.navExp !== undefined) void openExperiment(d.navExp);
    else if (d.navStep !== undefined) navStep(d.navStep === '-1' ? -1 : 1);
    else if (d.navEvent !== undefined) {
      const row = nav.events.find((e) => e.event_id === d.navEvent);
      if (row) void jumpTo(row);
    }
  });
  box.addEventListener('change', (ev) => {
    const input = ev.target as HTMLInputElement | HTMLSelectElement;
    const control = input.dataset.navFilter;
    const listing = nav.experiments?.find((x) => x.id === nav.exp);
    if (control === undefined || !listing) return;
    const r = applyFilter(nav, filterFromControl(nav.filters, control, input.value, flagControls(listing, nav.events)));
    nav = r.state;
    // An excluded current event is cleared WITHOUT a jump ([Q11] A): the chart stays where it is.
    if (r.clearedCurrent) {
      navDrawn = null;
      writeLink();
      refreshExperimentPanes();
    }
    renderNavigator();
  });
  document.addEventListener('keydown', (ev) => {
    const target = ev.target as HTMLElement | null;
    const dir = navKey(ev.key, target?.tagName ?? '', Boolean(target?.isContentEditable),
      el.source.value === RESEARCH && nav.exp !== null);
    if (dir === null) return;
    ev.preventDefault();
    navStep(dir);
  });
}

/**
 * A deep link on boot (C9). The source was already selected from it; the asset list is in. True when
 * it jumped (the jump loaded every pane); false leaves the caller to load the chart as usual.
 */
async function openDeepLink(): Promise<boolean> {
  const parsed = parseLink(location.search, sources, assets.map((a) => a.asset));
  if (parsed.link === null) {
    if (parsed.sentence) setStatus(parsed.sentence, true);
    return false;
  }
  const link = parsed.link;
  if (link.source !== RESEARCH) {
    if (parsed.sentence) setStatus(parsed.sentence, true);
    return false;
  }
  if (link.asset !== null) setChartedAsset(link.asset);
  if (link.tf !== null) {
    const exec = activePane();
    exec.setTimeframe(link.tf);
    const sel = document.querySelector<HTMLSelectElement>(`[data-pane="${exec.id}"] select.pane-tf`);
    if (sel) sel.value = link.tf;
  }
  await ensureNavList();
  const say = (msg: string | null) => {
    nav = { ...nav, message: msg };
    renderNavigator();
  };
  if (link.exp === null || !(await openExperiment(link.exp))) {
    say(parsed.sentence ?? nav.message);
    return false;
  }
  if (link.event === null) {
    say(parsed.sentence);
    return false;
  }
  let detail: EventDetail;
  try {
    detail = await getJSONOnce<EventDetail>(
      `/api/experiments/${expPath(link.exp)}/events/${encodeURIComponent(link.event)}`);
  } catch (err) {
    // The API's own sentence, and the chart is left as the link's asset and tf drew it.
    say(errText(err));
    return false;
  }
  await jumpTo(detail, link.tf);
  const notes = [parsed.sentence, linkSentence(link, parsed.refused.includes('asset'), detail)]
    .filter((x): x is string => Boolean(x));
  if (notes.length) say(notes.join('; '));
  return true;
}

function renderSourceNote() {
  const src = currentSource();
  if (!src) return;
  const reach =
    src.reachable === false
      ? `<span class="warn">unreachable${src.error ? `: ${src.error}` : ''}</span>`
      : `${src.assets ?? '—'} assets`;
  el.livebox.setAttribute('data-source', src.id);
  // The caveat is shown, not hidden behind a tooltip: choosing a source changes
  // which prices every overlay is computed from, by up to 1.38%.
  const note = document.getElementById('source-note');
  if (note) note.innerHTML = `<div class="feed">${src.feed} — ${reach}</div><div class="caveat">${src.caveat}</div>`;
  el.livetoggle.disabled = !src.live;
}

async function boot() {
  // AUTH FIRST, and non-blocking. Restore any session this tab already has so the very
  // first data call carries a token. There is deliberately NO login form yet: the API
  // does not enforce the token until phase 3, and putting a gate in front of a working
  // app before the backend can honour it would break the tool for no benefit.
  //
  // `authStatus()` is what makes the state visible rather than silent — an operator
  // must be able to tell "not signed in" from "signed in and the call still failed".
  await acRestoreSession();
  // BLOCK on a session when a pool is compiled in. Every API call needs the bearer token,
  // so rendering the chart first would show an empty page and a 401. Resolves immediately
  // when there is no pool (phases 1-2 behaviour) or a session already exists.
  await acRequireSession();
  // THE PANE GRID REPLACES THE SINGLE CHART. `initChart` built one chart into `el.chart`;
  // `initPanes` builds the saved N (1 to 4, 1 on phone; 261019-pane-layout) into the same element,
  // each with its own timeframe selector and
  // overlay toggles. The execution pane (15m) hosts the ticket's draggable legs, so `initChart`'s
  // remaining wiring — the ticket primitive — is attached to that pane rather than to a chart of
  // its own. (The click-to-inspect subscription was there too until 2026-08-18; the drawing
  // primitive is per pane, see `drawWire`.)
  initPanes();
  initChart();
  // AFTER the session is restored: the menu shows who is signed in and the security panel needs a
  // live token to read MFA state.
  initProfileMenu();
  // ≤ 700 px tab bar (2026-10-18, intent 261018-mobile-v1): BEFORE the API early returns below,
  // so the phone layout works on the backend-less CI page too. CSS-only above 700 px.
  initMobileTabs();
  // HEALTH AND SOURCES IN PARALLEL. They are independent, and awaiting them in sequence cost 1425ms
  // before the first bar was even requested (measured: health 789ms + sources 576ms + assets 60ms).
  // Together: 555ms.
  const [healthRes, sourcesRes] = await Promise.allSettled([
    getJSON<Health>('/api/health'),
    getJSON<{ sources: SourceInfo[]; default: string }>('/api/sources'),
  ]);

  if (healthRes.status === 'rejected') {
    setStatus(`API unreachable: ${healthRes.reason}`, true);
    renderOverlayToggles(null);
    // STILL SHOWN when the API is down. The build/env stamp is baked into the BUNDLE, so
    // it does not need the backend -- and "which app is this" is exactly the question
    // being asked when one of two tabs stops answering.
    renderBuildInfo(null);
    return;
  }
  health = healthRes.value;
  renderIctStatus(health);
  renderBuildInfo(health);
  renderOverlayToggles(health);

  // Sources come from the backend, never hardcoded here: the reachability probe and the caveats are
  // measured server-side, and a client-side copy of the list would drift the moment a feed is added.
  if (sourcesRes.status === 'rejected') {
    setStatus(`sources unavailable: ${sourcesRes.reason}`, true);
    return;
  }
  // AN EMPTY FEED IS NOT OFFERED. Dev's layer ships no `bars_cache` -- `/api/health` says
  // `exists: false, assets: 0` and `/api/sources` reports `assets: 0` -- and the dropdown
  // offered it anyway, so selecting "bars_cache (what production computed)" charted
  // nothing. Filtered HERE, into `sources` itself rather than only into the `<option>`
  // list, so `currentSource()` cannot resolve a feed the user has no way to pick: one
  // source of truth about what is selectable. See `offerableFeeds`.
  sources = offerableFeeds(sourcesRes.value.sources);
  el.source.innerHTML = sources.map((x) => `<option value="${x.id}">${x.label}</option>`).join('');
  // The backend's default only wins if it SURVIVED the filter. Assigning a value with no
  // matching option leaves a `<select>` blank, which would break the chart on a deploy
  // whose default feed happens to be the empty one.
  el.source.value = sources.some((s) => s.id === sourcesRes.value.default)
    ? sourcesRes.value.default
    : (sources[0]?.id ?? '');
  // A NAVIGATOR DEEP LINK picks the source before anything loads (t_42cdbde6 U7, C9). Only a valid
  // research link does: any other source in a link is the backend's default's business.
  if (parseLink(location.search, sources).link?.source === RESEARCH) el.source.value = RESEARCH;
  renderSourceNote();
  syncOverlayGates();

  if (!(await loadAssetsForSource())) return;

  wireNavigator();
  renderNavigator();
  el.source.addEventListener('change', () => {
    stopLive();
    renderSourceNote();
    // The anchor and the current event belong to the research windows; the experiment checkbox is
    // only live on research. The open experiment itself stays for the next visit.
    clearNavAnchor();
    syncOverlayGates();
    renderNavigator();
    if (el.source.value === RESEARCH) void ensureNavList();
    void (async () => {
      if (await loadAssetsForSource()) { await loadBars(); void loadContextPanes(); }
    })();
  });
  apWire();
  drawWire();
  // Escape discards a staged (dragged, un-sent) stop/target on every pane. Wired next to
  // the drawing keys because it is the same class of gesture undo.
  wireLevelCancel();
  wireZoneView();
  // The setup layer's `show all` toggle. Same delegated-listener discipline, registered once.
  wireSetupView();
  // The sidebar's collapse toggle. Restores the saved preference on the way in, so this runs before
  // the first bars are drawn -- collapsing after a chart has sized itself is a resize the library
  // handles, but doing it up front means one layout pass instead of two.
  wireInspectorToggle();
  el.asset.addEventListener('change', () => {
    // A MANUAL asset change leaves the navigator's event (BR3.6): the centred window and the
    // selection belong to the event's market. A jump never comes through here (`setChartedAsset`
    // dispatches no `change`), so this only ever runs for the user's own pick.
    clearNavAnchor();
    renderNavigator();
    // An explicit panel selection is dropped when the chart moves to a different
    // market: keeping it would draw AMZN's stop on a BTC chart, which is exactly
    // the sort of confidently-wrong picture this app exists to avoid.
    tpSelected = null;
    // Same reasoning for the ticket: its prices belong to the market they were
    // read off, so they are cleared rather than carried across.
    tkSyncSymbol();
    // EVERY BUILT PANE shares the asset, so all of them reload. Their timeframes are untouched:
    // switching symbol should not reset the layout the user arranged.
    //
    // Invalidate EVERY pane first: an asset change moves all three to a different market, so any
    // in-flight run is aimed at the previous symbol. Without this, a slow 15m overlay run started
    // under BTC could resolve after SOL's bars had rendered and paint BTC's markers onto SOL — the
    // artifact Andrei reported, in its racing form rather than its stale-geometry form.
    invalidateAllOverlays();
    void loadBars();
    void loadContextPanes();
  });

  el.reload.addEventListener('click', () => {
    void loadBars();
    void loadContextPanes();
  });
  // Order entry, from the toolbar. Same function the tab click runs, so there is
  // one code path into the ticket rather than two that can drift.
  el.newtrade.addEventListener('click', () => tkOpen());

  // Pausing live updates is a real need: a bar repainting under the cursor while
  // you are reading a level off it is actively unhelpful.
  el.livetoggle.addEventListener('change', () => startLive());

  // Overlay controls are injected by renderOverlayToggles, so they are wired
  // once here after that has run. Each only re-fetches the overlays — the bars
  // are unchanged, so redrawing them would just flicker the viewport.
  // Analysis PARAMETERS, not per-pane toggles: these change what the detector computes (the
  // inversion rule, the ATR tolerance, the equal-highs touch count), so a change applies to every
  // pane. The per-pane overlay CHECKBOXES live in each pane's own bar — see `initPanes`.
  //
  // An arrow wrapper, not a bare reference: `refreshOverlays` takes an optional pane, and passing
  // it straight to addEventListener would hand it the Event object as the pane. tsc caught that.
  // DERIVED FROM THE DOM, not a hand-written id list. This WAS
  // `['show-confirm', 'inv-rule', 'tol-atr', 'min-touches']`, and adding the `setup reach` dropdown
  // would have produced a control that renders, reads correctly, and does NOTHING when changed --
  // the same class of drift as `KNOWN_OVERLAYS` (three edits, nothing enforcing agreement) and the
  // `params_for` map in the payload tests. Every input inside `.overlay-sub` is a shared detector
  // knob by construction, so the listener follows the markup instead of trailing it.
  const knobs = document.querySelectorAll<HTMLElement>('.overlay-sub select, .overlay-sub input');
  for (const knob of knobs) {
    knob.addEventListener('change', () => {
      for (const pane of panes.values()) void refreshOverlays(pane);
    });
  }

  // From here on boot loads the bars, so a pane the picker builds after this point loads its own
  // (`applyPaneCount`); one built before it is loaded by the calls below, which iterate `panes`.
  paneLoadsStarted = true;
  // A deep link that names an event jumps, which loads every pane itself.
  if (!(await openDeepLink())) {
    if (el.source.value === RESEARCH) void ensureNavList();
    await loadBars();
    // Context panes AFTER the execution pane: it owns the shared UI, so it should paint first.
    void loadContextPanes();
  }

  // The trade panel boots LAST and independently: its health probe and snapshot
  // must never be able to stop the chart from rendering. An unreachable exchange
  // or a missing hl-review engine greys the panel out; the chart still works.
  void tpBoot();

  // Same discipline for the AI chat: an unavailable Bedrock (no credentials, no
  // model access) must grey a button, never break the chart.
  void acBoot();
}

// ═══════════════════════════════════════════════════════════════════════════
// AI CHAT OVERLAY — wiring. The DECISIONS live in ./aichat (pure, tested).
// ═══════════════════════════════════════════════════════════════════════════
// This section is deliberately thin: DOM plumbing, fetch, drag. Everything that
// can be got wrong without a compiler noticing — what gets posted, what the
// transcript becomes after a reply, how markdown is escaped — is in `aichat.ts`
// so `aichat.test.ts` can pin it. `chartapp/README.md` records a frontend-state
// bug that "passed all four gates green" because it lived in .ts; gate 5 is the
// answer to that class, and it can only see exported functions.

let acMsgs: ChatMsg[] = [];
let acHealth: AgentHealth | null = null;
let acBusy = false;

const acEl = {
  btn: () => document.getElementById('aichat-btn') as HTMLButtonElement | null,
  win: () => document.getElementById('aichat') as HTMLElement | null,
  log: () => document.getElementById('aichat-log') as HTMLElement | null,
  sub: () => document.getElementById('aichat-sub') as HTMLElement | null,
  input: () => document.getElementById('aichat-input') as HTMLTextAreaElement | null,
  send: () => document.getElementById('aichat-send') as HTMLButtonElement | null,
};

/** The chart view handed to the model, so "any setup here?" needs no restating. */
function acChartContext(): ChartContext {
  return {
    asset: el.asset.value || undefined,
    tf: execTf() || undefined,
    source: el.source.value || undefined,
    // FROM THE EXECUTION PANE'S OWN RECORD, which is where overlay state lives since overlays
    // became per-pane on 2026-07-31. The old read looked the SHARED sidebar checkbox ids up in the
    // DOM -- ids deleted that day -- so it always sent `[]` and told the model every
    // overlay was off. See `overlaysOn`. The chat is about the EXECUTION pane -- the same pane
    // `execTf()` above reports the timeframe of -- so the two halves of this context agree.
    overlays_on: overlaysOn(panes.get(EXECUTION_PANE_ID)?.overlays),
    bars_count: candleBars.length || undefined,
  };
}

function acRender() {
  const log = acEl.log();
  if (!log) return;
  if (acMsgs.length === 0) {
    // The empty state EXAMPLES exist because "ask me anything" is the least
    // useful prompt in a domain tool: this app has already been told twice that a
    // built feature was invisible, and a blank box is the same failure.
    log.innerHTML = `
      <div class="ac-empty">
        Reads this chart through the shared <code>ict/</code> module — the same code
        the overlays and the production analyzers run. Try:
        <ul>
          <li>Is there a long setup here? Give me levels.</li>
          <li>What is the current dealing range and OTE?</li>
          <li>Any unswept liquidity above price?</li>
          <li>Review my open positions against this chart.</li>
        </ul>
      </div>`;
    return;
  }
  log.innerHTML = acMsgs
    .map((m) => {
      const cls = `ac-msg ${m.role}${m.error ? ' error' : ''}`;
      const who = m.role === 'user' ? 'you' : m.pending ? 'analyst — thinking…' : 'analyst';
      // User text is ESCAPED, never rendered as markdown: it is echoed back into the
      // DOM and must not be able to inject. Assistant text goes through the
      // escape-then-mark-up renderer in aichat.ts.
      const body = m.role === 'user' ? escapeHtml(m.text) : renderMarkdown(m.text);
      const tools =
        m.role === 'assistant' && !m.pending && !m.error
          ? `<div class="ac-tools${(m.tools?.length ?? 0) === 0 ? ' none' : ''}">${escapeHtml(
              toolSummary(m.tools),
            )}</div>`
          : '';
      return `<div class="${cls}"><div class="who">${who}</div><div class="body">${body}</div>${tools}</div>`;
    })
    .join('');
  log.scrollTop = log.scrollHeight;
}

function acSyncSend() {
  const send = acEl.send();
  const input = acEl.input();
  if (send && input) {
    send.disabled = !canSend(input.value, acBusy, Boolean(acHealth?.ok));
  }
}

async function acSubmit() {
  const input = acEl.input();
  if (!input) return;
  const text = input.value.trim();
  if (!canSend(text, acBusy, Boolean(acHealth?.ok))) return;

  acMsgs.push({ role: 'user', text });
  acMsgs.push({ role: 'assistant', text: '', pending: true });
  input.value = '';
  acBusy = true;
  acSyncSend();
  acRender();

  try {
    const body = buildRequest(acMsgs, acChartContext());
    const reply = await postJSON<AgentReply>('/api/agent/chat', body);
    acMsgs = applyReply(acMsgs, reply);
  } catch (err) {
    acMsgs = applyError(acMsgs, `${err}`);
  } finally {
    acBusy = false;
    acSyncSend();
    acRender();
  }
}

/** Drag by the header. Kept here (not in aichat.ts) because it is pure DOM. */
function acWireDrag() {
  const win = acEl.win();
  const head = document.getElementById('aichat-head');
  if (!win || !head) return;
  let sx = 0, sy = 0, ox = 0, oy = 0, dragging = false;
  head.addEventListener('mousedown', (e) => {
    if ((e.target as HTMLElement).tagName === 'BUTTON') return;
    const r = win.getBoundingClientRect();
    // Switch from right/bottom anchoring to left/top on first drag, or the window
    // would jump by its own width the moment it is moved.
    win.style.left = `${r.left}px`;
    win.style.top = `${r.top}px`;
    win.style.right = 'auto';
    win.style.bottom = 'auto';
    dragging = true;
    sx = e.clientX; sy = e.clientY; ox = r.left; oy = r.top;
    e.preventDefault();
  });
  window.addEventListener('mousemove', (e) => {
    if (!dragging) return;
    // Clamped so the header cannot be dragged off-screen — a window you cannot
    // reach the close button of is a window you have to reload the page to dismiss.
    const maxX = window.innerWidth - 120;
    const maxY = window.innerHeight - 40;
    win.style.left = `${Math.max(0, Math.min(maxX, ox + e.clientX - sx))}px`;
    win.style.top = `${Math.max(0, Math.min(maxY, oy + e.clientY - sy))}px`;
  });
  window.addEventListener('mouseup', () => { dragging = false; });
}

function acToggle(open?: boolean) {
  const win = acEl.win();
  const btn = acEl.btn();
  if (!win || !btn) return;
  const show = open ?? win.hidden;
  win.hidden = !show;
  btn.classList.toggle('open', show);
  if (show) {
    acRender();
    acEl.input()?.focus();
  }
}

async function acBoot() {
  const btn = acEl.btn();
  if (!btn) return;
  try {
    acHealth = await getJSON<AgentHealth>('/api/agent/health');
  } catch (err) {
    acHealth = { ok: false, error: `${err}` };
  }

  const sub = acEl.sub();
  if (sub) {
    sub.textContent = healthNote(acHealth);
    sub.className = acHealth?.ok ? '' : 'err';
  }
  btn.disabled = !acHealth?.ok;
  btn.title = acHealth?.ok
    ? `Ask ${acHealth.model_id ?? 'the model'} about this chart (read-only — it cannot trade)`
    : `AI chat unavailable: ${acHealth?.error ?? 'unknown'}`;
  if (!acHealth?.ok) return;

  btn.addEventListener('click', () => acToggle());
  document.getElementById('aichat-close')?.addEventListener('click', () => acToggle(false));
  document.getElementById('aichat-clear')?.addEventListener('click', () => {
    acMsgs = [];
    acRender();
  });
  document.getElementById('aichat-form')?.addEventListener('submit', (e) => {
    e.preventDefault();
    void acSubmit();
  });
  const input = acEl.input();
  input?.addEventListener('input', acSyncSend);
  // Enter sends, Shift+Enter newlines — the convention every chat UI uses, and the
  // placeholder says so rather than leaving it to be discovered.
  input?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void acSubmit();
    }
  });
  acWireDrag();
  acSyncSend();
}

void boot();
